import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Layout from "@components/Layout";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell
} from "@components/ui/table";
import { HelionButton } from "@components/ui/HelionButton";
import { API_BASE_URL } from "@lib/config";
import { computeDte, fmtLocalDate, fmtLocalDateTime, parseUtc } from "@lib/dateUtils";
import { useUser } from "../hooks/useUser";
import { useSignalR } from "../hooks/useSignalR";
import NavChartPanel from "../components/NavChartPanel";
import LedgerConfirmCard from "../components/LedgerConfirmCard";
import { useLivePoll } from "../hooks/useLivePoll";
import { isMarketOpenAt } from "@lib/marketSessionClock";
import { GAMMA_PER_1PCT_HINT, gammaPer1Pct } from "@lib/marketCarry";
import OptionsMlBriefing, { type OptionsMlOverview } from "@components/ui/OptionsMlBriefing";
import { InfoHint, MICRO_LABEL, CARD_CLASS, chipClass, fmtUSD, bucketLabel } from "@components/cockpit";
import { LiveBadge } from "@components/cockpit/live";
import { PeriodReturnTile } from "@components/cockpit/PeriodReturnTile";
import { HarvestDecisionPanel } from "@components/cockpit/HarvestDecisionPanel";
// Sticky section nav, shared verbatim with the Risk dashboard.
import { QuickNav } from "@components/cockpit/QuickNav";
// The Portfolio page reads a backend flag for this; the Signals holdings table is fed
// by get_leaps_risk_snapshot, which carries no home-run fields, so it derives the same
// rule from the same policy object. See the module header for why it is not a hardcoded 2.
import { lotHarvestConstraint } from "@lib/harvestLotConstraint";
import type { NavReconciliation } from "@lib/navTypes";
import { SignalsMethodologyModal } from "@components/SignalsMethodologyModal";
import { navUnreadNote, syncSuccessMessage, type RefreshPortfolioResponse } from "@lib/refreshPortfolio";
import { BookExposureGreeks } from "@components/BookExposureGreeks";
import { RungStatusChip } from "@components/screener/LadderPriorityPanel";
import { mergeCapitalRows, ladderGapText, ladderFootnote, ladderScale, ladderBarGeometry, ladderFill, ladderGapSigned, ladderBuckets, bucketScale, roomRungs, type LadderStatusPayload } from "@lib/ladderStatus";
import {
  INTRADAY_FUNDS,
  normalizeIntradayFund,
  ESTIMATE_LABEL,
  type IntradayFundCode,
  dayPnlSourceLabel,
  estimateAsOfNote,
  heldInfoOf,
  heldPillText,
  heldTooltip,
  otherFundTiles,
  payloadBelongsTo,
  pnlIsPartial,
  pnlSourceOf,
  readStoredIntradayFund,
  sourcePill,
  storeIntradayFund,
  unrealizedSubLabel,
} from "@lib/intradayFundView";
// ONE derivation of harvest-lockout capital, shared with Risk and Screener so the three
// surfaces cannot quote different withheld amounts. See lib/harvestLockout.ts.
import {
  deriveHarvestLockout,
  netDeployable,
  deployabilityHeadline,
  type HarvestLockout,
} from "@lib/harvestLockout";
import { resolveSignalsTrim } from "../lib/signalsTrimState";

/* ----------------------- helpers: formatting ----------------------- */
const dollars = (v: any): string => {
  const n = typeof v === "number" ? v : Number(v);
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
};

/** =======================
 *  Theme-safe UI helpers
 *  ======================= */
const ui = {
  pageBg: "var(--bg-primary)",
  cardBg: "var(--card-bg)",
  cardBorder: "var(--card-border)",
  textPrimary: "var(--text-primary)",
  textSecondary: "var(--text-secondary)",
  textMuted: "var(--text-muted)",
  tableBg: "var(--card-bg)",
  tableHeaderBg: "var(--nav-bg)",
  tableHeaderText: "var(--nav-text)",
  subtleBarBg: "var(--bar-track)",
  subtleBarBgStrong: "var(--bar-track-strong)",
  subtleBarFill: "var(--bar-fill)",
  subtleBarFillStrong: "var(--bar-fill-strong)",
  subtleBarEdge: "var(--bar-edge)",
  hoverBgLight: "var(--surface-hover)",
  groupBg: "var(--surface-group)",
  overlaySoft: "var(--surface-overlay-soft)",
  overlayStrong: "var(--surface-overlay-strong)",
  innerShadow: "var(--surface-shadow-inner)",
  innerHighlight: "var(--surface-highlight-inner)",
  shadow: "var(--shadow-soft)",
};

const themeCardStyle: React.CSSProperties = {
  backgroundColor: ui.cardBg,
  border: `1px solid ${ui.cardBorder}`,
  color: ui.textPrimary,
  boxShadow: ui.shadow,
};

const themeInputStyle: React.CSSProperties = {
  backgroundColor: ui.cardBg,
  color: ui.textPrimary,
  border: `1px solid ${ui.cardBorder}`,
};

const smallMutedText: React.CSSProperties = {
  color: ui.textMuted,
};

const secondaryText: React.CSSProperties = {
  color: ui.textSecondary,
};

const cardBaseClass = CARD_CLASS;
const sectionLabelClass = "text-[11px] uppercase tracking-wide font-semibold";

/** =======================
 *  Config / thresholds
 *  ======================= */
const VPD_STALE_THRESHOLD = 0.60;
const META_REFRESH_MS = 30_000;
// Well inside the 30-minute window after which the harvest lockout read is refused as stale.
const HARVEST_REFRESH_MS = 5 * 60_000;
const META_MAX_RETRIES = 4;
const META_BACKOFF_MS = 350;
const ALLOCATION_TOLERANCE = 0.04;
const META_CACHE_KEY = "helion.signalsDashboard.metaCache.v1";

/* --------------------------- Today’s Playbook (CIO) --------------------------- */
type PlaybookTiming = "ACT_NOW" | "WAIT_FOR_CLOSE" | "NEXT_SESSION";

type ToneKey = "green" | "amber" | "red" | "slate" | "info";

const tonePalette: Record<ToneKey, { border: string; background: string; text: string; chipBg: string; chipText: string; accent: string }> = {
  green: { border: "var(--status-positive-border)", background: "var(--status-positive-gradient)", text: "var(--status-positive-text)", chipBg: "var(--status-positive-bg-strong)", chipText: "var(--status-positive-text)", accent: "var(--status-positive-text)" },
  amber: { border: "var(--status-warning-border)", background: "var(--status-warning-gradient)", text: "var(--status-warning-text)", chipBg: "var(--status-warning-bg-strong)", chipText: "var(--status-warning-text)", accent: "var(--status-warning-text)" },
  red: { border: "var(--status-danger-border)", background: "var(--status-danger-gradient)", text: "var(--status-danger-text)", chipBg: "var(--status-danger-bg-strong)", chipText: "var(--status-danger-text)", accent: "var(--status-danger-text)" },
  slate: { border: "var(--status-neutral-border)", background: "var(--status-neutral-gradient)", text: "var(--status-neutral-text)", chipBg: "var(--status-neutral-bg-strong)", chipText: "var(--status-neutral-text)", accent: "var(--status-neutral-text)" },
  // "info" (blue) = a directive that is NOT an all-clear — used for the deploy/ADD playbook
  // so "deploy toward target" never reads as the green "no action" state.
  info: { border: "var(--status-info-border)", background: "var(--status-info-gradient)", text: "var(--status-info-text)", chipBg: "var(--status-info-bg-strong)", chipText: "var(--status-info-text)", accent: "var(--status-info-text)" },
};

const StatusPill: React.FC<{ label: string; tone?: ToneKey }> = ({ label, tone = "slate" }) => {
  const palette = tonePalette[tone];
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide"
      style={{
        backgroundColor: palette.chipBg,
        color: palette.chipText,
        border: `1px solid ${palette.border}`
      }}
    >
      {label}
    </span>
  );
};

const metricTileStyle: React.CSSProperties = {
  background: `linear-gradient(180deg, ${ui.overlaySoft} 0%, transparent 100%)`,
  border: `1px solid ${ui.cardBorder}`,
  boxShadow: `inset 0 1px 0 ${ui.innerHighlight}`,
};

const panelChromeStyle: React.CSSProperties = {
  ...themeCardStyle,
  borderRadius: 18,
  background: `linear-gradient(180deg, ${ui.cardBg} 0%, ${ui.overlaySoft} 100%)`,
};

const commandBandStyle: React.CSSProperties = {
  ...panelChromeStyle,
  border: `1px solid ${ui.cardBorder}`,
  padding: 12,
};

const sectionShellStyle: React.CSSProperties = {
  ...panelChromeStyle,
  padding: 14,
};

const sectionHeaderRowStyle: React.CSSProperties = {
  paddingBottom: 8,
  marginBottom: 10,
  borderBottom: `1px solid ${ui.cardBorder}`,
};

const SignalMetric: React.FC<{ label: string; value: React.ReactNode; emphasize?: boolean }> = ({ label, value, emphasize = false }) => (
  <div className="rounded-xl px-3 py-2.5" style={metricTileStyle}>
    <div className="text-[10px] uppercase tracking-[0.14em] font-semibold" style={smallMutedText}>{label}</div>
    <div className={emphasize ? "mt-1 text-[1.45rem] leading-none font-semibold" : "mt-1 text-[0.95rem] font-semibold"} style={{ color: ui.textPrimary }}>
      {value}
    </div>
  </div>
);

/** A label-over-value stat with no tile chrome, for a single line of figures. */
const InlineStat: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.14em] font-semibold" style={smallMutedText}>{label}</div>
    <div className="mt-0.5 text-[0.95rem] font-semibold" style={{ color: ui.textPrimary }}>{value}</div>
  </div>
);

const SectionShell: React.FC<{ title: string; meta?: React.ReactNode; children: React.ReactNode }> = ({ title, meta, children }) => (
  <div style={sectionShellStyle} className="h-full">
    <div className="flex items-center justify-between gap-3" style={sectionHeaderRowStyle}>
      <div className={sectionLabelClass} style={smallMutedText}>{title}</div>
      {meta ? <div className="text-[11px]" style={smallMutedText}>{meta}</div> : null}
    </div>
    {children}
  </div>
);

function playbookFromSignals(args: {
  pendingHarvest: boolean;
  harvestActive: boolean;
  // F4: harvest_config.enabled, consumed so a DISABLED control cannot post an execute-next-session
  // directive. Undefined preserves today's behaviour — suppression happens only on a positive
  // assertion of disabled.
  harvestEnabled?: boolean;
  earlyDefenseActive: boolean;
  live: boolean;
  overTarget: boolean;
  capBreached: boolean;
  targetPct?: number | null;
  currentPct?: number | null;
  trimNotional?: number | null;
  vixTrimEnabled?: boolean;
  targetSource?: string | null;
  // Deploy/ADD side — the fund is materially UNDER target (beyond the tolerance band).
  underTarget?: boolean;
  addNotionalLow?: number | null;   // $ to reach the lower edge of the tolerance band
  addNotionalHigh?: number | null;  // $ to reach the target itself
  bandLowPct?: number | null;       // target − tolerance (lower band edge, as a weight)
}): { timing: PlaybookTiming; headline: string; subline: string; bullets: string[]; tone: "green" | "amber" | "red" | "info" } {
  const { pendingHarvest, harvestActive, harvestEnabled, earlyDefenseActive, live, overTarget, capBreached, targetPct, currentPct, trimNotional, vixTrimEnabled, targetSource, underTarget, addNotionalLow, addNotionalHigh, bandLowPct } = args;

  // REFRAMED 2026-08-13 to the CIO's stated policy. The 40% funded/cost-basis figure is a
  // DEPLOYMENT constraint -- we do not buy beyond it -- and is NOT a holding cap: market
  // value may grow without limit and there is no cap on options performance. Rebalancing is
  // forced only by a REGIME CHANGE or the GRIND AXIS, which is what the engine's
  // target_source actually reports (base_regime | early_defense | grind_defense).
  // This branch previously rendered "Action required now" and prescribed a trim, which
  // asserted an action trigger the policy does not contain. It now reports the true
  // consequence -- no further funded buying -- and leaves trims to regime and grind.
  // An engine-rule trim (confirmed defense / grind) outranks the cap note: the cap is not the
  // trigger, but it must not tell the CIO "not a trim" beside a real one (P2-5, 2026-11-03).
  if (capBreached && !overTarget) {
    return {
      // NEXT_SESSION, not ACT_NOW: reaching the deployment cap is a constraint on the next
      // buying decision, not a task to execute today. PlaybookTiming offers only ACT_NOW /
      // WAIT_FOR_CLOSE / NEXT_SESSION and this is deliberately not widened -- adding a member
      // would ripple into tone and rendering logic outside the scope of this correction.
      timing: "NEXT_SESSION",
      tone: "amber",
      headline: "No further funded buying—deployment cap reached",
      subline: "Funded/cost basis is at or above the 40% deployment cap, so no new funded purchases. This is not a trim signal: market-value growth is uncapped, and only a confirmed defense or the grind axis forces a rebalance.",
      bullets: [
        "The cap governs what we BUY on cost basis, not what we HOLD.",
        "Do not trim on this alone—check target_source for regime or grind before any de-risk.",
        "Existing staged orders that add funded exposure should be reviewed against the cap."
      ]
    };
  }

  if (live) {
    return {
      timing: "WAIT_FOR_CLOSE",
      tone: "amber",
      headline: "Wait for close confirmation",
      subline: "Regime and volatility are intraday. Helion operates close-to-close—do not execute based on intraday shifts.",
      bullets: [
        overTarget ? "If the regime holds at the close, stage trims for next session." : "Likely no action if the close does not confirm the regime.",
        earlyDefenseActive || targetSource === "early_defense"
          ? "Early Defense is the active override—respect the lower target only after close confirmation."
          : pendingHarvest && !harvestActive
          ? "Harvest is pending, but it should not drive action unless the backend explicitly re-enables it."
          : "Harvest status does not block close-confirmed risk sizing.",
        typeof trimNotional === "number" ? `Trim preview (if confirmed): ${dollars(trimNotional)}` : "Trim preview will update after close confirmation."
      ]
    };
  }

  if (earlyDefenseActive && overTarget) {
    const t = typeof targetPct === "number" ? `${(targetPct * 100).toFixed(1)}%` : "effective target";
    const c = typeof currentPct === "number" ? `${(currentPct * 100).toFixed(1)}%` : "current";
    return {
      timing: "NEXT_SESSION",
      tone: "amber",
      headline: "Early Defense active—execute target-drop sizing next session",
      subline: `You are over the effective target (${c} vs ${t}). Stage target-drop trims after the close and execute next session.`,
      bullets: [
        // F5: vix_trim_enabled is emitted only by the rebalance engine (rebalance_logic.py:1013)
        // and portfolio_view.py:626 — neither is on the get_leaps_snapshot path this page calls,
        // so the field is undefined here and the "still enabled" branch rendered unconditionally.
        // Assert the claim only when the field is actually present and true.
        // CORRECTED 2026-08-13. A first pass replaced the unconditional "still enabled"
        // claim with "not reported by this endpoint" — which is ALSO false. This endpoint
        // DOES report it: get_leaps_snapshot emits harvest_config, and the engine defines
        // vix_trim_enabled as bool(harvest_cfg.enabled) (rebalance_logic.py:1013) — the
        // same flag under another name. So fall back to harvestEnabled rather than
        // claiming ignorance we do not have.
        vixTrimEnabled === false || (vixTrimEnabled == null && harvestEnabled === false)
          ? "Independent VIX trim logic is disabled—risk reduction comes from the target drop and standard exits."
          : vixTrimEnabled === true || (vixTrimEnabled == null && harvestEnabled === true)
          ? "Independent VIX trim logic is still enabled in telemetry."
          : "Independent VIX trim state is absent from this payload—confirm on the rebalance engine before relying on it.",
        "Avoid intraday timing—confirm at close.",
        "Use staged orders as the execution checklist."
      ]
    };
  }

  // F4: harvestActive comes from the LIGHT path (get_leaps_snapshot/__init__.py:876-877), which
  // computes harvest_window from the raw VIX-z threshold WITHOUT consulting harvest.enabled, while
  // the engine gates the identical computation behind `if harvest_cfg.enabled:`
  // (strategy/rebalance_logic.py:883-889). harvest.enabled is false, so a green execute-next-session
  // harvest directive cannot be correct. Falls through to the existing close-confirmed
  // overTarget / underTarget / no-action branches. No threshold is changed.
  if (harvestActive && harvestEnabled !== false) {
    return {
      timing: "NEXT_SESSION",
      tone: "green",
      headline: "Harvest window active—execute next session",
      subline: "Execute staged trims next session if the backend explicitly stages them.",
      bullets: [
        "Do not execute intraday—use next-session workflow.",
        "Stage orders via rebalance or exit engines if needed.",
        "Execute staged contract list by symbol, expiry, strike, and right."
      ]
    };
  }

  if (overTarget) {
    const t = typeof targetPct === "number" ? `${(targetPct * 100).toFixed(0)}%` : "target";
    const c = typeof currentPct === "number" ? `${(currentPct * 100).toFixed(0)}%` : "current";
    return {
      timing: "NEXT_SESSION",
      tone: "amber",
      headline: "Close-confirmed risk sizing—execute next session",
      subline: `You are over target (${c} vs ${t}). If confirmed at the close, stage trims and execute next session.`,
      bullets: [
        typeof trimNotional === "number" && trimNotional > 0
          ? `Trim ~${dollars(trimNotional)} to reach target (${c} → ${t}).`
          : "Stage trims sized to the target gap after the close.",
        targetSource === "early_defense"
          ? "This is an Early Defense target-drop trim—not a separate harvest trim."
          : "Standard rebalance sizing applies.",
        // F5: see note above — undefined on this endpoint, so "remains enabled" is not assertable.
        vixTrimEnabled === false
          ? "VIX trim logic is disabled—the dashboard should only expect target-based derisking and standard exits."
          : vixTrimEnabled === true
          ? "VIX trim logic remains enabled in the current telemetry."
          : "VIX trim state is not reported by this endpoint—treat it as unknown here and confirm on the rebalance engine before relying on it.",
        "Use staged orders as the execution checklist."
      ]
    };
  }

  // Deploy/ADD side — the sleeve is materially UNDER target (past the tolerance band). This is the
  // deploy half of the mandate: a directive (blue/info), never a green all-clear. Points at the
  // tolerance BAND (target − tol → target), so it doesn't demand a false-precision pinpoint, and
  // stays close-confirmed + advisory. Within the band, this branch does NOT fire — that is a
  // genuine "no action" and falls through to the default below.
  if (underTarget) {
    const t = typeof targetPct === "number" ? `${(targetPct * 100).toFixed(0)}%` : "target";
    const c = typeof currentPct === "number" ? `${(currentPct * 100).toFixed(0)}%` : "current";
    const bandLo = typeof bandLowPct === "number" ? `${(bandLowPct * 100).toFixed(0)}%` : null;
    const bandTxt = bandLo ? `${bandLo}–${t}` : t;
    const addTxt =
      typeof addNotionalLow === "number" && typeof addNotionalHigh === "number" && addNotionalHigh > 0
        ? `Add ~${dollars(Math.max(0, addNotionalLow))}–${dollars(addNotionalHigh)} to reach the band → target (bounded by the funded cap).`
        : typeof addNotionalHigh === "number" && addNotionalHigh > 0
        ? `Add ~${dollars(addNotionalHigh)} toward target (bounded by the funded cap).`
        : "Size the add to the target gap after the close.";
    return {
      timing: "NEXT_SESSION",
      tone: "info",
      headline: "Deploy toward target—stage adds next session",
      subline: `You are under the effective target (${c} vs ${t}, band ${bandTxt}). If the regime holds at the close, stage adds next session toward the band—not intraday strength.`,
      bullets: [
        addTxt,
        earlyDefenseActive
          ? "Early Defense is active—respect the lower effective target; do not deploy above it."
          : "Deploy into the close-confirmed target; the live tape is situational only.",
        "Advisory only—confirm at the close and execute manually in TWS. No auto-trading."
      ]
    };
  }

  return {
    timing: "NEXT_SESSION",
    tone: "green",
    headline: "No action—monitor and follow workflow",
    subline: earlyDefenseActive
      ? "Early Defense is active, but the sleeve is already aligned with the effective target."
      : "Signals are informational. Execute only if close-confirmed signals stage orders.",
    bullets: [
      "Refresh data if needed.",
      // F5: see note above — undefined on this endpoint.
      vixTrimEnabled === false
        ? "Target-drop sizing and standard exits are the active risk controls."
        : vixTrimEnabled === true
        ? "If exit scan stages orders, execute next session."
        : "VIX trim state is unknown from this endpoint; if exit scan stages orders, execute next session.",
      "Stay disciplined: close-confirmation first."
    ]
  };
}

const PlaybookCard: React.FC<{
  pendingHarvest: boolean;
  harvestActive: boolean;
  // F4: declared here as well as on playbookFromSignals's args so the card and the headline band
  // read the SAME gate (see the "single source of truth" note at the playbookArgs call site);
  // without it the flag would only reach the card implicitly through the JSX spread.
  harvestEnabled?: boolean;
  earlyDefenseActive: boolean;
  live: boolean;
  overTarget: boolean;
  capBreached: boolean;
  targetPct?: number | null;
  currentPct?: number | null;
  trimNotional?: number | null;
  vixTrimEnabled?: boolean;
  targetSource?: string | null;
  underTarget?: boolean;
  addNotionalLow?: number | null;
  addNotionalHigh?: number | null;
  bandLowPct?: number | null;
}> = (p) => {
  const pb = playbookFromSignals(p);
  const tone: ToneKey = pb.tone === "red" ? "red" : pb.tone === "amber" ? "amber" : pb.tone === "info" ? "info" : "green";
  const palette = tonePalette[tone];
  const chip = pb.timing === "ACT_NOW" ? "Act now" : pb.timing === "WAIT_FOR_CLOSE" ? "Wait for close" : "Next session";

  return (
    <div
      className="h-full rounded-[18px] p-3.5"
      style={{
        ...panelChromeStyle,
        border: `1px solid ${ui.cardBorder}`,
      }}
    >
      {/* Slimmed (CIO, 2026-09-18). The headline is the stance band's Playbook column, word for
          word from the same playbookFromSignals call, and the old "Primary directive" box
          restated the timing chip a third time. What only this card says stays: the timing and
          exception chips, the one-line reason, and the operator checklist. */}
      <div className="flex items-start justify-between gap-3">
        <div className={sectionLabelClass} style={smallMutedText}>Today’s playbook</div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <StatusPill label={chip} tone={tone} />
          {p.capBreached ? <StatusPill label="Cap breach" tone="red" /> : null}
          {p.earlyDefenseActive ? <StatusPill label="Early Defense" tone="amber" /> : null}
          {/* F4: same gate as the playbook branch and the HarvestBadges chips — a disabled control
              must not post a green "Harvest active" pill next to a headline that no longer says so. */}
          {p.harvestActive && p.harvestEnabled !== false ? <StatusPill label="Harvest active" tone="green" /> : null}
        </div>
      </div>
      <div className="mt-1.5 text-[13px] leading-5" style={{ color: ui.textSecondary }}>{pb.subline}</div>

      <div className="mt-2.5 rounded-2xl px-3.5 py-2.5" style={{ ...metricTileStyle, borderColor: palette.border }}>
        <div className="text-[10px] uppercase tracking-[0.14em] font-semibold" style={smallMutedText}>Operator checklist</div>
        <ul className="mt-1.5 space-y-1 text-[0.88rem] leading-6" style={{ color: ui.textSecondary }}>
          {pb.bullets.map((b, i) => (
            <li key={i} className="flex gap-2">
              <span style={{ color: palette.chipText }}>•</span>
              <span>{b}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className="mt-2 text-[11px] leading-5" style={smallMutedText}>
        Helion executes close-to-close — treat this as the operating instruction, not commentary.
      </div>
    </div>
  );
};

const SpxPriorityCard: React.FC<{
  signal?: ReriskSignal | null;
  /** FUNDED (cost-basis) LEAPS weight and the cap it is governed by, both supplied by the page
   *  from the live payload. The card must not compute or hardcode either. */
  fundedWeight?: number | null;
  fundedCap?: number | null;
  /** The cost dollars behind `fundedWeight`, from the SAME source (engine funded_cost with the
   *  engine's weight, else the page's client cost), or null when no figure ties out. */
  fundedCost?: number | null;
  /** Harvest-lockout state from the shared derivation. Capital under an active lockout is
   *  withheld from the buy budget by the engine (rebalance_logic.py:947-950) and used to be
   *  invisible on this card, so the deploy gap read as free capital while it was not. */
  lockout?: HarvestLockout | null;
}> = ({ signal, fundedWeight = null, fundedCap = null, fundedCost = null, lockout = null }) => {
  if (!signal) return null;

  const state = String(signal.state || "").toUpperCase();
  const tone: ToneKey = state === "FULL" ? "green" : state === "PARTIAL" ? "amber" : state === "ALIGNED" ? "slate" : "red";
  const palette = tonePalette[tone];
  const chip = state === "FULL" ? "Full deploy" : state === "PARTIAL" ? "Partial deploy" : state === "ALIGNED" ? "Aligned" : "Hold";

  // `signal.current_weight` (the MARKET sleeve) is no longer printed here: it is the stance
  // band's "now", and this card now refers to it rather than repeating it.
  const suggested =typeof signal.suggested_target_pct === "number" && Number.isFinite(signal.suggested_target_pct)
    ? signal.suggested_target_pct
    : typeof signal.effective_target_pct === "number" && Number.isFinite(signal.effective_target_pct)
    ? signal.effective_target_pct
    : null;
  // BASIS — the deployment cap is `LEAPS_allocation_caps.funded_max`, which the engine enforces on
  // COST basis (strategy/rebalance_logic.py:730,938). `signal.current_weight` is a MARKET weight
  // (SUM(market_value)/NAV), so it must NEVER be divided by that cap: doing so pegged the bar at
  // 100% and captioned the sleeve "at the funded cap" while funded cost still had permitted
  // purchase headroom (restrictive today, permissive after a drawdown).
  // The track below is FUNDED weight vs the FUNDED cap, both passed in from the live payload —
  // no cap literal lives in this card, so a config change cannot leave it quoting a stale number.
  const capBasis = typeof fundedCap === "number" && Number.isFinite(fundedCap) && fundedCap > 0 ? fundedCap : null;
  const funded = typeof fundedWeight === "number" && Number.isFinite(fundedWeight) ? fundedWeight : null;
  const fundedCostUsd = funded != null && typeof fundedCost === "number" && Number.isFinite(fundedCost) && fundedCost > 0 ? fundedCost : null;
  const fundedTrackPct = funded != null && capBasis != null ? clamp((funded / capBasis) * 100, 0, 100) : null;
  const gapPct = typeof signal.deploy_gap_pct === "number" && Number.isFinite(signal.deploy_gap_pct) ? signal.deploy_gap_pct : null;
  const score = typeof signal.score === 'number' && Number.isFinite(signal.score) ? signal.score : null;
  const scorePct = score != null ? clamp(score * 100, 0, 100) : 0;
  const alignment = signal.surface_alignment ?? null;
  const alignmentStatus = String(alignment?.status || "").toUpperCase();
  const alignmentTone = (alignment?.tone === "green" || alignment?.tone === "amber" || alignment?.tone === "red" || alignment?.tone === "slate")
    ? (alignment.tone as ToneKey)
    : alignmentStatus === "ALIGNED"
    ? "green"
    : alignmentStatus === "CONFLICT" || alignmentStatus === "MISSED_OPPORTUNITY"
    ? "amber"
    : "slate";
  const alignmentPalette = tonePalette[alignmentTone];
  const convexityScore = typeof alignment?.convexity_quality_score === "number" ? alignment.convexity_quality_score : null;
  const alignmentScore = typeof alignment?.surface_alignment_score === "number" ? alignment.surface_alignment_score : null;
  const bestBucket = alignment?.market_best_bucket || null;
  const primaryBucket = alignment?.portfolio_primary_bucket || null;

  // Alignment context is diagnostic, not a directive - it collapses by default so the
  // card stops out-running the Playbook beside it. The summary line carries the status
  // pills either way, so a warning state is never hidden behind a closed disclosure.
  // Opens by itself only on CONFLICT / MISSED_OPPORTUNITY (CIO, 2026-09-18). A bucket
  // mismatch alone used to open it too, which on the live book was every day; its amber pill
  // stays on the summary line, so the flag is never hidden behind the closed disclosure.
  const alignmentNeedsAttention =
    alignmentStatus === "CONFLICT" ||
    alignmentStatus === "MISSED_OPPORTUNITY";
  const convexityEnvLabel = alignment
    ? String(alignment.convexity_environment || alignment.deployment_stance || "neutral")
        .replace(/_/g, " ")
        .toUpperCase()
    : null;

  // DEPLOY GAP net of the harvest lockout. `deploy_gap_notional` is
  // max(suggested_target - current_weight, 0) * NAV (strategy/rerisk_signal.py:321-322) —
  // it knows nothing about locked capital, so it is the one dollar figure on this card a
  // CIO reads directly as "deploy this much". When the lockout cannot be substantiated the
  // figure is withheld entirely rather than shown gross: an unverifiable "$X to deploy" is
  // the reassuring direction, which is exactly what the house rule forbids.
  // No prop => UNKNOWN, never "no lockout". deriveHarvestLockout({}) is the module's own
  // unknown state, so an unwired caller degrades to "—" instead of a confident gross figure.
  const lock = lockout ?? deriveHarvestLockout({});
  const deployGap = netDeployable(signal.deploy_gap_notional ?? null, lock);
  const lockTone: ToneKey =
    !lock.known ? "slate" : lock.status === "none" ? "green" : "amber";

  // Compact layout (CIO, 2026-09-18: "the SPX card could be much smaller"; ~520px → ~230px).
  // What went, and where it still lives: "Current sleeve (mkt)" is the stance band's "now";
  // the market-sleeve % in the old track caption was the same number a third time; the
  // lockout's all-clear sentence repeated its own chip (the sentence still shows whenever a
  // lock changes, withholds or cannot confirm the gap); the priority score's boxed tile is
  // now a header gauge. Nothing that gates or qualifies a figure was removed.
  const lockIsAllClear = lock.known && lock.status === "none";

  return (
    <div className="h-full rounded-[18px] p-3.5" data-testid="spx-priority-card" style={{ ...panelChromeStyle, border: `1px solid ${ui.cardBorder}` }}>
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
        <div className="min-w-0 flex-1">
          {/* No state chip here: the coloured state word leads the headline. The exception
              pills (cooldown, early defense) and the lockout chip stay — they say something
              the headline does not. */}
          <div className="flex flex-wrap items-center gap-2">
            <div
              className={sectionLabelClass}
              style={smallMutedText}
              title="Deployment detail behind the stance target above — timing is set by the Playbook."
            >
              SPX priority deployment engine
            </div>
            {signal.cooldown_active ? <StatusPill label="Cooldown" tone="slate" /> : null}
            {signal.early_defense_active ? <StatusPill label="Early Defense" tone="amber" /> : null}
            <StatusPill label={lock.headline} tone={lockTone} />
          </div>
          <div className="mt-1.5 text-[1.1rem] leading-tight font-semibold" style={{ color: ui.textPrimary }}>
            <span style={{ color: palette.chipText }}>{chip.toUpperCase()}</span>
            <span className="mx-2 opacity-40">·</span>
            <span>{signal.headline || "Portfolio throttle update"}</span>
          </div>
          <div className="mt-0.5 text-[12px] leading-5" style={{ color: ui.textSecondary }}>
            {signal.reason || "No deployment guidance available."}
          </div>
        </div>

        {/* Priority score as a header gauge rather than a boxed tile. */}
        <div className="w-[170px] shrink-0" title="Portfolio throttle — regime quality into deploy urgency.">
          <div className="flex items-baseline justify-between">
            <span className="text-[10px] uppercase tracking-[0.14em] font-semibold" style={smallMutedText}>Priority score</span>
            <span className="text-[1.15rem] leading-none font-semibold" style={{ color: ui.textPrimary }}>{fmtNum(score, 2)}</span>
          </div>
          <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full" style={barTrackStyle(true)}>
            <div className="h-full rounded-full" style={{ width: `${scorePct}%`, backgroundColor: palette.accent }} />
          </div>
        </div>
      </div>

      {/* One line of figures. Every "(mkt)" label and the net-of-lockout relabel are kept:
          suggested sleeve and the gap are MARKET basis, and a lock-reduced gap still shows
          its gross beside the net so nothing is hidden. */}
      <div className="mt-2.5 flex flex-wrap items-end gap-x-8 gap-y-2">
        <InlineStat label="Suggested sleeve (mkt)" value={fmtPct(suggested)} />
        <InlineStat
          label={deployGap.suppression === "partial" || deployGap.suppression === "full" ? "Deploy gap (mkt, net of lockout)" : "Deploy gap (mkt)"}
          value={
            deployGap.unknown ? (
              <span title={deployGap.disclosure}>—</span>
            ) : deployGap.suppression === "partial" || deployGap.suppression === "full" ? (
              <>
                {fmtUSD(deployGap.net)}
                <span className="ml-1 text-[11px] font-normal" style={smallMutedText}>
                  of {fmtUSD(deployGap.gross)}
                </span>
              </>
            ) : (
              fmtUSD(deployGap.net)
            )
          }
        />
        <InlineStat
          label={deployGap.suppression === "partial" || deployGap.suppression === "full" ? "Gap % of NAV (mkt, gross)" : "Gap % of NAV (mkt)"}
          value={gapPct == null ? "—" : fmtPct(gapPct)}
        />
      </div>

      <div className="mt-2.5 rounded-xl px-3 py-2" style={metricTileStyle}>
        <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
          <div className={sectionLabelClass} style={smallMutedText}>Deployment track — funded / cost basis</div>
          <div className="text-[11px]" style={smallMutedText}>
            Funded <span className="font-semibold" style={{ color: ui.textPrimary }}>{funded == null ? "—" : fmtPct(funded)}</span>
            {" "}of <span className="font-semibold" style={{ color: ui.textPrimary }}>{capBasis != null ? fmtPct(capBasis) : "—"}</span> cap
            {fundedCostUsd != null ? (
              <span data-testid="funded-track-cost">
                {" · "}
                <span className="font-semibold" style={{ color: ui.textPrimary }}>{fmtUsdMillions(fundedCostUsd)}</span> cost
              </span>
            ) : null}
          </div>
        </div>
        {fundedTrackPct != null ? (
          <div className="mt-1.5 relative h-2 w-full overflow-hidden rounded-full" style={barTrackStyle(true)}>
            <div className="absolute inset-y-0 left-0 rounded-full" style={{ width: `${fundedTrackPct}%`, backgroundColor: "var(--accent-info)", borderRight: `1px solid ${ui.subtleBarEdge}` }} />
          </div>
        ) : (
          // No numeric fallback: an unknown funded basis must not render as an empty bar, which
          // reads as "cap barely used" - the reassuring direction. Show nothing and say why.
          <div className="mt-1.5 text-[11px]" style={smallMutedText}>
            Funded (cost-basis) weight or cap unavailable — deployment track not shown. Do not read purchase headroom off this card.
          </div>
        )}
        {/* Basis honesty: the funded axis is what the engine enforces, and it is cost over TODAY's NAV, so it
            drifts with the market between trades with no buying (CIO 2026-10-29). The market sleeve is a
            separate, uncapped basis and is never plotted against this cap. */}
        <div className="mt-1 text-[11px] leading-4" style={smallMutedText} data-testid="funded-track-explainer">
          Funded % = cost paid ÷ today’s NAV, so it moves with the market between trades; only buys and sells change the cost,
          and the cap gates new buying only. The bar saturates at the cap (read a breach off the Funded LEAPS basis tile); the
          market sleeve (stance band &ldquo;now&rdquo;) is uncapped and not plotted here.
        </div>
        {/* HARVEST LOCKOUT — the capital this track does NOT account for. The engine subtracts
            locked harvest capital from the buy budget (rebalance_logic.py:947-950), which the
            bar cannot show. The chip in the header always states the lock; this sentence
            appears whenever it matters — a lock that shrinks or withholds the gap, a lock not
            applied to the budget, or a lock that cannot be confirmed. The wording is the
            shared derivation's, so Risk, Screener and this card cannot disagree. */}
        {lockIsAllClear ? null : (
          <div className="mt-1.5 border-t pt-1.5 text-[11px] leading-5" style={{ borderColor: ui.cardBorder, color: ui.textSecondary }}>
            {deployGap.disclosure}
          </div>
        )}
      </div>

      {alignment ? (
        <details
          className="mt-2.5 rounded-2xl"
          style={{ ...metricTileStyle, borderColor: alignmentPalette.border }}
          open={alignmentNeedsAttention}
        >
          <summary className="cursor-pointer select-none px-3 py-2">
            <div className="inline-flex flex-wrap items-center gap-2 align-middle">
              <span className={sectionLabelClass} style={smallMutedText}>Convexity alignment</span>
              <StatusPill label={alignment.label || "Context neutral"} tone={alignmentTone} />
              {alignment?.mismatch_flag ? <StatusPill label="Bucket mismatch" tone="amber" /> : null}
              <span className="text-[11px]" style={smallMutedText}>
                {convexityEnvLabel}
                {alignment.surface_regime ? ` · surface regime ${alignment.surface_regime}` : ""}
              </span>
            </div>
          </summary>
          <div className="px-3 pb-3">
            <div className="text-[13px] leading-6" style={{ color: ui.textSecondary }}>{alignment.reason || "Surface context unavailable."}</div>
            <div className="mt-2 grid grid-cols-2 gap-2 xl:grid-cols-4">
              <SignalMetric label="Convexity quality" value={convexityScore == null ? "—" : fmtNum(convexityScore, 2)} />
              <SignalMetric label="Surface alignment" value={alignmentScore == null ? "—" : fmtNum(alignmentScore, 2)} />
              <SignalMetric label="Best deployment bucket" value={bucketLabel(bestBucket)} />
              <SignalMetric label="Book primary bucket" value={bucketLabel(primaryBucket)} />
            </div>
          </div>
        </details>
      ) : null}

      {/* The loudest action verb on the page is the one action block that lacked an execution-boundary
          caption - trims are close-disciplined, so deploys must read the same way. */}
      <div className="mt-2 text-[11px] leading-5" style={smallMutedText}>
        Advisory · recommendation only — confirm at the close and execute manually in TWS next session. No auto-trading.
      </div>
    </div>
  );
};

/** =======================
 *  Endpoints
 *  ======================= */
const EP_LEAPS_SNAPSHOT = `${API_BASE_URL}/get_leaps_snapshot`;
const EP_LEAPS_METRICS_V2 = `${API_BASE_URL}/risk/leaps_metrics`;
const EP_NAV_LATEST_V2 = `${API_BASE_URL}/risk/nav_latest`;
const EP_LEAPS_RISK_LEGACY = `${API_BASE_URL}/get_leaps_risk_snapshot`;
const EP_NAV_LATEST_LEGACY = `${API_BASE_URL}/get_portfolio_nav_latest`;
const EP_REFRESH_PORTFOLIO = `${API_BASE_URL}/refresh_portfolio`;
const EP_HOME_RUN_HARVEST = `${API_BASE_URL}/home_run_harvest`;
const EP_LIVE_TAPE = `${API_BASE_URL}/live_tape`;
// The Screener's ladder (targets + room / on target / full at cost, 18m+ book) for Capital by Expiration (CIO 2026-10-29).
const EP_LADDER_STATUS = `${API_BASE_URL}/get_ladder_status`;
// Authenticated (risk/*) — confidential book/account data, NEVER on the anonymous tape.
const EP_INTRADAY_PNL = `${API_BASE_URL}/risk/intraday_pnl`;
// LIVE book greeks — the SAME source the Risk dashboard's Long-End Vol & Vega card uses, so the
// Intraday Book Exposure greeks here can't disagree with it. attribution=0 skips the heavier
// day-over-day decomposition (this card only needs the cockpit greeks).
const EP_LONG_END_VOL = `${API_BASE_URL}/risk/long_end_vol?attribution=0`;

/** =======================
 *  Types
 *  ======================= */
type MaybeNum = number | null | undefined;

interface HarvestCfg {
  // F4: the backend emits harvest_config.enabled (get_leaps_snapshot/__init__.py:892) and the
  // engine gates the whole harvest computation behind it (strategy/rebalance_logic.py:883).
  // It was absent from this interface, so the flag arrived, was stored, and could never be read.
  enabled?: boolean;
  z_threshold_pending?: number;
  z_threshold_active?: number;
  confirm_reversion_days?: number;
  require_delta_vix_negative?: boolean;
  selection?: string;
  max_stage_trim_pct?: number;
}

interface ReriskSignal {
  state?: string | null;
  score?: number | null;
  headline?: string | null;
  reason?: string | null;
  current_weight?: number | null;
  effective_target_pct?: number | null;
  suggested_target_pct?: number | null;
  deploy_gap_pct?: number | null;
  deploy_gap_notional?: number | null;
  cooldown_active?: boolean;
  early_defense_active?: boolean;
  spx_downtrend?: boolean;
  spx_pct_from_sma50?: number | null;
  spx_pct_from_sma200?: number | null;
  term_value?: number | null;
  surface_alignment?: {
    status?: string | null;
    tone?: string | null;
    label?: string | null;
    reason?: string | null;
    convexity_environment?: string | null;
    convexity_quality_score?: number | null;
    surface_alignment_score?: number | null;
    surface_distortion_score?: number | null;
    deployment_stance?: string | null;
    sweet_spot_signal?: string | null;
    surface_regime?: string | null;
    market_best_bucket?: string | null;
    policy_preferred_bucket?: string | null;
    portfolio_primary_bucket?: string | null;
    mismatch_flag?: boolean;
  } | null;
}

interface MetaNormalized {
  regime?: string;
  effective_regime?: string | null;
  phase?: number | string | null;
  vix_current?: number | null;
  vix_zscore?: number | null;
  vix_zscore_yesterday?: number | null;
  live_vix_used?: boolean;
  live_vix?: number | null;
  live_vix_zscore?: number | null;
  vix_date?: string | null;
  pending_harvest?: boolean;
  harvest_window?: boolean;
  reversion_confirm_days?: number | null;
  target_pct?: number | null;
  target_pct_effective?: number | null;
  target_source?: string | null;
  // get_leaps_snapshot (gatefix): true when yesterday's close z was unavailable and the band phase was ASSUMED
  // (rising vol). Absent on older backends -> treated as not approximate.
  target_approximate?: boolean;
  target_phase_basis?: "prior_session_z" | "assumed_rising_prior_z_unavailable" | string | null;
  rebalance_tolerance_pct?: number | null;
  vix_trim_enabled?: boolean;
  leaps_nav?: number | null;
  portfolio_nav?: number | null;
  current_weight?: number | null;
  caps?: { funded_max?: number | null; growth_max?: number | null } | null;
  defense_by_z?: boolean;
  defense_confirmed?: boolean;
  block_reason?: string | null;
  cooldown_active?: boolean;
  last_transition_utc?: string | null;
  spx?: {
    spx_close?: number | null;
    spx_current?: number | null;
    spx_prev_close?: number | null;
    spx_day_change?: number | null;
    spx_day_change_pct?: number | null;
    spx_live_used?: boolean;
    spx_prev_close_date?: string | null;
    spx_prev_close_source?: string | null;
    spx_sma?: number | null;
    // Close basis (the engine's) since the gatefix backend; the live comparison is the separate preview below.
    is_downtrend?: boolean;
    is_downtrend_basis?: "last_close" | string | null;
    is_downtrend_intraday_preview?: boolean | null;
    spx_trend_status?: "ok" | "unavailable" | string | null;
    // The defense gate's SPX leg. "skipped_unavailable" = SPX history missing, so the leg was SKIPPED (not passed).
    spx_leg?: "confirmed" | "not_confirmed" | "skipped_unavailable" | "not_required" | string | null;
    spx_sma200?: number | null;
    spx_pct_from_sma50?: number | null;
    spx_pct_from_sma200?: number | null;
  } | null;
  term?: { metric?: string | null; value?: number | null; VIX9D?: number; VIX3M?: number } | null;
  early_defense?: {
    enabled?: boolean;
    active?: boolean;
    target_pct?: number | null;
    reason?: string | null;
    conditions?: Record<string, any>;
  } | null;
  grind_axis?: {
    enabled?: boolean;
    active?: boolean;
    applied?: boolean;
    target_pct?: number | null;
    persist_window?: number | null;
    persist_days?: number | null;
    sma_days?: number | null;
    days_below_200_of_window?: number | null;
    pct_from_sma200?: number | null;
    only_when_zband_calm?: boolean;
    base_regime_calm?: boolean;
    rearm_mode?: string | null;
    rearm_confirm_days?: number | null;
    rearm_buffer_pct?: number | null;
    rearm_reclaim_confirmed?: boolean | null;
    since?: string | null;
    reason?: string | null;
  } | null;
  // Book-level giveback stop — SHADOW (CIO 2026-10-20). Reported by get_leaps_snapshot, acted on by nothing.
  giveback_stop_shadow?: {
    enabled?: boolean;
    shadow?: boolean;
    acting?: boolean;
    g?: number | null;
    floor_pct?: number | null;
    rearm_confirm_days?: number | null;
    rearm_buffer_pct?: number | null;
    sma_days?: number | null;
    fund?: string | null;
    active?: boolean | null;
    would_cap_target_pct?: number | null;
    peak_index?: number | null;
    peak_date?: string | null;
    current_index?: number | null;
    as_of?: string | null;
    drawdown_pct?: number | null;
    fire_at_drawdown_pct?: number | null;
    distance_to_fire_pp?: number | null;
    confirm_run?: number | null;
    spx_close?: number | null;
    spx_sma?: number | null;
    spx_as_of?: string | null;
    rearm_level?: number | null;
    spx_vs_rearm_pct?: number | null;
    spx_status?: "ok" | "unavailable" | "stale" | "no_sma" | string | null;
    rearm_status?: "counting" | "cannot_confirm" | string | null;
    market_date?: string | null;
    computed_at_utc?: string | null;
    ttl_seconds?: number | null;
    fires?: number | null;
    releases?: number | null;
    history?: Array<{ fired?: string | null; released?: string | null; depth_pct?: number | null; sessions_at_floor?: number | null }>;
    reason?: string | null;
    data?: {
      nav_sessions?: number | null; first_date?: string | null; flows_applied?: string[];
      anchored?: boolean | null; anchored_from?: string | null; anchor_count?: number | null;
      spx_source?: string | null; spx_last?: string | null; today_row_final?: boolean | null; today_row_dropped?: boolean | null;
    };
  } | null;
  harvest_config?: HarvestCfg;
  // F8: get_leaps_snapshot/__init__.py:893 emits "full" | "light" — the endpoint's own declaration
  // of whether the rebalance engine was consulted. It was dropped on the floor here.
  engine_mode?: string | null;
  rerisk_signal?: ReriskSignal | null;
  optionsMl?: OptionsMlOverview | null;
  // Market-anchored fresh-convexity benchmark (from the backend ORATS surface):
  // the VPD of a synthetic fresh ATM ~24-30m LEAPS. `vpd` is null when the surface
  // can't support it (see `reason`), in which case the UI falls back to an in-book
  // benchmark with a visible caveat.
  fresh_convexity?: {
    symbol?: string | null;
    tenor_bucket?: string | null;
    tenor_days?: number | null;
    atm_iv?: number | null;
    spot?: number | null;
    vpd?: number | null;
    coverage_ok?: boolean | null;
    source?: string | null;
    as_of?: string | null;
    reason?: string | null;
    // Per-tenor curve: fresh ATM VPD at each surface-supported maturity, so each
    // held position is anchored to a fresh contract near its OWN tenor.
    curve?: Array<{
      tenor_days?: number | null;
      median_dte?: number | null;
      atm_iv?: number | null;
      vpd?: number | null;
      count?: number | null;
      quality?: string | null;
    }> | null;
  } | null;
}

interface HomeRunHarvestSummary {
  activeLockCount?: number;
  eligibleUnlockCount?: number;
  lockedNotional?: number;
  lockedPctNav?: number | null;
  nextUnlockAtUTC?: string | null;
  candidateCount?: number;
  candidateHarvestNotional?: number;
  singleContractCandidateCount?: number;
}

interface HomeRunHarvestPayload {
  summary?: HomeRunHarvestSummary;
  activeLocks?: Array<any>;
  eligibleUnlocks?: Array<any>;
  recentLocks?: Array<any>;
  candidates?: Array<any>;
  policy?: Record<string, any>;
}

interface LeapsRiskRow {
  SnapshotAtUTC?: string;
  PriceSnapshotUTC?: string;
  Conid: number;
  AccountId?: string | null;
  Symbol: string;
  Expiration?: string | null;
  Right?: "C" | "P" | null;
  Strike?: MaybeNum;
  Quantity?: MaybeNum;
  Multiplier?: MaybeNum;
  UnderlyingPrice?: MaybeNum;
  LastPrice?: MaybeNum;
  Mid?: MaybeNum;
  Bid?: MaybeNum;
  Ask?: MaybeNum;
  BidSize?: MaybeNum;
  AskSize?: MaybeNum;
  Delta?: MaybeNum;
  Gamma?: MaybeNum;
  Theta?: MaybeNum;
  Vega?: MaybeNum;
  ImpliedVol?: MaybeNum;
  ImpliedVol_Broker?: MaybeNum;
  ImpliedVol_Fallback?: MaybeNum;
  OptionPriceUsed?: string | null;
  RiskFreeRate?: MaybeNum;
  DivYield?: MaybeNum;
  TTMYears?: MaybeNum;
  MissingGreeks?: boolean | number | null;
  AvgPrice?: MaybeNum;
  UnrealizedPnL?: MaybeNum;
  UnrealizedPnLPercent?: MaybeNum;
  RealizedPnL?: MaybeNum;
  CostBasis?: MaybeNum;
  AgeDays?: MaybeNum;
  EntryDate?: string | null;
}

interface NavLatest {
  total_nav?: number | null;
  as_of?: string | null;
  cash_nav?: number | null;
  source?: string | null;
  cashOverride?: any;
  cash_override?: any;
  // navReconciliation block from /risk/nav_latest — same source the Risk cockpit uses.
  // reconciliation.daily.estimated_nav_now = IBKR broker book + bank cash + accruals.
  // Typed against the shared declaration (2026-08-10) now that the estimated period
  // return renders here too — see @lib/navTypes for why one copy is load-bearing.
  reconciliation?: NavReconciliation | null;
}

/** =======================
 *  Format helpers
 *  ======================= */
const fmtSignedPct = (v?: number | null, dp = 1) => {
  if (v == null || isNaN(v)) return "—";
  const sign = v > 0 ? "+" : "";
  return `${sign}${v.toFixed(dp)}%`;
};

const fmtPct = (n?: number | null) =>
  n == null || isNaN(n) ? "—" : `${(n * 100).toFixed(1)}%`;

const fmtPnLPct = (n?: number | null) =>
  n == null || isNaN(n) ? "—" : `${(n * 100).toFixed(1)}%`;

const fmtBps = (n?: number | null) =>
  n == null || isNaN(n) ? "—" : `${n.toFixed(1)} bps`;

/** "$8.65M" for a million or more, else whole dollars. */
const fmtUsdMillions = (v?: number | null) =>
  v == null || !Number.isFinite(v) ? "—" : Math.abs(v) >= 1e6 ? `$${(v / 1e6).toFixed(2)}M` : fmtUSD(v);

const fmtNum = (v?: number | null, dp = 3) =>
  v == null || isNaN(v) ? "—" : v.toFixed(dp);

/** Gamma, shown as the change in this contract's delta for a 1% index move.
 *  Raw gamma on long-dated SPX is ~0.0002, which this table printed as 0.000000 for months --
 *  IBKR sends 3 decimals, so it WAS zero until the backend started computing it (2026-09-17).
 *  A missing input renders a dash with the reason, never 0.000. */
const gammaCell = (b: { Gamma?: MaybeNum; UnderlyingPrice?: MaybeNum }) => {
  const v = gammaPer1Pct(b?.Gamma, b?.UnderlyingPrice);
  if (v == null) {
    const why =
      b?.Gamma == null
        ? "No gamma on this line's risk snapshot."
        : "No index level on this line's risk snapshot, so gamma cannot be scaled to a 1% move.";
    return <span title={why}>—</span>;
  }
  const raw = typeof b?.Gamma === "number" ? b.Gamma : Number(b?.Gamma);
  return <span title={`${GAMMA_PER_1PCT_HINT} Raw gamma ${raw.toPrecision(3)}.`}>{v.toFixed(3)}</span>;
};

const fmtIV = (v?: number | null) => {
  if (v == null || isNaN(v)) return "—";
  const pct = v > 1.5 ? v : v * 100;
  return `${pct.toFixed(1)}%`;
};

/** =======================
 *  Action / robustness helpers
 *  ======================= */
const clamp = (x: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, x));

type ActionState = "HOLD" | "TRIM" | "ADD";
// Regime-Pressure severity labels — deliberately distinct words from the close REGIME bands
// (Lean-In / Sweet-Spot / High-Vol / Panic) so "Regime Pressure: Elevated" can't be confused
// with "Regime: High-Vol": the pressure gauge is a 0–100 summary dial, not the regime itself.
type ActionSeverity = "Calm" | "Watch" | "Elevated" | "Extreme";

const computeRegimePressure = (m: MetaNormalized | null): { score: number | null; severity: ActionSeverity; reasons: string[] } => {
  if (!m) return { score: null, severity: "Calm", reasons: [] };

  const z = typeof m.vix_zscore === "number" && Number.isFinite(m.vix_zscore)
    ? m.vix_zscore
    : (typeof m.vix_zscore_yesterday === "number" && Number.isFinite(m.vix_zscore_yesterday) ? m.vix_zscore_yesterday : null);

  const reasons: string[] = [];

  // Make a stale primary input visible: if today's close z is missing and we fell back to the
  // prior close, say so in the reasons tooltip rather than presenting it as current.
  const usedPriorZ =
    !(typeof m.vix_zscore === "number" && Number.isFinite(m.vix_zscore)) &&
    typeof m.vix_zscore_yesterday === "number" && Number.isFinite(m.vix_zscore_yesterday);
  if (usedPriorZ) reasons.push("Based on prior-close z");

  const termMetric = (m.term?.metric || "").toString().toLowerCase();
  const termVal = typeof m.term?.value === "number" && Number.isFinite(m.term.value) ? m.term.value : null;
  const termRisk =
    (termMetric === "ratio" && termVal != null && termVal >= 1.05) ||
    (termMetric === "vvix" && termVal != null && termVal >= 95);
  if (termRisk) reasons.push("Term structure stressed");

  const downtrend = !!m.spx?.is_downtrend;
  if (downtrend) reasons.push("SPX downtrend");

  const below200 = typeof m.spx?.spx_pct_from_sma200 === "number" && Number.isFinite(m.spx.spx_pct_from_sma200)
    ? m.spx.spx_pct_from_sma200 < 0
    : false;
  if (below200) reasons.push("Below 200DMA");

  const defenseConfirmed = m.defense_confirmed === true;
  if (defenseConfirmed) reasons.push("Defense confirmed");

  if (z == null) {
    const base = (termRisk || downtrend || below200 || defenseConfirmed) ? 55 : 0;
    const score = base ? clamp(base + (termRisk ? 10 : 0) + (downtrend ? 10 : 0) + (below200 ? 10 : 0), 0, 100) : null;
    const severity: ActionSeverity = score == null ? "Calm" : score >= 80 ? "Extreme" : score >= 60 ? "Elevated" : score >= 30 ? "Watch" : "Calm";
    return { score, severity, reasons };
  }

  const zScore = clamp(30 + (z * 30), 0, 100);
  const score = clamp(
    zScore + (termRisk ? 12 : 0) + (downtrend ? 10 : 0) + (below200 ? 10 : 0) + (defenseConfirmed ? 8 : 0),
    0,
    100
  );

  const severity: ActionSeverity = score >= 85 ? "Extreme" : score >= 65 ? "Elevated" : score >= 35 ? "Watch" : "Calm";
  return { score, severity, reasons };
};

const resolveAllocationToleranceBand = (
  targetPct: number | null,
  tolerancePct?: number | null
): number => {
  const relTol =
    typeof tolerancePct === "number" && Number.isFinite(tolerancePct)
      ? tolerancePct
      : ALLOCATION_TOLERANCE;

  if (typeof targetPct !== "number" || !Number.isFinite(targetPct)) {
    return relTol;
  }

  return Math.abs(targetPct) * relTol;
};

const formatAllocationTolerance = (
  targetPct: number | null | undefined,
  tolerancePct?: number | null
): string => {
  const relTol =
    typeof tolerancePct === "number" && Number.isFinite(tolerancePct)
      ? tolerancePct
      : ALLOCATION_TOLERANCE;
  const absTol = resolveAllocationToleranceBand(
    typeof targetPct === "number" && Number.isFinite(targetPct) ? targetPct : null,
    tolerancePct
  );
  return `${(relTol * 100).toFixed(0)}% of target (~${(absTol * 10000).toFixed(0)} bps)`;
};

const computeAllocationAction = (m: MetaNormalized | null): {
  state: ActionState;
  overPctPts: number | null;
  trimNotional: number | null;
  targetPct: number | null;
  currentPct: number | null;
  reason: string;
} => {
  if (!m) {
    return { state: "HOLD", overPctPts: null, trimNotional: null, targetPct: null, currentPct: null, reason: "No meta" };
  }

  const target =
    typeof m.target_pct_effective === "number" && Number.isFinite(m.target_pct_effective)
      ? m.target_pct_effective
      : typeof m.target_pct === "number" && Number.isFinite(m.target_pct)
      ? m.target_pct
      : null;

  const current = typeof m.current_weight === "number" && Number.isFinite(m.current_weight) ? m.current_weight : null;
  const nav = typeof m.portfolio_nav === "number" && Number.isFinite(m.portfolio_nav) ? m.portfolio_nav : null;

  if (target == null || current == null) {
    return { state: "HOLD", overPctPts: null, trimNotional: null, targetPct: target, currentPct: current, reason: "Allocation telemetry missing" };
  }

  const delta = current - target;
  const overPctPts = delta * 100;
  const tol = resolveAllocationToleranceBand(target, m.rebalance_tolerance_pct);

  if (delta > tol) {
    const trimNotional = nav != null ? delta * nav : null;
    return { state: "TRIM", overPctPts, trimNotional, targetPct: target, currentPct: current, reason: "Over target" };
  }
  if (delta < -tol) {
    return { state: "ADD", overPctPts, trimNotional: null, targetPct: target, currentPct: current, reason: "Under target" };
  }
  return { state: "HOLD", overPctPts, trimNotional: null, targetPct: target, currentPct: current, reason: "Within tolerance" };
};

/** =======================
 *  Regime helpers
 *  ======================= */
const getRegimeDisplay = (label: string | undefined): { name: string; band: string; color: string; accent: string } => {
  const l = label || "";
  // Colors are theme-aware status tokens (not raw light-theme hex) so the regime name — the most
  // scanned element on the page — clears the dark-mode contrast floor on the near-black surface.
  if (l.startsWith("Panic")) return { name: "Panic", band: "(z ≥ 2.5)", color: "var(--status-danger-text)", accent: "border-l-down" };
  if (l.startsWith("High-Vol")) return { name: "High-Vol", band: "(1.0 ≤ z < 2.5)", color: "var(--status-warning-text)", accent: "border-l-elevated" };
  if (l.startsWith("Sweet-Spot")) return { name: "Sweet-Spot", band: "(-1.25 ≤ z < 1.0)", color: "var(--status-positive-text)", accent: "border-l-up" };
  if (l.startsWith("Lean-In") || l.startsWith("Calm")) return { name: "Lean-In", band: "(-1.50 ≤ z ≤ -1.26)", color: "var(--status-info-text)", accent: "border-l-info" };
  return { name: l || "—", band: "(unknown)", color: ui.textPrimary, accent: "border-l-subtle" };
};

// REMOVED 2026-07-31 (audit Tier-0 #12): the Vega/NAV "target band".
//
// It was a hardcoded constant in this file with NO config, NO backend and NO memo
// anywhere in either repo, yet it drove an "under band / in band / over band" verdict
// on the Signals page. Three things were wrong with it:
//
//   1. NON-MONOTONIC AND INVERTED at the calm end. Panic 80-100, High-Vol 100-120,
//      Sweet-Spot 180-220, Lean-In 220-250, Very-Calm 150-180 -- so the CALMEST
//      regime got a lower allowance than the two regimes beside it.
//   2. ITS OWN TOOLTIP CONTRADICTED IT: "richer vol regimes warrant less vega", while
//      Very-Calm (the least rich) sat below Sweet-Spot.
//   3. IT CONTRADICTS POLICY. Sweet-Spot, Lean-In and Very-Calm all target 40% funded
//      (rebalance_engine.json, 2026-07-31), so a band that descends across them told
//      you to cut exposure in exactly the regimes where policy wants it full.
//
// Vega/NAV itself is a real and useful measurement, so it is still displayed. What is
// gone is the invented target around it and the verdict word. If a vega budget is
// worth having it should be AUTHORED in rebalance_engine.json beside the regime bands,
// with a rationale, and surfaced from the backend -- not inherited from a UI constant.

/** =======================
 *  Time helpers
 *  ======================= */
const num = (v: any, d: number = 0) => (v == null || isNaN(+v) ? d : +v);

/* =======================
 *  VPD efficiency SEASONING bands (single source of truth)
 * =======================
 * Efficiency = held VPD ÷ fresh-market benchmark VPD, as a PERCENT. Read it as
 * roughly the share of a contract's premium still carried as TIME VALUE. A low
 * reading therefore means the line has gone deep in-the-money — normally a WINNER
 * and a position that has de-levered itself — not a defect.
 *
 * Recalibrated 2026-07-28 for the market anchor: a genuinely fresh contract is a
 * stronger denominator than the old in-book yardstick, so the same position reads
 * ~15pts lower than it did under the in-book cutoffs. Config-driven so these can be
 * tuned once live data accumulates.
 *
 * 2026-09-09 (CIO): thresholds UNCHANGED; labels and colour changed. The sub-50 band
 * used to read "recycle candidate" in warning/critical styling — an action prompt
 * pointing at a trade our own research disfavours. There is no roll or recycle rule
 * behind this number and there should not be one:
 *   • docs/STUDY_P10_INSTRUMENT_STRUCTURE_MEMO.md — moving into cheaper, more
 *     convex lines at constant dollar weight is LEVERAGE, not alpha (vol 21% →
 *     53–69%, Sharpe → 0.56–0.66).
 *   • docs/RICHNESS_HARVEST_MEMO.md — no beta-orthogonal exit edge; exits stay
 *     regime-locked.
 *   • HelionBackend/exit_signals.json → home_run_redeployment.notes — Step 1
 *     (2026-09) tested immediate redeploy and it FAILED; delay-0 is the one variant
 *     carrying adverse evidence.
 * This is a DIAGNOSTIC (a seasoning gauge), not a screen that proposes a trade.
 * Keep in sync with signalsMethodologyContent.ts and the holdings-table footnote. */
const EFFICIENCY_BANDS = { fresh: 85, healthy: 70, softening: 50 } as const;
/* Union keys are historical IDENTIFIERS, deliberately left alone to keep the diff
 * small — they are not what the CIO reads. Display labels live in tierLabel inside
 * efficiencyBadge(); the "recycle" key renders as "deep ITM". */
type EfficiencyTier = "above" | "fresh" | "healthy" | "softening" | "recycle";
const efficiencyTier = (pctEff: number): EfficiencyTier => {
  if (pctEff >= 100) return "above";              // richer than a fresh contract (e.g. OTM)
  if (pctEff >= EFFICIENCY_BANDS.fresh) return "fresh";      // fresh-equivalent convexity
  if (pctEff >= EFFICIENCY_BANDS.healthy) return "healthy";  // lightly seasoned
  if (pctEff >= EFFICIENCY_BANDS.softening) return "softening"; // seasoned
  return "recycle";                               // deep ITM — premium is mostly intrinsic
};

/** =======================
 *  Robust fetch helpers
 *  ======================= */
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

const fetchJson = async (url: string, signal?: AbortSignal) => {
  const res = await fetch(url, {
    method: "GET",
    headers: { Accept: "application/json" },
    signal
  });

  if (!res.ok) {
    const err: any = new Error(`${url} HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
};

const isMetaReadyForTiles = (m: MetaNormalized | null) => {
  if (!m) return false;

  const vixOk = typeof m.vix_current === "number" && Number.isFinite(m.vix_current);
  const zOk = typeof m.vix_zscore === "number" && Number.isFinite(m.vix_zscore);
  const regimeOk = typeof m.regime === "string" && (m.regime || "").trim().length > 0;
  const spxOk = typeof m.spx?.spx_current === "number" && Number.isFinite(m.spx.spx_current)
    || typeof m.spx?.spx_close === "number" && Number.isFinite(m.spx.spx_close)
    || typeof m.spx?.spx_prev_close === "number" && Number.isFinite(m.spx.spx_prev_close);

  return vixOk || zOk || regimeOk || spxOk;
};

const backoffMs = (attempt: number) => {
  return attempt === 0 ? 0 : attempt * attempt * META_BACKOFF_MS;
};

const fmtAge = (ageMs: number | null | undefined): string => {
  if (ageMs == null || !Number.isFinite(ageMs)) return "—";
  const s = Math.max(0, Math.floor(ageMs / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s ago`;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${h}h ${String(mm).padStart(2, "0")}m ago`;
};

/** =======================
 *  UI micro-helpers
 *  ======================= */
const bar = (pct: number) => ({
  width: `${clamp(pct, 0, 100)}%`,
});

const safePct = (val: number, maxAbs: number) => {
  if (!Number.isFinite(val) || !Number.isFinite(maxAbs) || maxAbs <= 0) return 0;
  return (Math.abs(val) / maxAbs) * 100;
};

const barTrackStyle = (strong = false): React.CSSProperties => ({
  backgroundColor: strong ? ui.subtleBarBgStrong : ui.subtleBarBg,
  border: `1px solid ${ui.subtleBarEdge}`,
  boxShadow: `inset 0 1px 1px ${ui.innerShadow}`,
});

const neutralBarFillStyle = (strong = false): React.CSSProperties => ({
  background: strong
    ? `linear-gradient(90deg, ${ui.subtleBarFillStrong} 0%, ${ui.subtleBarFill} 100%)`
    : `linear-gradient(90deg, ${ui.subtleBarFill} 0%, ${ui.subtleBarFillStrong} 100%)`,
  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.10)",
});

const severityBarFillStyle = (sev: ActionSeverity): React.CSSProperties => {
  if (sev === "Extreme") {
    return {
      background: "linear-gradient(90deg, #dc2626 0%, #b91c1c 100%)",
      boxShadow: "inset 0 1px 0 rgba(255,255,255,0.10)",
    };
  }
  if (sev === "Elevated") {
    return {
      background: "linear-gradient(90deg, #ea580c 0%, #c2410c 100%)",
      boxShadow: "inset 0 1px 0 rgba(255,255,255,0.10)",
    };
  }
  if (sev === "Watch") {
    return {
      background: "linear-gradient(90deg, #eab308 0%, #d97706 100%)",
      boxShadow: "inset 0 1px 0 rgba(255,255,255,0.10)",
    };
  }
  return neutralBarFillStyle(true);
};

const loadCachedMeta = (): MetaNormalized | null => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(META_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? (parsed as MetaNormalized) : null;
  } catch {
    return null;
  }
};

const persistCachedMeta = (m: MetaNormalized | null) => {
  if (typeof window === "undefined" || !m) return;
  try {
    window.sessionStorage.setItem(META_CACHE_KEY, JSON.stringify(m));
  } catch {
    // ignore storage failures
  }
};

/** =======================
 *  Component
 *  ======================= */
// ---- Zone A: LIVE MARKET TAPE ----------------------------------------------
// Best-effort intraday values from /live_tape (IBKR snapshot on the 30s poll).
// Deliberately separated from the close-confirmed signal cockpit below: this is
// situational awareness only and never moves the playbook.

// Lightweight "is the US market in regular hours right now" check (ET, weekday,
// 09:30–16:00). Holiday-blind by design — it is combined with the backend payload's
// holiday-aware `market_open` flag so holidays still read as closed. Drives only the
// freshness label, never any signal.
function isClockRTH(): boolean {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York",
      weekday: "short",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).formatToParts(new Date());
    const wd = parts.find((p) => p.type === "weekday")?.value ?? "";
    if (wd === "Sat" || wd === "Sun") return false;
    const hh = parseInt(parts.find((p) => p.type === "hour")?.value ?? "0", 10);
    const mm = parseInt(parts.find((p) => p.type === "minute")?.value ?? "0", 10);
    const mins = hh * 60 + mm;
    return mins >= 9 * 60 + 30 && mins < 16 * 60;
  } catch {
    return true; // if we can't tell, defer to the >60s stale check
  }
}

const LiveTapeStrip: React.FC<{ tape: any | null; ageMs: number | null; streaming?: boolean; vixLive?: number | null; vixClose?: number | null; vixLiveUsed?: boolean }> = ({ tape, ageMs, streaming, vixLive, vixClose, vixLiveUsed }) => {
  const spx = tape?.spx ?? {};
  const term = tape?.vix_term ?? {};
  const rv = typeof tape?.realized_vol_21d === "number" ? tape.realized_vol_21d : null;
  const ret = typeof spx.intraday_return_pct === "number" ? spx.intraday_return_pct : null;
  const trend: string | null = spx.trend ?? null;
  const ratio = typeof term.ratio === "number" ? term.ratio : null;
  const termState: string | null = term.state ?? null;
  const arrow = ret == null ? "" : ret > 0 ? "▲" : ret < 0 ? "▼" : "▬";
  const signedPct = (v: any, dp = 1) =>
    typeof v === "number" ? `${v >= 0 ? "+" : ""}${(v * 100).toFixed(dp)}%` : "—";

  // Freshness: only treat data as "stale" (a real problem) while the market is OPEN.
  // Off-hours the broadcast is idle by design — show "market closed", not an alarm.
  // marketOpen = clock says regular hours AND the backend (holiday-aware) didn't say closed.
  const STALE_MS = 60_000;
  const marketOpen = isClockRTH() && (tape?.market_open !== false);
  const stale = marketOpen && typeof ageMs === "number" && ageMs > STALE_MS;

  // VIX: live vs the settled close (signal basis). Both come from the same meta source
  // so the delta is a meaningful intraday change, not a cross-source artifact.
  const haveLiveVix = !!vixLiveUsed && typeof vixLive === "number" && Number.isFinite(vixLive);
  const haveCloseVix = typeof vixClose === "number" && Number.isFinite(vixClose);
  const vixDelta = haveLiveVix && haveCloseVix ? (vixLive as number) - (vixClose as number) : null;
  const vixArrow = vixDelta == null ? "" : vixDelta > 0 ? "▲" : vixDelta < 0 ? "▼" : "▬";

  // Broad-index context (live level + intraday % vs IBKR prior close) + vol-of-vol.
  // Situational tiles next to SPX; null on IND-entitlement miss renders as "no live
  // quote", never a stale level. change_pct is null when the prior close is unavailable.
  // Raw context-tile reads from THIS push. change_pct is null when the prior close is unavailable.
  const ndxLvlRaw = typeof tape?.indices?.ndx?.level === "number" ? tape.indices.ndx.level : null;
  const ndxChgRaw = typeof tape?.indices?.ndx?.change_pct === "number" ? tape.indices.ndx.change_pct : null;
  const rutLvlRaw = typeof tape?.indices?.rut?.level === "number" ? tape.indices.rut.level : null;
  const rutChgRaw = typeof tape?.indices?.rut?.change_pct === "number" ? tape.indices.rut.change_pct : null;
  const vvixRaw = typeof tape?.vvix === "number" ? tape.vvix : null;
  const vvixChgRaw = typeof tape?.vvix_change_pct === "number" ? tape.vvix_change_pct : null;
  const arrowFor = (v: number | null) => (v == null ? "" : v > 0 ? "▲" : v < 0 ? "▼" : "▬");

  // Sticky last-good for the situational context tiles (NDX / RUT / VVIX). IBKR's IND snapshots
  // for these drop out intermittently (entitlement blips, momentary snapshot gaps), which used to
  // flicker the tile to "—". Instead hold the last real reading and mark it "held" — visibly NOT a
  // fresh tick, never presented as live. Same last-good-but-honest pattern the intraday-P&L poll
  // and the SPX degraded badge already use. Situational only: signals never read the tape.
  // Level + change are held together so a held tile shows one consistent last-good snapshot.
  const heldRef = useRef<Record<string, { level: number; change: number | null; atMs: number }>>({});
  const stickyTile = (key: string, level: number | null, change: number | null) => {
    if (level != null && Number.isFinite(level)) {
      heldRef.current[key] = { level, change, atMs: Date.now() };
      return { level, change, held: false, ageMs: null as number | null };
    }
    const h = heldRef.current[key];
    if (h) return { level: h.level as number | null, change: h.change, held: true, ageMs: Date.now() - h.atMs };
    return { level: null as number | null, change: null as number | null, held: false, ageMs: null as number | null };
  };
  const ndx = stickyTile("ndx", ndxLvlRaw, ndxChgRaw);
  const rut = stickyTile("rut", rutLvlRaw, rutChgRaw);
  const vvixTile = stickyTile("vvix", vvixRaw, vvixChgRaw);

  // House up=GREEN / down=RED for a signed price move or distance-from-average (traditional finance
  // cadence), via the centralized up/down tokens. Returns undefined at exactly zero or when not a
  // number, so it falls back to the muted text color. Volatility tiles (VIX / VVIX) deliberately opt
  // out — "up" there isn't "good".
  const moveColor = (v: number | null | undefined): string | undefined =>
    typeof v !== "number" || !Number.isFinite(v)
      ? undefined
      : v > 0
      ? "text-up"
      : v < 0
      ? "text-down"
      : undefined;

  // Feed health: surface the payload's OWN errors[] (previously ignored by the UI) so a
  // partially-failed snapshot reads honestly instead of last-good-as-fresh. Only CRITICAL
  // feeds (live SPX, close-history baseline) flag degraded; context tiles null silently.
  const errs: string[] = Array.isArray(tape?.errors) ? tape.errors : [];
  const spxFeedDown = errs.includes("spx");
  const closeBaselineDown = errs.includes("close_history");
  const degraded = !!tape?.degraded || spxFeedDown || closeBaselineDown;

  // Baseline staleness is a SEPARATE failure from a feed error: the close history loaded
  // fine, it is just missing a session, so `errors`/`degraded` stay empty while the move
  // below silently spans more than one day. Kept out of `degraded` so the two read
  // distinctly — "the feed broke" vs "the number you're comparing to is the wrong day".
  const prevStale = spx.prev_close_stale === true;
  const prevStaleSessions =
    typeof spx.prev_close_sessions_stale === "number" ? spx.prev_close_sessions_stale : null;
  // "2026-07-29" -> "Jul 29". Parsed as a plain date (no timezone shift).
  const shortDate = (iso: any): string | null => {
    if (typeof iso !== "string") return null;
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
    if (!m) return null;
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return Number.isNaN(d.getTime())
      ? null
      : d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  };
  const prevDateLabel = shortDate(spx.prev_close_date);
  const prevStaleTitle = prevStale
    ? `The stored SPX close history is ${prevStaleSessions ?? "1"} session(s) behind: the newest close is ${
        prevDateLabel ?? "an older session"
      }, but ${shortDate(spx.expected_prev_close_date) ?? "the prior session"} should be there. ` +
      `The move shown spans MORE than one session, so it overstates today. Trend and realized vol use the same history.`
    : undefined;

  // Fixed-width slot so a tile holds its place whether the value fills or falls blank ("—"):
  // no shrink (blank stays put), no wrap at the row level (see container). Width sized to the
  // tile's full, populated content so nothing reflows when live data drops in or out.
  const Tile: React.FC<{ label: string; hint?: string; w?: string; children: React.ReactNode }> = ({ label, hint, w = "w-[136px]", children }) => (
    <div className={`${w} shrink-0`}>
      <div className="text-[10px] uppercase tracking-[0.16em] opacity-55 whitespace-nowrap">
        {label}
        {hint ? <InfoHint text={hint} /> : null}
      </div>
      <div className="mt-0.5">{children}</div>
    </div>
  );

  // Demoted footer ticker: lives inside the stance card, beneath the close-confirmed verdict.
  // Top divider + faint fill mark it as the secondary, situational-only zone — never a card of
  // its own competing with the verdict for attention.
  return (
    <div
      className="px-3.5 pt-3 pb-3.5"
      style={{
        borderTop: `1px solid ${ui.cardBorder}`,
        background: ui.overlaySoft,
      }}
    >
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <span className="text-[10px] uppercase tracking-[0.18em] opacity-60">Live market tape</span>
        {marketOpen ? (
          <LiveBadge title={streaming ? "Streaming live via SignalR — situational awareness only" : "Live updates paused — using periodic refresh"} />
        ) : null}
        {stale ? (
          <span
            className="text-[10px] font-semibold text-warn"
            title={ageMs != null ? `Last update ${fmtAge(ageMs)} — the live feed may be delayed` : "The live feed may be delayed"}
          >
            ⚠ feed delayed
          </span>
        ) : (
          <span className="text-[10px] opacity-55">
            {!marketOpen ? "market closed · as of last close" : streaming ? "streaming" : "polling"}
          </span>
        )}
        {/* Feed-health honesty: quiet when healthy, speaks up only when a CRITICAL field
            inside the last (otherwise on-time) push actually failed. */}
        {degraded && marketOpen && !stale ? (
          <span
            className="text-[10px] font-semibold text-warn"
            title={
              spxFeedDown
                ? "Live SPX snapshot failed on the last update — values shown are last-good, not a fresh tick"
                : "The close-history baseline failed to load — trend and realized vol may be incomplete"
            }
          >
            ⚠ {spxFeedDown ? "SPX feed unavailable" : "baseline stale"}
          </span>
        ) : null}
        {/* Stale prior close: shown whether or not the market is open, because the wrong
            baseline persists overnight and across weekends — exactly when nobody is
            watching the feed-health chip. */}
        {prevStale ? (
          <span className="text-[10px] font-semibold text-warn" title={prevStaleTitle}>
            ⚠ prev close {prevDateLabel ? `is ${prevDateLabel}` : "stale"} · move spans{" "}
            {prevStaleSessions != null ? prevStaleSessions + 1 : 2} sessions
          </span>
        ) : null}
        <span className="text-[10px] opacity-50 ml-auto">
          Signals act on the close; the live tape is situational awareness only.
        </span>
      </div>

      {!tape ? (
        <div className="text-[12px] opacity-55">Live tape loading…</div>
      ) : (
        <div className="flex flex-nowrap items-stretch gap-x-6 overflow-x-auto tabular-nums">
          <Tile
            label="SPX now"
            w="w-[152px]"
            hint="Live IBKR SPX snapshot and today's move vs the prior settled close. The date beside 'prev' is the session that close came from — if it isn't the last completed session, the move shown spans more than one day and is flagged above."
          >
            <div className="text-[1.15rem] leading-none font-bold" style={{ color: ui.textPrimary }}>
              {typeof spx.live === "number" ? fmtNum(spx.live, 2) : "—"}
            </div>
            <div className="text-[11px] mt-1" style={{ color: ui.textSecondary }}>
              {ret == null ? "intraday move —" : <span className={moveColor(ret)}>{arrow} {signedPct(ret, 2)}</span>}
              {/* Always name the session the baseline came from. The number alone reads as
                  "yesterday" even when it isn't — the silent failure this tile had. */}
              {typeof spx.prev_close === "number" ? (
                <span className={prevStale ? "text-warn font-semibold" : undefined} title={prevStaleTitle}>
                  {` · prev ${fmtNum(spx.prev_close, 0)}`}
                  {prevDateLabel ? ` (${prevDateLabel})` : ""}
                </span>
              ) : null}
            </div>
          </Tile>

          <Tile label="SPX trend" w="w-[272px]" hint="LIVE, intraday SPX vs its 50- and 200-day moving averages. Each big number is the actual SPX price of that average (from the stored close history) — the price SPX would cross — with the live % distance small beside it, and the overall trend chip below. Uses the live price, so it will differ from the Grind axis tile's % vs the same 200-day, which is close-basis (the signal). Situational only — signals act on the close.">
            {/* Each average leads with its big bold SMA level and keeps its small live % distance
                inline right beside it (so the % always tracks its own number); the overall trend
                chip sits on its own line beneath. */}
            <div className="leading-none font-bold tabular-nums" style={{ color: ui.textPrimary }}>
              <span className="text-[10px] font-semibold opacity-55 mr-0.5">50d</span>
              <span className="text-[1.15rem]">{typeof spx.sma50 === "number" ? Math.round(spx.sma50).toLocaleString() : "—"}</span>
              <span className={`text-[10px] font-semibold ml-1 ${moveColor(spx.vs_sma50_pct) ?? "opacity-70"}`}>{signedPct(spx.vs_sma50_pct)}</span>
              <span className="text-[1.15rem] font-normal opacity-40 mx-2">·</span>
              <span className="text-[10px] font-semibold opacity-55 mr-0.5">200d</span>
              <span className="text-[1.15rem]">{typeof spx.sma200 === "number" ? Math.round(spx.sma200).toLocaleString() : "—"}</span>
              <span className={`text-[10px] font-semibold ml-1 ${moveColor(spx.vs_sma200_pct) ?? "opacity-70"}`}>{signedPct(spx.vs_sma200_pct)}</span>
            </div>
            <div className="mt-1.5">
              <span className={chipClass(trend === "uptrend" ? "positive" : trend === "downtrend" ? "danger" : "neutral")}>
                {trend ? trend.charAt(0).toUpperCase() + trend.slice(1) : "—"}
              </span>
            </div>
          </Tile>

          <Tile label="NDX" w="w-[142px]" hint="Live Nasdaq-100 index (IBKR), for broad-market context. Intraday move is vs the IBKR prior close (field 7741). If the live quote drops out the last-good level is held and marked 'held' with its age — never shown as a fresh tick.">
            <div className="text-[1.15rem] leading-none font-bold" style={{ color: ndx.level == null ? ui.textMuted : ndx.held ? ui.textSecondary : ui.textPrimary }}>
              {ndx.level == null ? "—" : fmtNum(ndx.level, 2)}
            </div>
            <div className="text-[11px] mt-1" style={{ color: ui.textSecondary }}>
              {ndx.level == null
                ? (marketOpen ? "no live quote" : "—")
                : ndx.held
                ? <span className="opacity-70">held · {fmtAge(ndx.ageMs)}</span>
                : ndx.change == null
                ? "live · Nasdaq-100"
                : <><span className={moveColor(ndx.change)}>{arrowFor(ndx.change)} {signedPct(ndx.change, 2)}</span> · Nasdaq-100</>}
            </div>
          </Tile>

          <Tile label="RUT" w="w-[148px]" hint="Live Russell 2000 index (IBKR) — small-cap / risk-appetite context. Intraday move is vs the IBKR prior close (field 7741). If the live quote drops out the last-good level is held and marked 'held' with its age — never shown as a fresh tick.">
            <div className="text-[1.15rem] leading-none font-bold" style={{ color: rut.level == null ? ui.textMuted : rut.held ? ui.textSecondary : ui.textPrimary }}>
              {rut.level == null ? "—" : fmtNum(rut.level, 2)}
            </div>
            <div className="text-[11px] mt-1" style={{ color: ui.textSecondary }}>
              {rut.level == null
                ? (marketOpen ? "no live quote" : "—")
                : rut.held
                ? <span className="opacity-70">held · {fmtAge(rut.ageMs)}</span>
                : rut.change == null
                ? "live · Russell 2000"
                : <><span className={moveColor(rut.change)}>{arrowFor(rut.change)} {signedPct(rut.change, 2)}</span> · Russell 2000</>}
            </div>
          </Tile>

          <Tile label="VIX live" w="w-[150px]" hint="Live VIX vs the settled close VIX (the signal basis). The delta is today's intraday change; signals act on the close, not this live value.">
            <div className="text-[1.15rem] leading-none font-bold" style={{ color: ui.textPrimary }}>
              {haveLiveVix ? (vixLive as number).toFixed(2) : haveCloseVix ? (vixClose as number).toFixed(2) : "—"}
            </div>
            <div className="text-[11px] mt-1" style={{ color: ui.textSecondary }}>
              {vixDelta == null
                ? (haveCloseVix ? `close ${(vixClose as number).toFixed(2)} · signal basis` : "live VIX unavailable")
                : `${vixArrow} ${vixDelta >= 0 ? "+" : ""}${vixDelta.toFixed(2)} vs close ${(vixClose as number).toFixed(2)}`}
            </div>
          </Tile>

          <Tile label="VIX term structure" w="w-[172px]" hint="Live VIX9D / VIX3M. Ratio > 1 = backwardation (near-term stress); < 1 = contango (calm).">
            <div className="text-[1.15rem] leading-none font-bold" style={{ color: ui.textPrimary }}>
              {ratio == null ? "—" : ratio.toFixed(3)}
            </div>
            <div className="text-[11px] mt-1 flex flex-wrap items-center gap-1.5" style={{ color: ui.textSecondary }}>
              {termState ? (
                <span className={chipClass(termState === "backwardation" ? "warning" : "neutral")}>{termState}</span>
              ) : null}
              {typeof term.vix9d === "number" && typeof term.vix3m === "number"
                ? `VIX9D ${term.vix9d.toFixed(2)} / VIX3M ${term.vix3m.toFixed(2)}`
                : ""}
            </div>
          </Tile>

          <Tile label="VVIX" w="w-[138px]" hint="Live CBOE VVIX (vol-of-vol) — how violently VIX itself is being repriced. Context only: in our P1 convexity-cheapness study VVIX did not survive out-of-sample as a sizing edge, so it never drives Helion signals.">
            <div className="text-[1.15rem] leading-none font-bold" style={{ color: vvixTile.level == null ? ui.textMuted : vvixTile.held ? ui.textSecondary : ui.textPrimary }}>
              {vvixTile.level == null ? "—" : vvixTile.level.toFixed(1)}
            </div>
            <div className="text-[11px] mt-1" style={{ color: ui.textSecondary }}>
              {vvixTile.level == null
                ? (marketOpen ? "no live quote" : "—")
                : vvixTile.held
                ? `held · ${fmtAge(vvixTile.ageMs)}`
                : vvixTile.change == null
                ? "vol-of-vol · context only"
                : `${arrowFor(vvixTile.change)} ${signedPct(vvixTile.change, 1)} · context only`}
            </div>
          </Tile>

          <Tile label="Realized vol (21d)" w="w-[122px]" hint="21-day realized volatility, annualized, recomputed with the live SPX (provisional until the official close is stored).">
            <div className="text-[1.15rem] leading-none font-bold" style={{ color: ui.textPrimary }}>
              {rv == null ? "—" : `${(rv * 100).toFixed(1)}%`}
            </div>
            <div className="text-[11px] mt-1" style={{ color: ui.textSecondary }}>{marketOpen ? "provisional · live" : "as of last close"}</div>
          </Tile>
        </div>
      )}
    </div>
  );
};

// Giveback stop (SHADOW) "no read" reasons, in words. The raw code stays in the tooltip and the methodology entry;
// an unknown code reads as a generic "Unavailable" rather than leaking an identifier onto the tile.
const GBS_REASON_TEXT: Record<string, string> = {
  flow_ledger_unavailable_admin_id_unset: "Flow ledger unavailable (administrator id not set)",
  budget_exceeded: "Still computing; shown on the next refresh",
  not_reported: "Not reported by the LEAPS snapshot",
  insufficient_nav_history: "Not enough NAV history yet",
  spx_unavailable: "SPX close series unavailable",
  disabled: "Switched off",
  unavailable: "Unavailable",
};

const SignalsDashboard: React.FC = () => {
  const { userRoles, loading: userLoading, userEmail } = useUser();

  const [methodologyOpen, setMethodologyOpen] = useState(false);

  // The eight sections this page is built from, in page order. Same pattern (and the
  // same component) as the Risk dashboard: pills point at the <section> wrappers
  // (signals-sec-*), so a jump lands on a whole section rather than partway into it.
  const navItems = useMemo(
    () => [
      { id: "signals-sec-stance", label: "Stance" },
      { id: "signals-sec-intraday", label: "Intraday Book" },
      { id: "signals-sec-deployment", label: "Deployment" },
      // Positioning and harvest are one section now; "Harvest" points at the panel inside it
      // rather than a section of its own, so the pill still lands where the work is done.
      { id: "signals-sec-positioning", label: "Positioning" },
      { id: "signals-harvest", label: "Harvest" },
      { id: "signals-sec-holdings", label: "Holdings" },
      { id: "signals-sec-expiry", label: "Expiration Ladder" },
      { id: "signals-sec-ml", label: "Options ML" },
    ],
    []
  );
  const [meta, setMeta] = useState<MetaNormalized | null>(() => loadCachedMeta());
  const lastGoodMetaRef = useRef<MetaNormalized | null>(loadCachedMeta());
  const [metaFetchedAtMs, setMetaFetchedAtMs] = useState<number | null>(null);
  const [nowTickMs, setNowTickMs] = useState<number>(() => Date.now());

  useEffect(() => {
    // Drives the relative "age" labels (updated Xm ago) + the streaming/stale checks. This tick
    // re-renders the page, so run it every 5s, not every 1s: the labels are minute-granular and the
    // streaming window is 15s, so 5s is imperceptible while cutting the full re-renders ~5x.
    const id = window.setInterval(() => setNowTickMs(Date.now()), 5000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    if (meta) persistCachedMeta(meta);
  }, [meta]);

  // Poll the authenticated intraday-P&L endpoint (book/account data — deliberately NOT on the
  // anonymous live tape). Best-effort: on failure we keep the last-good value, never blank the
  // tile to a misleading zero. Situational only; layered on reconciled NAV, never an official return.
  // Paced by useLivePoll: no ticking while the tab is hidden, no overlapping requests.
  useLivePoll(async () => {
    // Defer to the SignalR push when it's live: if a fresh `intradayPnl` push arrived recently,
    // skip the poll so a single warm server-side IBKR subscription serves everyone. The poll
    // resumes automatically when the socket is down or the broadcast goes quiet (graceful fallback).
    if (pnlStreamingRef.current && Date.now() - lastPnlPushMsRef.current < 45000) return;
    try {
      const d = await fetchJson(EP_INTRADAY_PNL);
      if (d && typeof d === "object") {
        // While a trade-ledger fill capture holds the IBKR account selection, the backend reads
        // nothing and returns NO totals (never one account's: the two sub-accounts are two
        // funds). Keep the last real reading on screen and label it held, rather than blanking
        // the card to "unavailable" for a pause that lasts under a minute.
        if (Array.isArray((d as any).errors) && (d as any).errors.includes("deferred_for_fill_capture")) {
          setPnlHeldSince((prev) => prev ?? Date.now());
          return;
        }
        setPnlHeldSince(null);
        setIntradayPnl(d);
        setIntradayPnlAtMs(Date.now());
      }
    } catch {
      /* keep last-good; the UI shows "live P&L unavailable" if never loaded */
    }
  }, META_REFRESH_MS);

  // Poll the LIVE book greeks (risk/long_end_vol cockpit) — same source as the Risk dashboard's
  // Long-End Vol & Vega card. Best-effort; keep last-good on failure, never blank to a misleading
  // zero. Read-only book data; situational, never feeds signals/sizing/the cap.
  //
  // Paced by useLivePoll. THIS poll was the single largest source of IBKR rate limiting in the
  // whole application — 84% of all 429s — because a tab left open overnight kept asking a closed
  // market for live quotes. See the hook's header for the measurements.
  useLivePoll(async () => {
    try {
      const d = await fetchJson(EP_LONG_END_VOL);
      if (d && typeof d === "object" && !d.error) setLiveBookGreeks(d);
    } catch {
      /* keep last-good */
    }
  }, META_REFRESH_MS);

  const [rows, setRows] = useState<LeapsRiskRow[]>([]);
  const [homeRun, setHomeRun] = useState<HomeRunHarvestPayload | null>(null);
  // Ladder targets for Capital by Expiration. Last-good is KEPT on a failed refresh (no blink); the error is shown.
  const [ladder, setLadder] = useState<LadderStatusPayload | null>(null);
  const [ladderErr, setLadderErr] = useState<string | null>(null);
  // When the harvest payload above was last actually READ. A page left open all day must not
  // keep asserting a lockout figure (or, worse, an all-clear) from a morning fetch.
  const [homeRunFetchedAtMs, setHomeRunFetchedAtMs] = useState<number | null>(null);
  const [nav, setNav] = useState<NavLatest | null>(null);
  const [liveTape, setLiveTape] = useState<any | null>(null);
  const [liveTapeFetchedAtMs, setLiveTapeFetchedAtMs] = useState<number | null>(null);
  // Intraday brokerage P&L (authenticated risk endpoint; confidential book data).
  const [intradayPnl, setIntradayPnl] = useState<any | null>(null);
  // When that payload arrived, so a HELD figure's age keeps counting while it stays on screen.
  const [intradayPnlAtMs, setIntradayPnlAtMs] = useState<number | null>(null);
  // When the last poll was deferred for a fill capture (see the poll above); null otherwise.
  const [pnlHeldSince, setPnlHeldSince] = useState<number | null>(null);
  // LIVE book greeks cockpit (from risk/long_end_vol) — shared with the Risk dashboard so the
  // Intraday Book Exposure greeks below are the SAME live numbers, not the close-confirmed snapshot.
  const [liveBookGreeks, setLiveBookGreeks] = useState<any | null>(null);

  // SignalR live-tape push. Receive-only: identical payload to GET /api/live_tape.
  // While `liveTapeStreaming` is true the 30s poll skips the tape (see the interval
  // below); when the socket is down the poll resumes automatically — graceful
  // degradation, no surface goes dark. Connect only once auth has resolved.
  const { connected: liveTapeStreaming } = useSignalR(
    "liveTape",
    (payload: any) => {
      if (payload && typeof payload === "object") {
        setLiveTape(payload);
        setLiveTapeFetchedAtMs(Date.now());
        lastLiveTapePushMsRef.current = Date.now();
      }
    },
    { enabled: !userLoading }
  );
  // Mirror into a ref so the interval's `tick` closure reads the live value.
  const liveTapeStreamingRef = useRef(false);
  useEffect(() => {
    liveTapeStreamingRef.current = liveTapeStreaming;
  }, [liveTapeStreaming]);
  // Timestamp of the last SignalR push actually received. The poll backstops a SILENT
  // broadcast (socket connected but not pushing — e.g. the broadcast timer stalls), not
  // just a disconnected socket, so a broadcast hiccup never strands the tape.
  const lastLiveTapePushMsRef = useRef(0);

  // SignalR intraday-P&L push (hub `helion`, target `intradayPnl`) — identical payload to
  // GET /api/risk/intraday_pnl. ONE warm server-side IBKR pnl subscription pushes to every
  // viewer, so no browser primes its own (subscription warm-up is paid once, server-side).
  // While streaming, the 30s poll above defers to the push; if the socket drops or the
  // broadcast goes quiet, the poll backstops it. Connect only once auth has resolved.
  const lastPnlPushMsRef = useRef(0);
  const { connected: pnlStreaming } = useSignalR(
    "intradayPnl",
    (payload: any) => {
      if (payload && typeof payload === "object") {
        setIntradayPnl(payload);
        setIntradayPnlAtMs(Date.now());
        // A real push is a fresh reading: it ends any "held for fill capture" state. Without this
        // the label could stay on for the whole session, because the poll that would clear it
        // does not run while pushes are arriving.
        setPnlHeldSince(null);
        lastPnlPushMsRef.current = Date.now();
      }
    },
    { enabled: !userLoading }
  );
  // Mirror into a ref so the poll interval's closure reads the live streaming state.
  const pnlStreamingRef = useRef(false);
  useEffect(() => {
    pnlStreamingRef.current = pnlStreaming;
  }, [pnlStreaming]);

  // ---- Intraday Book Exposure fund toggle (CIO request 2026-09-25). THIS SECTION ONLY: the rest of
  // the page stays Horizon (the fund of record) until the full fund switch (M7). Horizon keeps the
  // SignalR push + poll above (the fund of record's payload); another fund (Meridian) is polled from
  // its own ?fund= reads ONLY while it is selected. Never blended: a payload labelled for another
  // fund is refused. The choice is remembered per browser (try/catch-wrapped localStorage).
  const [intradayFund, setIntradayFundState] = useState<IntradayFundCode>(() => readStoredIntradayFund());
  const setIntradayFund = useCallback((code: IntradayFundCode) => {
    setIntradayFundState(code);
    storeIntradayFund(code);
  }, []);
  const [otherFundPnl, setOtherFundPnl] = useState<any | null>(null);
  const [otherFundNav, setOtherFundNav] = useState<any | null>(null);
  const [otherFundErr, setOtherFundErr] = useState<string | null>(null);
  useEffect(() => {
    // A different fund was chosen: drop the previous fund's figures so none can show under the new label.
    setOtherFundPnl(null);
    setOtherFundNav(null);
    setOtherFundErr(null);
  }, [intradayFund]);
  useLivePoll(
    async () => {
      const code = intradayFund;
      try {
        const d = await fetchJson(`${EP_INTRADAY_PNL}?fund=${code}`);
        if (Array.isArray((d as any)?.errors) && (d as any).errors.includes("deferred_for_fill_capture")) return;
        if (payloadBelongsTo(d, code)) {
          setOtherFundPnl(d);
          setOtherFundErr(null);
        } else {
          setOtherFundErr("the response was not this fund's");
        }
      } catch (e: any) {
        setOtherFundErr(e?.status ? `HTTP ${e.status}` : "read failed");
      }
    },
    META_REFRESH_MS,
    { enabled: intradayFund !== "HORIZON" }
  );
  // The fund's own NAV row (risk/nav_latest?fund=): its own IBKR account only. This read touches every
  // fund account at IBKR, so it runs every 5 minutes, not every 30 s; the live value comes from the P&L read.
  useLivePoll(
    async () => {
      const code = intradayFund;
      try {
        const d = await fetchJson(`${EP_NAV_LATEST_V2}?fund=${code}`);
        if (normalizeIntradayFund(d?.fund?.code) === code) setOtherFundNav(d);
      } catch {
        /* keep last-good; the P&L read carries the live value */
      }
    },
    5 * 60_000,
    { enabled: intradayFund !== "HORIZON" }
  );

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  /* Broker-sync staleness, kept OUT of `err` on purpose: refreshAll() clears `err` as its
   * first act, so anything written there immediately before a reload is erased. This is
   * cleared when a new sync STARTS, never when a reload finishes -- a successful reload of
   * stale holdings must not wipe the notice that they are stale. */
  const [syncWarn, setSyncWarn] = useState<string | null>(null);
  // M3 Step 1: the Sync success line (holdings refreshed; the official NAV is the scheduled jobs').
  const [syncNote, setSyncNote] = useState<string | null>(null);
  const [accountFilter, setAccountFilter] = useState<string>("__ALL__");
  const [symbolFilter, setSymbolFilter] = useState<string>("");
  const [groupByExpiry, setGroupByExpiry] = useState<boolean>(true);

  const [sortConfig, setSortConfig] = useState<{ key: string; direction: "asc" | "desc" }>({
    key: "Expiration",
    direction: "asc",
  });

  const handleSort = (key: string) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: "asc" };
    });
  };

  const sortIndicator = (col: string) => {
    // Faint ↕ on every sortable header so sortability is discoverable; the active column
    // brightens to ▲/▼.
    if (sortConfig.key !== col) return <span className="opacity-30"> ↕</span>;
    return <span>{sortConfig.direction === "asc" ? " ▲" : " ▼"}</span>;
  };

  const metaAbortRef = useRef<AbortController | null>(null);
  const metaInFlightRef = useRef<Promise<void> | null>(null);
  const refreshInFlightRef = useRef<Promise<void> | null>(null);

  const normalizeMeta = (raw: any): MetaNormalized => {
    const rich = raw?.leaps ?? {};
    const base = raw?.meta ?? raw ?? {};
    const m = { ...rich, ...base };

    const legacyVix = m.vixValue ?? m.vix_current ?? null;
    const legacyZ = m.vixZScore ?? m.vix_zscore ?? null;
    const legacyLive = m.vixLiveUsed ?? m.live_vix_used ?? false;
    const legacyDate = m.vixDate ?? m.vix_date ?? null;

    return {
      regime: m.regime,
      effective_regime: m.effective_regime ?? m.effectiveRegime ?? m.regime ?? null,
      phase: m.phase,
      vix_current: Number.isFinite(legacyVix) ? legacyVix : (m.vix_current ?? null),
      vix_zscore: Number.isFinite(legacyZ) ? legacyZ : (m.vix_zscore ?? null),
      vix_zscore_yesterday:
        typeof m.vix_zscore_yesterday === "number" && Number.isFinite(m.vix_zscore_yesterday)
          ? m.vix_zscore_yesterday
          : null,
      live_vix_used: !!legacyLive,
      live_vix:
        typeof m.live_vix === "number" && Number.isFinite(m.live_vix) ? m.live_vix : null,
      live_vix_zscore:
        typeof m.live_vix_zscore === "number" && Number.isFinite(m.live_vix_zscore)
          ? m.live_vix_zscore
          : null,
      vix_date: legacyDate,
      pending_harvest: !!m.pending_harvest,
      harvest_window: !!m.harvest_window,
      reversion_confirm_days: m.reversion_confirm_days ?? null,
      target_pct:
        m.target_pct_effective ??
        m.targetPctEffective ??
        m.target_pct ??
        m.target_pct_capped ??
        (m.target_leaps_allocation_pct ?? null),
      target_pct_effective: m.target_pct_effective ?? m.targetPctEffective ?? null,
      target_source: m.target_source ?? m.targetSource ?? null,
      // Passthrough; only a real boolean counts (an older backend omits it and nothing renders).
      target_approximate: typeof m.target_approximate === "boolean" ? m.target_approximate : undefined,
      target_phase_basis: typeof m.target_phase_basis === "string" ? m.target_phase_basis : null,
      rebalance_tolerance_pct:
        typeof m.rebalance_tolerance_pct === "number" && Number.isFinite(m.rebalance_tolerance_pct)
          ? m.rebalance_tolerance_pct
          : typeof m.rebalanceTolerancePct === "number" && Number.isFinite(m.rebalanceTolerancePct)
          ? m.rebalanceTolerancePct
          : null,
      vix_trim_enabled:
        typeof m.vix_trim_enabled === "boolean"
          ? m.vix_trim_enabled
          : typeof m.vixTrimEnabled === "boolean"
          ? m.vixTrimEnabled
          : undefined,
      leaps_nav: m.leaps_nav ?? (m.current_notional ?? null),
      portfolio_nav: m.portfolio_nav ?? null,
      current_weight: m.current_weight ?? (m.current_leaps_pct ?? null),
      caps: m.caps ?? null,
      defense_by_z: m.defense_by_z ?? null,
      defense_confirmed: m.defense_confirmed ?? null,
      block_reason: m.block_reason ?? null,
      cooldown_active: m.cooldown_active ?? null,
      last_transition_utc: m.last_transition_utc ?? null,
      spx: m.spx ?? null,
      term: m.term ?? null,
      early_defense:
        m.early_defense && typeof m.early_defense === "object"
          ? {
              enabled: typeof m.early_defense.enabled === "boolean" ? m.early_defense.enabled : undefined,
              active: typeof m.early_defense.active === "boolean" ? m.early_defense.active : undefined,
              target_pct: m.early_defense.target_pct ?? m.early_defense.targetPct ?? null,
              reason: m.early_defense.reason ?? null,
              conditions: m.early_defense.conditions ?? {},
            }
          : null,
      grind_axis:
        m.grind_axis && typeof m.grind_axis === "object"
          ? {
              enabled: typeof m.grind_axis.enabled === "boolean" ? m.grind_axis.enabled : undefined,
              active: typeof m.grind_axis.active === "boolean" ? m.grind_axis.active : undefined,
              applied: typeof m.grind_axis.applied === "boolean" ? m.grind_axis.applied : undefined,
              target_pct: m.grind_axis.target_pct ?? null,
              persist_window: m.grind_axis.persist_window ?? null,
              persist_days: m.grind_axis.persist_days ?? null,
              sma_days: m.grind_axis.sma_days ?? null,
              days_below_200_of_window: m.grind_axis.days_below_200_of_window ?? null,
              pct_from_sma200: m.grind_axis.pct_from_sma200 ?? null,
              only_when_zband_calm:
                typeof m.grind_axis.only_when_zband_calm === "boolean" ? m.grind_axis.only_when_zband_calm : undefined,
              base_regime_calm:
                typeof m.grind_axis.base_regime_calm === "boolean" ? m.grind_axis.base_regime_calm : undefined,
              rearm_mode: m.grind_axis.rearm_mode ?? null,
              rearm_confirm_days: m.grind_axis.rearm_confirm_days ?? null,
              rearm_buffer_pct: m.grind_axis.rearm_buffer_pct ?? null,
              rearm_reclaim_confirmed:
                typeof m.grind_axis.rearm_reclaim_confirmed === "boolean" ? m.grind_axis.rearm_reclaim_confirmed : null,
              since: m.grind_axis.since ?? null,
              reason: m.grind_axis.reason ?? null,
            }
          : null,
      // Shadow book-level stop: passthrough of the backend's own replay (no client-side recomputation).
      giveback_stop_shadow:
        m.giveback_stop_shadow && typeof m.giveback_stop_shadow === "object" ? (m.giveback_stop_shadow as MetaNormalized["giveback_stop_shadow"]) : null,
      harvest_config: m.harvest_config ?? m.harvest ?? undefined,
      // F8: passthrough only — reports WHICH path produced this read, changes nothing about it.
      engine_mode: m.engine_mode ?? null,
      rerisk_signal:
        m.rerisk_signal && typeof m.rerisk_signal === "object"
          ? {
              state: m.rerisk_signal.state ?? null,
              score:
                typeof m.rerisk_signal.score === "number" && Number.isFinite(m.rerisk_signal.score)
                  ? m.rerisk_signal.score
                  : null,
              headline: m.rerisk_signal.headline ?? null,
              reason: m.rerisk_signal.reason ?? null,
              current_weight:
                typeof m.rerisk_signal.current_weight === "number" && Number.isFinite(m.rerisk_signal.current_weight)
                  ? m.rerisk_signal.current_weight
                  : null,
              effective_target_pct:
                typeof m.rerisk_signal.effective_target_pct === "number" && Number.isFinite(m.rerisk_signal.effective_target_pct)
                  ? m.rerisk_signal.effective_target_pct
                  : null,
              suggested_target_pct:
                typeof m.rerisk_signal.suggested_target_pct === "number" && Number.isFinite(m.rerisk_signal.suggested_target_pct)
                  ? m.rerisk_signal.suggested_target_pct
                  : null,
              deploy_gap_pct:
                typeof m.rerisk_signal.deploy_gap_pct === "number" && Number.isFinite(m.rerisk_signal.deploy_gap_pct)
                  ? m.rerisk_signal.deploy_gap_pct
                  : null,
              deploy_gap_notional:
                typeof m.rerisk_signal.deploy_gap_notional === "number" && Number.isFinite(m.rerisk_signal.deploy_gap_notional)
                  ? m.rerisk_signal.deploy_gap_notional
                  : null,
              cooldown_active: typeof m.rerisk_signal.cooldown_active === "boolean" ? m.rerisk_signal.cooldown_active : undefined,
              early_defense_active: typeof m.rerisk_signal.early_defense_active === "boolean" ? m.rerisk_signal.early_defense_active : undefined,
              spx_downtrend: typeof m.rerisk_signal.spx_downtrend === "boolean" ? m.rerisk_signal.spx_downtrend : undefined,
              spx_pct_from_sma50:
                typeof m.rerisk_signal.spx_pct_from_sma50 === "number" && Number.isFinite(m.rerisk_signal.spx_pct_from_sma50)
                  ? m.rerisk_signal.spx_pct_from_sma50
                  : null,
              spx_pct_from_sma200:
                typeof m.rerisk_signal.spx_pct_from_sma200 === "number" && Number.isFinite(m.rerisk_signal.spx_pct_from_sma200)
                  ? m.rerisk_signal.spx_pct_from_sma200
                  : null,
              term_value:
                typeof m.rerisk_signal.term_value === "number" && Number.isFinite(m.rerisk_signal.term_value)
                  ? m.rerisk_signal.term_value
                  : null,
              surface_alignment:
                m.rerisk_signal.surface_alignment && typeof m.rerisk_signal.surface_alignment === "object"
                  ? {
                      status: m.rerisk_signal.surface_alignment.status ?? null,
                      tone: m.rerisk_signal.surface_alignment.tone ?? null,
                      label: m.rerisk_signal.surface_alignment.label ?? null,
                      reason: m.rerisk_signal.surface_alignment.reason ?? null,
                      convexity_environment: m.rerisk_signal.surface_alignment.convexity_environment ?? null,
                      convexity_quality_score:
                        typeof m.rerisk_signal.surface_alignment.convexity_quality_score === "number" && Number.isFinite(m.rerisk_signal.surface_alignment.convexity_quality_score)
                          ? m.rerisk_signal.surface_alignment.convexity_quality_score
                          : null,
                      surface_alignment_score:
                        typeof m.rerisk_signal.surface_alignment.surface_alignment_score === "number" && Number.isFinite(m.rerisk_signal.surface_alignment.surface_alignment_score)
                          ? m.rerisk_signal.surface_alignment.surface_alignment_score
                          : null,
                      surface_distortion_score:
                        typeof m.rerisk_signal.surface_alignment.surface_distortion_score === "number" && Number.isFinite(m.rerisk_signal.surface_alignment.surface_distortion_score)
                          ? m.rerisk_signal.surface_alignment.surface_distortion_score
                          : null,
                      deployment_stance: m.rerisk_signal.surface_alignment.deployment_stance ?? null,
                      sweet_spot_signal: m.rerisk_signal.surface_alignment.sweet_spot_signal ?? null,
                      surface_regime: m.rerisk_signal.surface_alignment.surface_regime ?? null,
                      market_best_bucket: m.rerisk_signal.surface_alignment.market_best_bucket ?? null,
                      policy_preferred_bucket: m.rerisk_signal.surface_alignment.policy_preferred_bucket ?? null,
                      portfolio_primary_bucket: m.rerisk_signal.surface_alignment.portfolio_primary_bucket ?? null,
                      mismatch_flag:
                        typeof m.rerisk_signal.surface_alignment.mismatch_flag === "boolean"
                          ? m.rerisk_signal.surface_alignment.mismatch_flag
                          : undefined,
                    }
                  : null,
            }
          : null,
      optionsMl: raw?.optionsMl ?? null,
      fresh_convexity:
        m.fresh_convexity && typeof m.fresh_convexity === "object"
          ? {
              symbol: m.fresh_convexity.symbol ?? null,
              tenor_bucket: m.fresh_convexity.tenor_bucket ?? null,
              tenor_days:
                typeof m.fresh_convexity.tenor_days === "number" && Number.isFinite(m.fresh_convexity.tenor_days)
                  ? m.fresh_convexity.tenor_days
                  : null,
              atm_iv:
                typeof m.fresh_convexity.atm_iv === "number" && Number.isFinite(m.fresh_convexity.atm_iv)
                  ? m.fresh_convexity.atm_iv
                  : null,
              spot:
                typeof m.fresh_convexity.spot === "number" && Number.isFinite(m.fresh_convexity.spot)
                  ? m.fresh_convexity.spot
                  : null,
              vpd:
                typeof m.fresh_convexity.vpd === "number" && Number.isFinite(m.fresh_convexity.vpd)
                  ? m.fresh_convexity.vpd
                  : null,
              coverage_ok:
                typeof m.fresh_convexity.coverage_ok === "boolean" ? m.fresh_convexity.coverage_ok : null,
              source: m.fresh_convexity.source ?? null,
              as_of: m.fresh_convexity.as_of ?? null,
              reason: m.fresh_convexity.reason ?? null,
              curve: Array.isArray(m.fresh_convexity.curve)
                ? m.fresh_convexity.curve.map((n: any) => ({
                    tenor_days: typeof n?.tenor_days === "number" && Number.isFinite(n.tenor_days) ? n.tenor_days : null,
                    median_dte: typeof n?.median_dte === "number" && Number.isFinite(n.median_dte) ? n.median_dte : null,
                    atm_iv: typeof n?.atm_iv === "number" && Number.isFinite(n.atm_iv) ? n.atm_iv : null,
                    vpd: typeof n?.vpd === "number" && Number.isFinite(n.vpd) ? n.vpd : null,
                    count: typeof n?.count === "number" && Number.isFinite(n.count) ? n.count : null,
                    quality: n?.quality ?? null,
                  }))
                : null,
            }
          : null,
    };
  };

  const loadTilesMeta = async (opts?: { requireReady?: boolean }) => {
    const requireReady = opts?.requireReady ?? false;

    if (metaInFlightRef.current) {
      return metaInFlightRef.current;
    }

    const runner = (async () => {
      if (metaAbortRef.current) metaAbortRef.current.abort();
      const ac = new AbortController();
      metaAbortRef.current = ac;

      const url = EP_LEAPS_SNAPSHOT;
      const shouldRetry = requireReady || !lastGoodMetaRef.current;

      for (let attempt = 0; attempt <= (shouldRetry ? META_MAX_RETRIES : 0); attempt++) {
        try {
          if (attempt > 0) await sleep(backoffMs(attempt));

          const data = await fetchJson(url, ac.signal);
          const next = normalizeMeta(data);

          setMetaFetchedAtMs(Date.now());

          const ready = isMetaReadyForTiles(next);
          if (ready) {
            lastGoodMetaRef.current = next;
            persistCachedMeta(next);
            setMeta(next);
            return;
          }

          if (!lastGoodMetaRef.current) {
            persistCachedMeta(next);
            setMeta(next);
          } else {
            setMeta(lastGoodMetaRef.current);
          }

          if (!shouldRetry) return;
        } catch (e: any) {
          if (e?.name === "AbortError") return;
          if (lastGoodMetaRef.current) setMeta(lastGoodMetaRef.current);
          if (!shouldRetry) return;
        }
      }
    })();

    metaInFlightRef.current = runner;
    try {
      await runner;
    } finally {
      metaInFlightRef.current = null;
    }
  };

  const loadRiskRows = async () => {
    try {
      const data = await fetchJson(EP_LEAPS_METRICS_V2);
      const list = Array.isArray(data) ? data : (Array.isArray(data?.rows) ? data.rows : []);
      setRows(list);
      return;
    } catch (e: any) {
      if (e?.status !== 404) {
        console.error(e);
      }
    }

    try {
      const data = await fetchJson(EP_LEAPS_RISK_LEGACY);
      const list = Array.isArray(data) ? data : (Array.isArray(data?.rows) ? data.rows : []);
      setRows(list);
      return;
    } catch (e: any) {
      console.error(e);
      setRows([]);
      setErr((prev) => prev ?? "LEAPS risk metrics unavailable (backend route missing).");
    }
  };

  const loadLatestNav = async () => {
    try {
      const data = await fetchJson(EP_NAV_LATEST_V2);
      const navObj = data?.nav ?? null;

      const total =
        typeof navObj?.TotalNAV === "number" ? navObj.TotalNAV
          : typeof data?.total_nav === "number" ? data.total_nav
          : null;

      const asOf =
        (typeof data?.asOfUTC === "string" && data.asOfUTC) ? data.asOfUTC
          : (typeof navObj?.AsOfDate === "string" && navObj.AsOfDate) ? navObj.AsOfDate
          : (typeof data?.as_of === "string" && data.as_of) ? data.as_of
          : null;

      setNav({
        total_nav: total,
        as_of: asOf,
        cash_nav: typeof navObj?.CashNAV === "number" ? navObj.CashNAV : (typeof data?.cash_nav === "number" ? data.cash_nav : null),
        source: typeof data?.source === "string" ? data.source : null,
        cashOverride: data?.cashOverride ?? null,
        cash_override: data?.cash_override ?? null,
        // Carry the reconciliation block (bank cash + accruals) so the Total NAV tile
        // can show the authoritative fund NAV, matching the Risk cockpit.
        reconciliation: (data as any)?.navReconciliation ?? null,
      });
      return;
    } catch (e: any) {
      if (e?.status !== 404) console.error(e);
    }

    try {
      const data = await fetchJson(EP_NAV_LATEST_LEGACY);
      setNav({
        total_nav: typeof data?.total_nav === "number" ? data.total_nav : null,
        as_of: data?.as_of ?? null,
        cash_nav: typeof data?.cash_nav === "number" ? data.cash_nav : null,
        source: typeof data?.source === "string" ? data.source : null,
        cashOverride: data?.cashOverride ?? null,
        cash_override: data?.cash_override ?? null,
      });
      return;
    } catch (e: any) {
      console.error(e);
      setNav(null);
    }
  };

  const loadHomeRunHarvest = async () => {
    try {
      const data = await fetchJson(EP_HOME_RUN_HARVEST);
      setHomeRun(data && typeof data === "object" ? (data as HomeRunHarvestPayload) : null);
      setHomeRunFetchedAtMs(Date.now());
    } catch (e: any) {
      if (e?.status !== 404) console.error(e);
      setHomeRun(null);
      // Clear the stamp too — otherwise a failed refresh would leave the previous read's
      // timestamp behind and a null payload would look "fresh".
      setHomeRunFetchedAtMs(null);
    }
  };

  // Ladder targets (display only). Best effort: never sets the page error; keeps the last good read. NOT awaited by
  // refreshAll and aborted after 15 s, so a slow feed can never hold the page's Refresh / Loading state (review
  // 2026-10-29, finding 1).
  const loadLadderStatus = async () => {
    const ctl = new AbortController();
    const timer = setTimeout(() => ctl.abort(), 15_000);
    try {
      const data = await fetchJson(EP_LADDER_STATUS, ctl.signal);
      setLadder(data && typeof data === "object" ? (data as LadderStatusPayload) : null);
      setLadderErr(null);
    } catch (e: any) {
      setLadderErr(e?.name === "AbortError" ? "timed out" : e?.status ? `HTTP ${e.status}` : "network error");
    } finally {
      clearTimeout(timer);
    }
  };

  // Live market tape (best-effort intraday). On failure we KEEP the last-good
  // value (do not clear) so the strip degrades gracefully rather than blanking.
  const loadLiveTape = async () => {
    try {
      const data = await fetchJson(EP_LIVE_TAPE);
      if (data && typeof data === "object") {
        setLiveTape(data);
        setLiveTapeFetchedAtMs(Date.now());
      }
    } catch (e: any) {
      if (e?.status !== 404) console.error(e);
    }
  };

  const forceSyncAndReload = async () => {
    setLoading(true);
    setErr(null);

    try {
      // "Sync IBKR + Refresh" runs the heavy server-side IBKR → SQL sync (positions and the
      // greeks snapshot that feeds the risk grid), THEN reloads every surface from the
      // canonical endpoints — the exact same data path the standalone "Refresh" button uses.
      // It does NOT write the official daily NAV (M3 Step 1, 2026-09-25): the scheduled jobs
      // do (06:15 refresh, 06:45 / 13:45 MT preflight) and the 14:05 MT close mark is final.
      //
      // We deliberately do NOT paint the grid from refresh_portfolio's inline `risk.rows`.
      // That display view can lag the freshly-written greeks snapshot and come back as an
      // EMPTY array right after a sync (and it uses a different column shape than the grid).
      // The previous code treated that empty array as "rows updated" and then SKIPPED the
      // canonical reload — which is why the tables stayed blank until a second "Refresh"
      // click. Always reloading canonically finishes the whole loop in a single click.
      let syncOk = false;
      setSyncWarn(null); // a new attempt supersedes the last verdict
      setSyncNote(null);
      try {
        const res = await fetch(EP_REFRESH_PORTFOLIO, {
          method: "POST",
          headers: { Accept: "application/json" },
        });
        // HTTP 200 IS NOT SUCCESS HERE. refresh_portfolio degrades to last-known data and
        // still answers 200 when the IBKR sync fails, so `res.ok` alone showed a clean
        // tick over stale holdings — the same class of silent success as the
        // sync_ibkr_positions 404 documented below. Keying off the explicit syncOk /
        // sync.error the backend returns is the guard PortfolioView has carried since the
        // 2026-07-01 audit (P1 #24); it was never ported here. (CIO decision Q5,
        // 2026-08-27.)
        let body: RefreshPortfolioResponse | null = null;
        try {
          body = await res.clone().json();
        } catch {
          /* non-JSON body — fall back to the status code alone */
        }
        const reportedFailure = body?.syncOk === false || Boolean(body?.sync?.error);
        syncOk = res.ok && !reportedFailure;
        if (res.ok && reportedFailure) {
          const detail =
            body?.sync?.error ||
            (Array.isArray(body?.errors) ? (body?.errors ?? []).join("; ") : "IBKR sync failed");
          setSyncWarn(
            `IBKR sync failed — the holdings below are last-known, not fresh. ${detail}`
          );
        } else if (syncOk) {
          const synced = typeof body?.synced === "number" ? body.synced : null;
          setSyncNote(syncSuccessMessage(synced));
          // Amber, never a failure: syncOk alone decides success.
          setSyncWarn(navUnreadNote(body));
        }
      } catch {
        // Network/route failure — fall through to the lighter positions-only sync below.
      }

      if (!syncOk) {
        // Fall back to the lighter positions-only sync. The route is `refresh-positions`
        // (refresh_positions_http); this previously posted to `sync_ibkr_positions`, which
        // IS NOT A ROUTE -- the 404 was swallowed by an empty catch and refreshAll() then
        // re-rendered the SAME STALE HOLDINGS. You clicked Sync, saw no error, and
        // concluded the book was current. A silent success is worse than a loud failure.
        // (2026-07-30 audit Tier-0 #13.)
        try {
          const fb = await fetch(`${API_BASE_URL}/refresh-positions`, { method: "POST" });
          syncOk = fb.ok;
          if (!fb.ok) {
            setSyncWarn(
              `Positions sync failed (${fb.status}). The holdings below may be STALE — they were not refreshed from IBKR.`
            );
          }
        } catch (fbErr: any) {
          setSyncWarn(
            `Positions sync unreachable (${fbErr?.message ?? "network error"}). The holdings below may be STALE.`
          );
        }
      }

      // Finish the loop from the canonical endpoints (tiles/meta, /risk/leaps_metrics rows,
      // NAV, home-run harvest, live tape) so the tables fill in a single click.
      await refreshAll();
    } catch (e: any) {
      console.error(e);
      setErr(e?.message ?? "Unknown error");
    } finally {
      setLoading(false);
    }
  };

  // Export the LEAPS holdings/options grid exactly as shown (current account/symbol filters and
  // sort order) to a CSV the CIO downloads locally — for records / audit. Emits the RAW underlying
  // values (better for analysis than the formatted display strings); no trade affordance, and the
  // data never leaves the browser.
  const handleExportCsv = () => {
    try {
      const cols: Array<[string, (r: any) => any]> = [
        ["Account", (r) => r.base.AccountId ?? ""],
        ["Symbol", (r) => r.base.Symbol ?? ""],
        ["Expiration", (r) => r.base.Expiration ?? ""],
        ["DTE", (r) => computeDte(r.base.Expiration) ?? ""],
        ["Age (days)", (r) => r.base.AgeDays ?? ""],
        ["Right", (r) => r.base.Right ?? ""],
        ["Strike", (r) => r.base.Strike ?? ""],
        ["Quantity", (r) => r.base.Quantity ?? ""],
        ["Underlying", (r) => r.base.UnderlyingPrice ?? ""],
        ["Last", (r) => r.base.LastPrice ?? ""],
        ["Delta", (r) => r.base.Delta ?? ""],
        ["Gamma", (r) => r.base.Gamma ?? ""],
        ["Gamma (delta per 1% index)", (r) => gammaPer1Pct(r.base.Gamma, r.base.UnderlyingPrice) ?? ""],
        ["Theta", (r) => r.base.Theta ?? ""],
        ["Vega", (r) => r.base.Vega ?? ""],
        ["IV", (r) => r.base.ImpliedVol ?? ""],
        ["VPD", (r) => (r as any).vpd ?? ""],
        ["Efficiency", (r) => (r as any).efficiency ?? ""],
        ["Unrealized P&L", (r) => r.base.UnrealizedPnL ?? ""],
        ["P&L %", (r) => r.base.UnrealizedPnLPercent ?? ""],
      ];
      const esc = (v: any) => {
        const s = v == null ? "" : String(v);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };
      const header = cols.map((c) => esc(c[0])).join(",");
      const body = sortedRowsWithVpd.map((r) => cols.map((c) => esc(c[1](r))).join(","));
      const csv = [header, ...body].join("\r\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `helion_leaps_holdings_${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("CSV export failed", e);
    }
  };

  const refreshAll = async () => {
    if (refreshInFlightRef.current) {
      return refreshInFlightRef.current;
    }

    const runner = (async () => {
      setLoading(true);
      setErr(null);
      void loadLadderStatus();   // fire-and-forget: never part of the awaited refresh
      try {
        await Promise.all([
          loadTilesMeta({ requireReady: true }),
          loadRiskRows(),
          loadLatestNav(),
          loadHomeRunHarvest(),
          loadLiveTape(),
        ]);
      } catch (e: any) {
        console.error(e);
        setErr(e?.message ?? "Unknown error");
      } finally {
        setLoading(false);
      }
    })();

    refreshInFlightRef.current = runner;
    try {
      await runner;
    } finally {
      refreshInFlightRef.current = null;
    }
  };

  useEffect(() => {
    if (!userLoading) refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userLoading]);

  // Main tile/tape refresh. Paced by useLivePoll so a hidden tab stops asking: this loop drives
  // get_leaps_snapshot and live_tape, which together accounted for ~1,600 overnight IBKR-backed
  // calls a night before the gate. `immediate: false` because the mount effect above already
  // performs the first load — the hook must not add a duplicate fetch on every page open.
  useLivePoll(
    async () => {
      await loadTilesMeta({ requireReady: false });
      // Poll the tape unless a SignalR push arrived recently. This backstops a SILENT or
      // dead broadcast (socket connected but not pushing): if no push in >20s, the poll
      // keeps the tape fresh, so a broadcast hiccup never strands the page on "feed delayed".
      if (Date.now() - lastLiveTapePushMsRef.current > 20000) loadLiveTape();
    },
    META_REFRESH_MS,
    { enabled: !userLoading, immediate: false },
  );

  // Re-read the harvest payload while the page is open. It used to load only on page open and
  // on Refresh, and the lockout derivation (correctly) refuses a read older than
  // HARVEST_LOCKOUT_MAX_AGE_MS (30 min) — so any tab left open past half an hour flipped to
  // "Harvest data unavailable" / "lockout unknown" while the panel below still showed the
  // old payload. /home_run_harvest reads SQL only (no IBKR call), so this adds nothing to the
  // IBKR rate-limit budget. A failed re-read keeps the last good payload AND its timestamp,
  // so persistent failure still ages honestly into "unknown" rather than looking fresh.
  useLivePoll(
    async () => {
      try {
        const data = await fetchJson(EP_HOME_RUN_HARVEST);
        if (data && typeof data === "object") {
          setHomeRun(data as HomeRunHarvestPayload);
          setHomeRunFetchedAtMs(Date.now());
        }
      } catch {
        /* keep last-good; the staleness guard handles a persistent outage */
      }
    },
    HARVEST_REFRESH_MS,
    { enabled: !userLoading, immediate: false },
  );

  useEffect(
    () => () => {
      if (metaAbortRef.current) metaAbortRef.current.abort();
    },
    [],
  );

  const displayMeta = useMemo(() => meta ?? lastGoodMetaRef.current ?? loadCachedMeta() ?? null, [meta]);
  const tileMeta = displayMeta;

  const pressure = useMemo(() => computeRegimePressure(displayMeta), [displayMeta]);
  const allocAction = useMemo(() => computeAllocationAction(displayMeta), [displayMeta]);

  const accounts = useMemo(() => {
    const uniq = new Set<string>();
    rows.forEach(r => {
      const id = (r.AccountId || "").trim();
      if (id) uniq.add(id);
    });
    return ["__ALL__", ...Array.from(uniq)];
  }, [rows]);

  const filteredRows = useMemo(() => {
    let r = rows.slice();
    if (accountFilter !== "__ALL__") r = r.filter(x => (x.AccountId || "").trim() === accountFilter);
    if (symbolFilter.trim()) {
      const needle = symbolFilter.trim().toUpperCase();
      r = r.filter(x => x.Symbol?.toUpperCase().includes(needle));
    }

    r.sort((a, b) => {
      const d1 = computeDte(a.Expiration);
      const d2 = computeDte(b.Expiration);
      if (d1 == null && d2 == null) return 0;
      if (d1 == null) return 1;
      if (d2 == null) return -1;
      return d1 - d2;
    });

    return r;
  }, [rows, accountFilter, symbolFilter]);

  const navUsed = useMemo(() => {
    return nav?.total_nav ?? displayMeta?.portfolio_nav ?? null;
  }, [nav, displayMeta]);

  const cashOverrideState = useMemo(() => {
    const raw = nav?.cashOverride ?? nav?.cash_override ?? null;
    const observed = typeof raw?.observed_cash === "number" ? raw.observed_cash : (nav?.cash_nav ?? 0);
    const reserved = typeof raw?.reserved_cash === "number" ? raw.reserved_cash : observed;
    const enabled = !!(
      raw && (
        raw.enabled === true ||
        raw.mode === "reserved" ||
        raw.mode === "constrained" ||
        (typeof reserved === "number" && reserved > 0)
      )
    );
    const deployable = typeof raw?.deployable_cash === "number" ? raw.deployable_cash : Math.max(0, observed - reserved);
    const mode = typeof raw?.mode === "string" ? raw.mode : (enabled ? "reserved" : "normal");
    return { enabled, observed, reserved, deployable, mode };
  }, [nav]);

  const observedCash = cashOverrideState.observed;
  const reservedCash = cashOverrideState.reserved;
  // The Cash & deployability tile's dollar figure, and its share of NAV computed from that
  // SAME figure (the retired Deployment Status tile was 1 − this, off reservedCash alone).
  const cashShown = reservedCash || observedCash || 0;
  const cashPctNav =
    navUsed != null && Number.isFinite(navUsed) && navUsed > 0 ? cashShown / navUsed : null;

  // `deploymentLabel` is defined below, next to the harvest-lockout derivation it now
  // depends on — the label used to say "Fully deployable" while the harvest card on the
  // same page said capital was withheld.

  // CIO POLICY, 2026-08-13: the 40% funded/cost-basis figure is a PURCHASE constraint --
  // we do not buy beyond it -- not a holding cap. Market value may grow without limit;
  // there is no cap on options performance. The only things that force a rebalance are a
  // REGIME CHANGE or the GRIND AXIS.
  // Consequence for this number: it is a fund-level deployment measure, so it must be
  // computed over ALL rows. It previously reduced over `filteredRows`, giving a filtered
  // numerator over a whole-fund NAV denominator -- so a UI account or symbol filter
  // silently moved a policy figure. Fixed to `rows`.
  // A row missing BOTH cost fields must NOT contribute 0: that silently understates the funded
  // basis, and `cost / nav` then returns a finite 0.0 that sails past a null-check and renders
  // "Funded 0.0%" — read as "none of the cap used, full purchase headroom", the maximally
  // permissive direction on a card that gates buying. The backend treats exactly this state as an
  // alarm (rebalance_logic.py:902-906). Count the unpriced rows so the weight can refuse instead.
  // Mirrors RiskDashboard's fallback, which already skips such rows and returns null on cost <= 0.
  const fundedLeapsCost = useMemo(() => {
    let unpriced = 0;
    const cost = rows.reduce((acc, r) => {
      const entryRaw = (r as any).AvgPrice ?? (r as any).EntryPrice;
      const entry = entryRaw == null ? NaN : num(entryRaw, NaN);
      const qty = num(r.Quantity, NaN);
      const mult = num(r.Multiplier, 100);
      if (!Number.isFinite(entry) || !Number.isFinite(qty)) {
        unpriced += 1;
        return acc;
      }
      return acc + entry * qty * (Number.isFinite(mult) && mult !== 0 ? mult : 100);
    }, 0);
    return { cost, unpriced };
  }, [rows]);

  const fundedLeapsWeight = useMemo(() => {
    // Prefer the engine's own funded_weight when the payload carries it, so this page and the
    // Risk page cannot quote different funded percentages against the same cap.
    const engineFunded = (displayMeta as any)?.rebalance?.funded_weight;
    if (typeof engineFunded === "number" && Number.isFinite(engineFunded)) return engineFunded;
    if (navUsed == null || !Number.isFinite(navUsed) || navUsed <= 0) return null;
    // Cannot assess the funded basis if any rung lacks cost data, or if the total came out <= 0.
    if (fundedLeapsCost.unpriced > 0 || !(fundedLeapsCost.cost > 0)) return null;
    return fundedLeapsCost.cost / navUsed;
  }, [fundedLeapsCost, navUsed, displayMeta]);

  // The DOLLARS behind fundedLeapsWeight, from the same source so the two tie out (CIO 2026-10-29): the
  // engine's funded_cost when the % is the engine's funded_weight, else the client cost the fallback
  // divided. Null when nothing ties out -- the engine's % is never shown beside the client's dollars.
  const fundedLeapsCostShown = useMemo(() => {
    if (fundedLeapsWeight == null) return null;
    const rb = (displayMeta as any)?.rebalance;
    if (typeof rb?.funded_weight === "number" && Number.isFinite(rb.funded_weight)) {
      const c = rb?.funded_cost;
      return typeof c === "number" && Number.isFinite(c) && c > 0 ? c : null;
    }
    return fundedLeapsCost.cost > 0 ? fundedLeapsCost.cost : null;
  }, [fundedLeapsWeight, fundedLeapsCost, displayMeta]);

  const fundedCap = useMemo(() => {
    const raw = displayMeta?.caps?.funded_max;
    return typeof raw === "number" && Number.isFinite(raw) ? raw : 0.40;
  }, [displayMeta]);

  const fundedCapBreached = fundedLeapsWeight != null ? fundedLeapsWeight > fundedCap + 0.005 : false;

  // Harvest lockout, from the shared derivation. This page fetches /home_run_harvest, whose
  // payload carries `policy` (enabled / apply_to_buy_budget) and the RAW `summary
  // .lockedNotional`; it does NOT carry the rebalance engine's own figures, because Signals
  // calls get_leaps_snapshot WITHOUT includeEngine=1 (engine_mode "light"), so no
  // `home_run_redeployment` / `buy_block_reason` reaches this page. No engine argument is
  // passed for that reason — the module then re-applies the backend's own policy gate.
  const harvestLockout = useMemo(
    () => deriveHarvestLockout({ harvest: homeRun, fetchedAtMs: homeRunFetchedAtMs, nowMs: nowTickMs }),
    [homeRun, homeRunFetchedAtMs, nowTickMs]
  );

  /* Deployment Status headline. It read "Fully deployable" regardless of the lockout, on the
     same page whose harvest card was simultaneously reporting locked notional — the tile a
     CIO scans said the opposite of the card he would have to open. Both now come from the
     one derivation, so they cannot diverge again. */
  const deploymentLabel = deployabilityHeadline(harvestLockout, {
    reserved: cashOverrideState.enabled,
    reservedLabel: "Constrained by discretionary cash",
    freeLabel: "Fully deployable",
  });

  const effectiveLeapsTargetPct = useMemo(() => {
    const raw = displayMeta?.target_pct_effective;
    if (typeof raw === "number" && Number.isFinite(raw)) return raw;
    const fallback = displayMeta?.target_pct;
    return typeof fallback === "number" && Number.isFinite(fallback) ? fallback : null;
  }, [displayMeta]);

  // CIO POLICY 2026-08-13: a funded-basis breach is NOT a de-risk trigger. It means we stop
  // buying, not that we sell. The `if (fundedCapBreached) return true;` line removed here made
  // the deployment cap sufficient on its own to call a trim executable. De-risk is required
  // only when the ENGINE's target has dropped below the cap -- i.e. driven by regime or grind,
  // which are the only rebalance triggers in the policy.
  //
  // P2-5 (2026-11-03): the trim verdict now mirrors the ENGINE's rule (lib/signalsTrimState):
  // grind / early defense, or a CONFIRMED defense in any band -- including the Sweet-Spot level
  // trim at a 40% target, which this page used to call "appreciation drift". A band target drop
  // whose sell gate has not confirmed is over target but NOT a trim; the engine stages nothing.
  const signalsTrim = useMemo(
    () =>
      resolveSignalsTrim({
        actionState: allocAction.state,
        targetPct: effectiveLeapsTargetPct,
        fundedCap,
        targetSource: (displayMeta as any)?.target_source ?? null,
        defenseConfirmed: displayMeta?.defense_confirmed,
      }),
    [allocAction.state, effectiveLeapsTargetPct, fundedCap, displayMeta]
  );
  const levelTrimByDefense = signalsTrim.kind === "defense";
  const trimGateNotConfirmed = signalsTrim.gateNotConfirmed;

  const executableTrimSignal = allocAction.state === "TRIM" && signalsTrim.executable;

  const shouldFlipThetaSign = useMemo(() => {
    const thetas = filteredRows
      .map(r => (typeof r.Theta === "number" && Number.isFinite(r.Theta) ? r.Theta : null))
      .filter((x): x is number => x != null && x !== 0);

    if (thetas.length < 3) return false;

    const pos = thetas.filter(t => t > 0).length;
    const neg = thetas.filter(t => t < 0).length;

    return pos > neg;
  }, [filteredRows]);

  const thetaNorm = (t?: number | null) => {
    if (t == null || !Number.isFinite(t)) return null;
    return shouldFlipThetaSign ? -t : t;
  };

  // Book Sensitivity is a FUND-level figure over a whole-fund NAV, so it sums over ALL rows —
  // the same fix fundedLeapsCost got on 2026-08-13. It used to reduce over `filteredRows`, so
  // the holdings table's Account / Symbol filter silently moved these percentages (and the
  // separate Vega/NAV tile, now a line in this one) while the denominator stayed the whole fund.
  const convexity = useMemo(() => {
    const n = navUsed;
    const spx = typeof displayMeta?.spx?.spx_close === "number" && Number.isFinite(displayMeta?.spx?.spx_close)
      ? displayMeta.spx!.spx_close!
      : null;

    const vega$ = rows.reduce((acc, r) => {
      const v = num(r.Vega, 0);
      const qty = num(r.Quantity, 0);
      const mult = num(r.Multiplier, 100);
      return acc + v * qty * mult;
    }, 0);

    const theta$PerDay = rows.reduce((acc, r) => {
      const tRaw = num(r.Theta, 0);
      const t = shouldFlipThetaSign ? -tRaw : tRaw;
      const qty = num(r.Quantity, 0);
      const mult = num(r.Multiplier, 100);
      return acc + t * qty * mult;
    }, 0);

    const delta$Per1pct = (spx != null)
      ? rows.reduce((acc, r) => {
          const d = num(r.Delta, 0);
          const qty = num(r.Quantity, 0);
          const mult = num(r.Multiplier, 100);
          return acc + d * qty * mult * (spx * 0.01);
        }, 0)
      : null;

    const vegaPctNav = (n && n > 0) ? (vega$ / n) * 100 : null;
    // Per +1 vol point, in bps of NAV — the old standalone Vega/NAV tile's figure.
    const vegaBps = (n && n > 0) ? (vega$ / n) * 10000 : null;
    const thetaBpsPerDay = (n && n > 0) ? (theta$PerDay / n) * 10000 : null;
    const deltaPctNavPer1pct = (n && n > 0 && delta$Per1pct != null) ? (delta$Per1pct / n) * 100 : null;
    const dToV = (vega$ !== 0) ? (delta$Per1pct != null ? (delta$Per1pct / vega$) : null) : null;

    return {
      spx,
      vega$,
      vegaPctNav,
      vegaBps,
      theta$PerDay,
      thetaBpsPerDay,
      delta$Per1pct,
      deltaPctNavPer1pct,
      deltaToVega: dToV,
    };
  }, [rows, navUsed, displayMeta, shouldFlipThetaSign]);

  const { rowsWithVpd, freshVpd, medianEfficiency, benchmark } = useMemo(() => {
    // Per-row clean VPD (vega per premium dollar) with a stale/crossed/zero-quote +
    // zero-vega guard. A bad tick (bid=ask=0, crossed, non-positive price) or a
    // missing/zero vega must NOT produce a phantom efficiency, so those rows carry
    // vpd=null and are excluded from both the benchmark and the screen.
    const cleanPrice = (r: LeapsRiskRow): number | null => {
      const bid = num(r.Bid, NaN);
      const ask = num(r.Ask, NaN);
      if (Number.isFinite(bid) && Number.isFinite(ask)) {
        if (bid <= 0 && ask <= 0) return null;      // empty two-sided quote
        if (bid > 0 && ask > 0 && bid > ask) return null; // crossed
      }
      const mid = num(r.Mid, NaN);
      const px = (Number.isFinite(mid) && mid > 0) ? mid : num(r.LastPrice, NaN);
      return (Number.isFinite(px) && px > 0) ? px : null;
    };

    const rowsCalc = filteredRows.map(r => {
      const price = cleanPrice(r);
      const vegaRaw = num(r.Vega, NaN);
      const vega = (Number.isFinite(vegaRaw) && vegaRaw > 0) ? vegaRaw : null;
      const vpd = (price != null && vega != null) ? (vega / price) : null;
      const dte = computeDte(r.Expiration);
      const symbol: string = String(r.Symbol ?? "").toUpperCase();
      return { base: r, price, vpd, dte, symbol };
    });

    // Market-anchored benchmark from the backend ORATS surface. PER-TENOR: a curve
    // of fresh ATM VPD at each surface-supported maturity, so each held position is
    // measured against a fresh contract near its OWN tenor (a 5yr rung vs fresh 5yr
    // convexity, not a 2yr yardstick). fc.vpd is the interpolated 24-30m headline,
    // used for the tile and as a fallback. Null/empty when the surface can't support
    // it (see reason) -> we fall back to an in-book benchmark, labeled.
    const fc = displayMeta?.fresh_convexity ?? null;
    const marketSym = (fc?.symbol || "SPX").toUpperCase();
    const marketVpd =
      fc && typeof fc.vpd === "number" && Number.isFinite(fc.vpd) && fc.vpd > 0 ? fc.vpd : null;

    // Usable curve nodes: fresh VPD vs the cluster's actual median DTE, sorted.
    // `quality` is carried through deliberately: the backend labels thin nodes (a
    // handful of quotes at the deep end) and without it a benchmark built on 3 quotes
    // is indistinguishable from one built on 400.
    const curveNodes = (fc?.curve ?? [])
      .filter(n => n != null
        && typeof n.vpd === "number" && Number.isFinite(n.vpd) && n.vpd > 0
        && typeof n.median_dte === "number" && Number.isFinite(n.median_dte) && n.median_dte > 0)
      .map(n => ({
        dte: n.median_dte as number,
        vpd: n.vpd as number,
        quality: (n as any).quality as string | undefined,
        count: (n as any).count as number | undefined,
      }))
      .sort((a, b) => a.dte - b.dte);

    // Anchor a position to its own tenor: linear-interpolate the curve in DTE,
    // clamped (flat) beyond the ends. Falls back to the headline point if no curve.
    // Mirrors the backend interp_curve() so live and modeled agree.
    const marketBenchAt = (dte: number | null): number | null => {
      if (curveNodes.length === 0) return marketVpd;
      if (dte == null || !Number.isFinite(dte)) return marketVpd;
      if (dte <= curveNodes[0].dte) return curveNodes[0].vpd;
      if (dte >= curveNodes[curveNodes.length - 1].dte) return curveNodes[curveNodes.length - 1].vpd;
      for (let i = 0; i < curveNodes.length - 1; i++) {
        const a = curveNodes[i], b = curveNodes[i + 1];
        if (dte >= a.dte && dte <= b.dte) {
          const t = b.dte > a.dte ? (dte - a.dte) / (b.dte - a.dte) : 0;
          return a.vpd + t * (b.vpd - a.vpd);
        }
      }
      return marketVpd;
    };

    // HOW the benchmark for a given DTE was obtained. Interpolated is a measurement;
    // clamped is an EXTRAPOLATION -- beyond the deepest covered node the curve is held
    // FLAT, so a 5y position is silently scored against the deepest node we actually
    // have (often ~3y). That is a guess wearing the same styling as a measurement, and
    // it is exactly the long end where ORATS coverage thins to a few quotes.
    // (2026-07-31 tenor investigation.)
    type BenchMode = "interpolated" | "clamped_long" | "clamped_short" | "headline" | "none";
    const marketBenchModeAt = (dte: number | null): BenchMode => {
      if (curveNodes.length === 0) return marketVpd == null ? "none" : "headline";
      if (dte == null || !Number.isFinite(dte)) return marketVpd == null ? "none" : "headline";
      if (dte <= curveNodes[0].dte) return "clamped_short";
      if (dte >= curveNodes[curveNodes.length - 1].dte) return "clamped_long";
      return "interpolated";
    };

    // The deepest tenor the curve actually covers -- anything past this is extrapolated.
    const deepestNodeDte = curveNodes.length ? curveNodes[curveNodes.length - 1].dte : null;
    const thinNodeCount = curveNodes.filter(
      n => n.quality != null && n.quality !== "ok" && n.quality !== "good"
    ).length;
    const marketAvailable = curveNodes.length > 0 || marketVpd != null;
    const perTenor = curveNodes.length > 0;

    // In-book per-symbol fallback: median VPD of a symbol's longest-DTE quartile
    // (the freshest convexity actually held in that name). Min-sample guarded so a
    // 1-2 contract benchmark is never trusted. Used when the market anchor is
    // unavailable, or for any non-market underlying (SPX/NDX never cross-mix).
    const MIN_BENCH_SAMPLES = 3;
    const inBookBenchFor = (sym: string): number | null => {
      const withVpd = rowsCalc.filter(x => x.symbol === sym && x.vpd != null && x.dte != null) as
        { vpd: number; dte: number }[];
      if (withVpd.length < MIN_BENCH_SAMPLES) return null;
      const sortedByDte = [...withVpd].sort((a, b) => b.dte - a.dte);
      const q = Math.max(1, Math.floor(sortedByDte.length * 0.25));
      const vpdList = sortedByDte.slice(0, q).map(x => x.vpd).sort((a, b) => a - b);
      if (vpdList.length === 0) return null;
      return vpdList.length % 2
        ? vpdList[(vpdList.length - 1) / 2]
        : (vpdList[vpdList.length / 2 - 1] + vpdList[vpdList.length / 2]) / 2;
    };
    const inBookCache: Record<string, number | null> = {};
    const inBookFor = (sym: string): number | null => {
      if (!(sym in inBookCache)) inBookCache[sym] = inBookBenchFor(sym);
      return inBookCache[sym];
    };

    const symbolSet = new Set<string>();
    for (const x of rowsCalc) {
      if (x.symbol) symbolSet.add(x.symbol);
    }
    const symbols: string[] = Array.from(symbolSet);

    // Per-row efficiency = clean VPD ÷ the benchmark at that position's tenor.
    const withEff = rowsCalc.map(x => {
      let bench: number | null = null;
      let source: "market" | "in_book" | null = null;
      if (x.symbol === marketSym && marketAvailable) {
        bench = marketBenchAt(x.dte);
        if (bench != null) source = "market";
      }
      if (bench == null) {
        bench = inBookFor(x.symbol);
        if (bench != null) source = "in_book";
      }
      const efficiency = (x.vpd != null && bench != null && bench > 0) ? (x.vpd / bench) : null;
      // benchMode lets the UI distinguish a measured benchmark from an extrapolated one.
      const benchMode = source === "market" ? marketBenchModeAt(x.dte) : "headline";
      return { ...x, efficiency, benchmarkSource: source, benchAtDte: bench, benchMode };
    });

    const effVals = withEff.map(x => x.efficiency).filter((v): v is number => v != null && Number.isFinite(v));
    const medEff = effVals.length
      ? [...effVals].sort((a, b) => a - b)[Math.floor((effVals.length - 1) / 2)]
      : null;

    // Representative benchmark for the tile: prefer the market headline, else the
    // in-book benchmark of the dominant (most-held) underlying.
    let repVpd: number | null = marketVpd;
    let repSource: "market" | "in_book" | null = marketVpd != null ? "market" : null;
    if (repVpd == null) {
      const dominant = symbols
        .map(s => ({ n: rowsCalc.filter(x => x.symbol === s).length, v: inBookFor(s) }))
        .filter(o => o.v != null)
        .sort((a, b) => b.n - a.n)[0];
      if (dominant) { repVpd = dominant.v; repSource = "in_book"; }
    }
    const anyInBook = withEff.some(x => x.benchmarkSource === "in_book");
    const overallSource: "market" | "in_book" | "mixed" | null =
      repSource == null ? null : repSource === "market" && anyInBook ? "mixed" : repSource;

    // Freshness: flag when the surface benchmark lags the freshest held snapshot by
    // more than ~4 calendar days (covers weekends/holidays) so a stale anchor can't
    // masquerade as live.
    const rowAsOf = withEff
      .map(x => (x.base.SnapshotAtUTC ? Date.parse(x.base.SnapshotAtUTC) : NaN))
      .filter(t => Number.isFinite(t));
    const newestRow = rowAsOf.length ? Math.max(...rowAsOf) : NaN;
    const benchAsOfMs = fc?.as_of ? Date.parse(fc.as_of) : NaN;
    const benchStale =
      marketVpd != null && Number.isFinite(newestRow) && Number.isFinite(benchAsOfMs)
        ? newestRow - benchAsOfMs > 4 * 24 * 3600 * 1000
        : false;

    return {
      rowsWithVpd: withEff,
      freshVpd: repVpd,
      medianEfficiency: medEff,
      benchmark: {
        source: overallSource,
        vpd: repVpd,
        asOf: fc?.as_of ?? null,
        reason: marketVpd == null ? (fc?.reason ?? "market benchmark unavailable") : null,
        coverageOk: marketVpd != null,
        stale: benchStale,
        atmIv: fc?.atm_iv ?? null,
        tenorDays: fc?.tenor_days ?? null,
        perTenor,
        nodeCount: curveNodes.length,
        // Rows whose benchmark was EXTRAPOLATED (curve clamped flat) rather than
        // interpolated between two real nodes -- i.e. positions longer-dated than
        // anything the surface actually covers.
        extrapolatedCount: withEff.filter(
          x => x.benchmarkSource === "market" && x.benchMode === "clamped_long"
        ).length,
        deepestNodeDte,
        thinNodeCount,
      },
    };
  }, [filteredRows, displayMeta]);

  const sortedRowsWithVpd = useMemo(() => {
    const arr = [...rowsWithVpd];
    const { key, direction } = sortConfig;

    const pick = (row: any) => {
      const b = row.base as LeapsRiskRow;
      switch (key) {
        case "Account": return (b.AccountId || "").toUpperCase();
        case "Symbol": return (b.Symbol || "").toUpperCase();
        case "Expiration": return (b.Expiration || "");
        case "DTE": return computeDte(b.Expiration) ?? 99999;
        case "Age (d)": return num(b.AgeDays, -Infinity);
        case "Right": return (b.Right || "");
        case "Strike": return num(b.Strike, 0);
        case "Quantity": return num(b.Quantity, 0);
        case "UnderlyingPrice": return num(b.UnderlyingPrice, 0);
        case "LastPrice": return num(b.LastPrice, 0);
        case "Delta": return num(b.Delta, 0);
        // Sort on the figure the column SHOWS. Raw gamma sorts the same way here, but a
        // column that sorts on a number it does not display is how a table lies quietly.
        case "Gamma": return num(gammaPer1Pct(b.Gamma, b.UnderlyingPrice), 0);
        case "Theta": return num(thetaNorm(b.Theta), 0);
        case "Vega": return num(b.Vega, 0);
        case "ImpliedVol": return num(b.ImpliedVol, 0);
        case "VPD": return num(row.vpd, -Infinity);
        case "Efficiency": return num(row.efficiency, -Infinity);
        case "UnrealizedPnL": return num(b.UnrealizedPnL, 0);
        case "PnL%": return num(b.UnrealizedPnLPercent, -Infinity);
        default: return 0;
      }
    };

    arr.sort((a, b) => {
      const x = pick(a);
      const y = pick(b);

      if (typeof x === "string" && typeof y === "string") {
        const c = x.localeCompare(y);
        return direction === "asc" ? c : -c;
      }
      const nx = Number(x);
      const ny = Number(y);
      const c = nx < ny ? -1 : nx > ny ? 1 : 0;
      return direction === "asc" ? c : -c;
    });

    return arr;
  }, [rowsWithVpd, sortConfig, shouldFlipThetaSign]);

  const barScales = useMemo(() => {
    const deltas = sortedRowsWithVpd.map(r => Math.abs(num(r.base.Delta, 0))).filter(v => Number.isFinite(v));
    const vegas = sortedRowsWithVpd.map(r => Math.abs(num(r.base.Vega, 0))).filter(v => Number.isFinite(v));
    const vpds = sortedRowsWithVpd.map(r => Math.abs(num((r as any).vpd, 0))).filter(v => Number.isFinite(v));
    const effs = sortedRowsWithVpd.map(r => Math.abs(num((r as any).efficiency, 0))).filter(v => Number.isFinite(v));
    const maxDelta = deltas.length ? Math.max(...deltas) : 1;
    const maxVega = vegas.length ? Math.max(...vegas) : 1;
    const maxVpd = vpds.length ? Math.max(...vpds) : 1;
    const maxEff = effs.length ? Math.max(...effs) : 1;
    return { maxDelta, maxVega, maxVpd, maxEff };
  }, [sortedRowsWithVpd]);

  const zNow = displayMeta?.vix_zscore ?? null;

  const latestSnapshotAt = useMemo(() => {
    if (!rows || rows.length === 0) return "—";
    const times = rows.map(r => parseUtc(r.SnapshotAtUTC || r.PriceSnapshotUTC)).filter(Boolean) as Date[];
    if (times.length === 0) return "—";
    const max = new Date(Math.max(...times.map(d => d.getTime())));
    return fmtLocalDateTime(max);
  }, [rows]);

  const HarvestBadges = ({ m }: { m: MetaNormalized }) => {
    // F4: the light path (get_leaps_snapshot/__init__.py:876-877) computes pending_harvest and
    // harvest_window from the raw VIX-z thresholds WITHOUT consulting harvest.enabled, while the
    // engine gates the identical computation behind `if harvest_cfg.enabled:`
    // (strategy/rebalance_logic.py:883-889). Consume the flag here so a disabled control cannot
    // post an active chip. `!== false` deliberately: suppress only on a POSITIVE assertion of
    // disabled, so a missing field behaves exactly as it does today. Thresholds are untouched.
    const harvestEnabled = m.harvest_config?.enabled !== false;
    const pending = harvestEnabled && !!m.pending_harvest;
    const active = harvestEnabled && !!m.harvest_window;
    const earlyDefense = !!m.early_defense?.active;

    if (!pending && !active && !earlyDefense) return null;

    return (
      <div className="flex gap-1">
        {earlyDefense ? (
          <span className="text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wide bg-live/15 text-live ring-1 ring-live/30">
            Early Defense
          </span>
        ) : null}
        {pending ? (
          <span className="text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wide bg-warn/15 text-warn ring-1 ring-warn/30">
            Pending Harvest
          </span>
        ) : null}
        {active ? (
          <span className="text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wide bg-up/15 text-up ring-1 ring-up/30">
            Harvest Active
          </span>
        ) : null}
      </div>
    );
  };

  const DefenseGate = ({ m }: { m: MetaNormalized }) => {
    const confirmed = m.defense_confirmed;
    const byZ = m.defense_by_z;
    const blocked = (m.block_reason || "").trim();
    // gatefix backend: SPX history missing -> the S&P-below-50-day leg is SKIPPED (not passed). Positive assertion
    // only, so an older payload without spx_leg renders exactly as before.
    const spxSkipped = m.spx?.spx_leg === "skipped_unavailable";

    const chip = (txt: string, cls: string) => (
      <span className={`text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wide ${cls}`}>
        {txt}
      </span>
    );

    return (
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          {typeof confirmed === "boolean"
            ? confirmed
              ? chip("Confirmed", "bg-down/15 text-down ring-1 ring-down/30")
              : chip("Not Confirmed", "bg-subtle/15 text-subtle")
            : chip("—", "bg-subtle/15 text-subtle")}
          {typeof byZ === "boolean"
            ? byZ
              ? chip("Z-Trigger", "bg-warn/15 text-warn ring-1 ring-warn/30")
              : chip("Z-Ok", "bg-up/15 text-up ring-1 ring-up/30")
            : null}
        </div>
        {/* Directly under the state chip, not beside it: on one line the cell grew ~170px and pushed the Giveback
            stop tile off the five-checks line at 1500px. */}
        {spxSkipped ? (
          // Amber only when the skip matters: the z-score has fired, so confirmation is being tested without the SPX
          // check. On a calm day (z below the trigger) nothing rested on it, so a grey data note (CIO 2026-10-29).
          // Unknown z keeps the cautious amber.
          <div className="flex">
            {byZ === false ? (
              <span
                className="text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wide whitespace-nowrap cursor-help bg-subtle/15 text-subtle"
                title="SPX price history was unavailable. The defense gate is not being tested today (VIX z-score below the trigger), so nothing rested on the SPX check; if the z-score triggers while the data is still missing, the S&P-below-50-day check will be skipped (not passed)."
                data-testid="defense-spx-skipped"
                data-tone="grey"
              >
                SPX data missing
              </span>
            ) : (
              <span
                className="text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wide whitespace-nowrap cursor-help bg-warn/15 text-warn ring-1 ring-warn/30"
                title="SPX price history was unavailable, so the S&P-below-50-day check was skipped (not passed). Confirmation rests on the VIX z-score and term structure."
                data-testid="defense-spx-skipped"
                data-tone="amber"
              >
                SPX leg skipped (data)
              </span>
            )}
          </div>
        ) : null}
        {blocked ? (
          <div className="text-[11px]" style={secondaryText}>
            Block: <span className="font-medium">{blocked}</span>
          </div>
        ) : (
          <div className="text-[11px]" style={smallMutedText}>Hysteresis + confirms applied</div>
        )}
      </div>
    );
  };

  // Grind axis — the persistent-downtrend de-risk overlay. It complements the VIX-spike z-score:
  // the spike axis owns fast crashes, this axis owns slow bears that walk price below the 200-day
  // without spiking VIX. Advisory & close-basis, identical to the tested backtest signal. It only
  // ever LOWERS the recommended target, and only while the VIX band is still calm (spike-gated).
  // Book-level giveback stop — SHADOW (CIO 2026-10-20). The one rule that reads the BOOK's own realised loss
  // (every other brake reads the market). Backend replays the fund's flow-adjusted NAV history against the SPX
  // 200-day; this tile only displays that replay. It acts on nothing: no target, no trim, no stage, no order.
  const GivebackStopTile = ({ m }: { m: MetaNormalized }) => {
    const s = m.giveback_stop_shadow;
    const enabled = !!s?.enabled;
    const active = !!s?.active;
    const dd = typeof s?.drawdown_pct === "number" && Number.isFinite(s.drawdown_pct) ? (s.drawdown_pct as number) : null;
    const fireAt = typeof s?.fire_at_drawdown_pct === "number" ? (s.fire_at_drawdown_pct as number) : -25;
    const need = typeof s?.rearm_confirm_days === "number" ? (s.rearm_confirm_days as number) : 3;
    const run = typeof s?.confirm_run === "number" ? (s.confirm_run as number) : null;
    const floorPct = typeof s?.floor_pct === "number" ? (s.floor_pct as number) : 0.10;
    const rearmLvl = typeof s?.rearm_level === "number" && Number.isFinite(s.rearm_level) ? (s.rearm_level as number) : null;
    const spxVsRearm = typeof s?.spx_vs_rearm_pct === "number" && Number.isFinite(s.spx_vs_rearm_pct) ? (s.spx_vs_rearm_pct as number) : null;
    const fires = typeof s?.fires === "number" ? (s.fires as number) : 0;
    const sessions = typeof s?.data?.nav_sessions === "number" ? (s.data.nav_sessions as number) : null;
    const reason = s?.reason ?? null;
    const spxStatus = s?.spx_status ?? null;
    // Re-arm progress is shown only when the backend says it is counting; a missing or stale SPX read is named,
    // never drawn as "0/3".
    const cannotConfirm = active && (s?.rearm_status === "cannot_confirm" || run == null);
    const spxPhrase = spxStatus === "stale" ? "SPX close stale" : spxStatus === "no_sma" ? "SPX 200-day not available" : "SPX close unavailable";
    const lvl = (v: number | null) => (v == null ? "—" : Math.round(v).toLocaleString());
    // No read: no drawdown came back -- or the field is missing from the snapshot altogether (an older backend, a
    // failed build of the field): that is a no-read too, never "Off". The reason code is for the tooltip /
    // methodology; the tile says it in words.
    const missing = !s;
    const noRead = missing || (enabled && dd == null);
    const reasonCode = missing ? "not_reported" : reason;
    const reasonText = reasonCode ? GBS_REASON_TEXT[reasonCode] ?? (reasonCode.startsWith("error") ? "Unavailable (read error)" : "Unavailable") : "Read pending";

    const chip = (txt: string, cls: string) => (
      <span className={`text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wide font-semibold whitespace-nowrap ${cls}`}>{txt}</span>
    );
    const shadowChip = chip("Shadow", "bg-subtle/15 text-subtle ring-1 ring-subtle/30");
    let statusChip: React.ReactNode;
    if (noRead) statusChip = chip("No read", "bg-warn/15 text-warn ring-1 ring-warn/30");
    else if (!enabled) statusChip = chip("Off", "bg-subtle/15 text-subtle");
    else if (active) statusChip = chip("Fired", "bg-down/15 text-down ring-1 ring-down/30");
    else statusChip = chip("Armed", "bg-up/15 text-up ring-1 ring-up/30");

    const room = typeof s?.distance_to_fire_pp === "number" && Number.isFinite(s.distance_to_fire_pp) ? (s.distance_to_fire_pp as number) : null;
    // Compact tile (CIO 2026-10-21: "giveback stop smaller, grind axis larger, same line"). The long provenance
    // (sessions, as-of, close-mark-not-final, broker-marks-vs-NAVPS, SPX read status) lives in the footer's tooltip;
    // the tile keeps state, headline, gauge, the would-be action and one short muted line.
    const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const shortDate = (iso: string) => {
      const mm = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
      return mm ? `${MONTHS[Number(mm[2]) - 1] ?? mm[2]} ${Number(mm[3])}` : iso;
    };

    // Giveback gauge: how far the book sits from its running peak, against the fire line. Fills as the
    // drawdown deepens; the hairline is the trigger. Same visual grammar as the grind tile's days-below bar.
    const gauge = dd == null ? null : (() => {
      const span = Math.abs(fireAt) * 1.2;               // bar runs to 120% of the trigger so a fire sits inside it
      const frac = Math.max(0, Math.min(Math.abs(Math.min(dd, 0)) / span, 1));
      const threshPct = (Math.abs(fireAt) / span) * 100;
      const fillCls = active ? "bg-down/80" : dd <= fireAt * 0.6 ? "bg-warn/70" : "bg-subtle/40";
      const left = !enabled
        ? `${dd.toFixed(1)}% from peak`
        : active
        ? room != null && room < 0 ? `${Math.abs(room).toFixed(1)} pp past the line` : "at the line"
        : room != null ? `${room.toFixed(1)} pp to fire` : "watching the peak";
      return (
        <div title={`Flow-adjusted NAV is ${dd.toFixed(2)}% from its running peak${s?.peak_date ? ` (${s.peak_date})` : ""}${room != null && !active ? `; ${room.toFixed(1)} pp of further giveback before it fires` : ""}; the stop fires at ${fireAt.toFixed(0)}%.`}>
          <div className="relative w-full" style={{ height: 5 }}>
            <div className="absolute inset-0 rounded-full overflow-hidden bg-subtle/20">
              <div className={`h-full rounded-full ${fillCls}`} style={{ width: `${frac * 100}%` }} />
            </div>
            <div className="absolute" style={{ left: `${threshPct}%`, top: -1, bottom: -1, width: 1.5, transform: "translateX(-50%)", backgroundColor: "rgba(148,163,184,.85)" }} />
          </div>
          <div className="flex items-center justify-between gap-2 text-[10.5px] mt-1 tabular-nums" style={secondaryText}>
            <span>{left}</span>
            <span className="opacity-70">fires at {fireAt.toFixed(0)}%</span>
          </div>
        </div>
      );
    })();

    let action: React.ReactNode = null;
    if (noRead) {
      action = (
        <div className="text-[11px] break-words" style={secondaryText} title={reasonCode ? `reason: ${reasonCode}` : undefined} data-testid="giveback-stop-noread">
          {reasonText}
        </div>
      );
    } else if (!enabled) {
      action = <div className="text-[11px]" style={smallMutedText}>Switched off</div>;
    } else if (active) {
      action = (
        <>
          <div className="text-[11px] font-semibold text-down">Would cap target at {(floorPct * 100).toFixed(0)}%</div>
          <div
            className="text-[10.5px] tabular-nums break-words"
            style={secondaryText}
            title={
              cannotConfirm
                ? `Re-arm needs ${need} straight SPX closes above its 200-day + 2%; ${spxPhrase.toLowerCase()}, so the count cannot be confirmed. The fire itself stands on the NAV.`
                : `Re-arm: ${need} straight SPX closes above the re-arm level ${lvl(rearmLvl)} (200-day + 2%). SPX close is ${spxVsRearm != null ? `${spxVsRearm > 0 ? "+" : ""}${spxVsRearm.toFixed(1)}%` : "—"} vs that level.`
            }
          >
            {cannotConfirm
              ? `Re-arm cannot confirm (${spxPhrase})`
              : `Re-arm ${run}/${need} closes > ${lvl(rearmLvl)}${spxVsRearm != null ? ` · SPX ${spxVsRearm > 0 ? "+" : ""}${spxVsRearm.toFixed(1)}%` : ""}`}
          </div>
        </>
      );
    } else if (dd != null && room == null) {
      action = <div className="text-[11px]" style={smallMutedText}>Watching the running peak</div>;
    }

    // Everything the old footer spelled out, now in the tooltip of the one short line that remains.
    const detail = !noRead && enabled && s
      ? [
          `${fires} fire${fires === 1 ? "" : "s"} on record`,
          sessions != null ? `${sessions} sessions of NAV history replayed` : null,
          s?.as_of ? `as of ${s.as_of} (flow-adjusted NAV, at close)` : "flow-adjusted NAV, at close",
          s?.data?.today_row_dropped ? "today's mark not final yet (left out of the replay)" : null,
          s?.data && s.data.anchored === false ? "broker marks, not pinned to admin NAVPS" : null,
          !active && spxStatus && spxStatus !== "ok" ? `${spxPhrase} (re-arm input only)` : null,
        ].filter(Boolean).join(" · ")
      : null;

    return (
      <div className="space-y-1.5 min-w-0" data-testid="giveback-stop-tile">
        <div className="flex items-center gap-2">{statusChip}{shadowChip}</div>
        <div className="leading-none whitespace-nowrap" style={{ color: ui.textPrimary }}>
          {dd == null ? (
            <span className="text-[13px] font-bold">—</span>
          ) : (
            <>
              <span className={`text-[13px] font-bold ${active ? "text-down" : dd < 0 ? "text-warn" : ""}`}>{dd.toFixed(1)}%</span>{" "}
              <span className="text-[10px] font-normal" style={smallMutedText}>vs peak</span>
            </>
          )}
        </div>
        {gauge}
        {action}
        {detail ? (
          <div className="text-[10px] whitespace-nowrap" style={smallMutedText} data-testid="giveback-stop-detail">
            {fires} fire{fires === 1 ? "" : "s"}{s?.as_of ? ` · as of ${shortDate(s.as_of)}` : ""}
            <InfoHint text={detail} />
          </div>
        ) : null}
      </div>
    );
  };

  const GrindAxisTile = ({ m }: { m: MetaNormalized }) => {
    const g = m.grind_axis;
    const enabled = !!g?.enabled;
    const active = !!g?.active;
    const applied = !!g?.applied;
    const baseCalm = !!g?.base_regime_calm;
    const pctFrom = typeof g?.pct_from_sma200 === "number" && Number.isFinite(g.pct_from_sma200) ? (g.pct_from_sma200 as number) : null;
    const below = typeof g?.days_below_200_of_window === "number" ? (g.days_below_200_of_window as number) : null;
    const win = typeof g?.persist_window === "number" ? (g.persist_window as number) : 20;
    const need = typeof g?.persist_days === "number" ? (g.persist_days as number) : 15;
    const floorPct = typeof g?.target_pct === "number" ? (g.target_pct as number) : 0.10;
    const floorTxt = `${(floorPct * 100).toFixed(0)}%`;

    // Actual SPX index levels of the moving averages — the price where SPX would cross each
    // average. Grounds the "% vs 200-day" read with the concrete crossing thresholds the CIO
    // watches (the grind fires on a persistent close BELOW the 200-day). Close-basis, from the
    // same SPX close history as the signal; the live intraday level lives on the tape below.
    // Strictly the settled CLOSE (never the live/current price): the grind axis is a
    // close-basis signal — its "% vs 200-day" is computed from the last close, so the SPX
    // level shown here must be that same close, or the two would silently disagree. The
    // live intraday SPX (and its own % vs the 200-day) lives on the situational live tape.
    const spx = m.spx ?? null;
    const sma50Lvl = typeof spx?.spx_sma === "number" && Number.isFinite(spx.spx_sma) ? (spx.spx_sma as number) : null;
    const sma200Lvl = typeof spx?.spx_sma200 === "number" && Number.isFinite(spx.spx_sma200) ? (spx.spx_sma200 as number) : null;
    const spxCloseLvl = typeof spx?.spx_close === "number" && Number.isFinite(spx.spx_close) ? (spx.spx_close as number) : null;
    const lvl = (v: number | null) => (v == null ? "—" : Math.round(v).toLocaleString());

    const chip = (txt: string, cls: string) => (
      <span className={`text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wide font-semibold ${cls}`}>{txt}</span>
    );

    let statusChip: React.ReactNode;
    if (!enabled) statusChip = chip("Off", "bg-subtle/15 text-subtle");
    else if (applied) statusChip = chip("De-risk", "bg-down/15 text-down ring-1 ring-down/30");
    else if (active) statusChip = chip("Detected", "bg-warn/15 text-warn ring-1 ring-warn/30");
    else statusChip = chip("Armed", "bg-up/15 text-up ring-1 ring-up/30");

    // Days-below proximity gauge: how close the trend is to firing (need of win), state-colored,
    // with a hairline marker at the fire threshold. Encodes the count in form, not just number.
    const gauge = below == null ? (
      <div className="text-[11px]" style={secondaryText}>trend read pending</div>
    ) : (() => {
      const frac = Math.max(0, Math.min(below / win, 1));
      const threshPct = (need / win) * 100;
      const fillCls = (applied || below >= need) ? "bg-down/80" : below > 0 ? "bg-warn/70" : "bg-subtle/40";
      return (
        <div title={`${below} of the last ${win} sessions closed below the 200-day; the grind fires at ${need}.`}>
          <div className="relative w-full" style={{ height: 5 }}>
            <div className="absolute inset-0 rounded-full overflow-hidden bg-subtle/20">
              <div className={`h-full rounded-full ${fillCls}`} style={{ width: `${frac * 100}%` }} />
            </div>
            <div className="absolute" style={{ left: `${threshPct}%`, top: -1, bottom: -1, width: 1.5, transform: "translateX(-50%)", backgroundColor: "rgba(148,163,184,.85)" }} />
          </div>
          <div className="flex items-center justify-between text-[10.5px] mt-1 tabular-nums" style={secondaryText}>
            <span>{below}/{win} days below</span>
            <span className="opacity-70">fires at {need}</span>
          </div>
        </div>
      );
    })();

    let action: React.ReactNode;
    if (!enabled) {
      action = <div className="text-[11px]" style={smallMutedText}>Persistent-downtrend de-risk</div>;
    } else if (applied) {
      action = <div className="text-[11px] font-semibold text-down">Cuts target to {floorTxt}</div>;
    } else if (active && !baseCalm) {
      action = <div className="text-[11px]" style={secondaryText}>Spike axis already defensive — grind stands down</div>;
    } else if (active) {
      action = <div className="text-[11px]" style={secondaryText}>Target already at/below the {floorTxt} floor</div>;
    } else {
      action = <div className="text-[11px]" style={smallMutedText}>Trend intact — watching the 200-day</div>;
    }

    return (
      <div className="space-y-1.5 min-w-0" data-testid="grind-axis-tile">
        <div className="flex items-center gap-2">{statusChip}</div>
        <div className="leading-none" style={{ color: ui.textPrimary }}>
          {pctFrom == null ? (
            <span className="text-[13px] font-bold">—</span>
          ) : (
            <>
              <span className={`text-[13px] font-bold ${pctFrom < 0 ? "text-warn" : ""}`}>
                {pctFrom > 0 ? "+" : ""}{pctFrom.toFixed(1)}%
              </span>
              <span className="text-[10px] font-normal ml-1" style={smallMutedText}>vs 200-day · at close</span>
            </>
          )}
        </div>
        {(sma200Lvl != null || sma50Lvl != null) ? (
          <div
            className="text-[10.5px] tabular-nums leading-tight"
            style={smallMutedText}
            title="Actual SPX index levels of the 200- and 50-day moving averages (the price SPX would cross each), plus the settled SPX close. The grind is a CLOSE-BASIS signal, so its % is measured off the close shown here — it will differ from the live tape's SPX-trend %, which uses the intraday price against the same averages."
          >
            <span style={secondaryText}>200-day {lvl(sma200Lvl)}</span>
            {sma50Lvl != null ? <span> · 50-day {lvl(sma50Lvl)}</span> : null}
            {spxCloseLvl != null ? <span className="opacity-70"> · SPX {lvl(spxCloseLvl)} close</span> : null}
          </div>
        ) : null}
        {gauge}
        {action}
        {enabled && applied && (g?.rearm_mode === "reclaim" || g?.rearm_mode === "reclaim_slope") ? (
          <div className="text-[10px]" style={smallMutedText}>
            Holds until {typeof g?.rearm_confirm_days === "number" ? g.rearm_confirm_days : 3} closes ≥{((typeof g?.rearm_buffer_pct === "number" ? (g.rearm_buffer_pct as number) : 0.02) * 100).toFixed(0)}% above the 200-day
          </div>
        ) : null}
      </div>
    );
  };

  // SPXTile / TermTile removed — their live SPX-trend (level, SMA50/200, day change) and VIX
  // term-structure reads are shown on the Live Market Tape. The close-basis signal inputs
  // (Defense Gate, VIX, Z-Score) now live in the "Signal checks" row of the top command band,
  // beside the regime verdict.

  const efficiencyBadge = (eff: number | null) => {
    if (eff == null || !Number.isFinite(eff)) return "—";
    const pctEff = eff * 100;
    const tier = efficiencyTier(pctEff);
    const tierLabel: Record<EfficiencyTier, string> = {
      above: "above fresh",
      fresh: "fresh-equivalent",
      healthy: "lightly seasoned",
      softening: "seasoned",
      recycle: "deep ITM",
    };
    // The two seasoned bands are NEUTRAL by design (2026-09-09). They used to carry
    // warn/critical colour, which reads as "something is wrong here" — but a low
    // efficiency describes a deep-ITM winner, not a defect. The neutral ramp gets
    // slightly more prominent as the line seasons, without asserting good or bad.
    const cls: Record<EfficiencyTier, string> = {
      above: "bg-up/25 text-up ring-1 ring-up/50",
      fresh: "bg-up/15 text-up ring-1 ring-up/30",
      healthy: "bg-up/10 text-up ring-1 ring-up/20",
      softening: "",
      recycle: "",
    };
    const chipStyle: Partial<Record<EfficiencyTier, React.CSSProperties>> = {
      softening: {
        backgroundColor: ui.overlaySoft,
        color: ui.textSecondary,
        boxShadow: `inset 0 0 0 1px ${ui.cardBorder}`,
      },
      recycle: {
        backgroundColor: ui.overlayStrong,
        color: ui.textPrimary,
        boxShadow: `inset 0 0 0 1px ${ui.cardBorder}`,
      },
    };
    const title =
      `Efficiency = VPD ÷ a tenor-matched fresh ATM benchmark (${pctEff.toFixed(0)}%, ${tierLabel[tier]}). ` +
      `Roughly the share of premium still held as time value. A low reading means the line has gone deep ` +
      `in-the-money — normally a winner, and a position that has de-levered itself — not a defect, and not a trade prompt.`;
    return (
      <span title={title} className={`px-1.5 py-0.5 rounded ${cls[tier]}`} style={chipStyle[tier]}>
        {pctEff.toFixed(0)}%
      </span>
    );
  };

  /* -----------------------------------------------------------------------------------
   * B2 (CIO-approved 2026-09-09): per-row benchmark PROVENANCE.
   *
   * benchmarkSource and benchMode were already computed per row (see the efficiency
   * useMemo) but were only ever surfaced in aggregate, as the tile's "N extrapolated"
   * chip. So a reading scored against a FLAT EXTRAPOLATION -- past the deepest tenor the
   * ORATS surface actually covers, which is exactly where its coverage thins to a handful
   * of quotes -- was styled identically to one interpolated between two real nodes.
   *
   * `interpolated` is deliberately UNMARKED: it is the normal, measured case, and marking
   * it would turn the column into noise. Only the three unproven modes get a marker.
   *
   * The marker keeps WARN colour even though the seasoning bands went neutral in the same
   * session. That is not an inconsistency: the bands describe POSITION quality (a deep-ITM
   * line is a winner, not a defect), whereas this describes DATA quality (an extrapolated
   * benchmark genuinely is less trustworthy than a quoted one). Different claims.
   * --------------------------------------------------------------------------------- */
  const benchProvenance = (row: any): { mark: string; title: string } | null => {
    if (row?.efficiency == null || !Number.isFinite(row.efficiency)) return null;
    const source = row?.benchmarkSource as string | null | undefined;
    const mode = row?.benchMode as string | null | undefined;

    if (source === "in_book") {
      return {
        mark: "\u00a7",
        title:
          "\u00a7 Benchmarked against OUR OWN BOOK, not the market: the ORATS surface could not " +
          "support a trustworthy fresh-ATM benchmark for this underlying, so the denominator is " +
          "the median VPD of the freshest DTE quartile we already hold. That is self-referential " +
          "\u2014 a book that has seasoned as a whole benchmarks itself and reads near 100%. " +
          "Treat this row's efficiency as indicative only.",
      };
    }
    if (source === "market" && mode === "clamped_long") {
      return {
        mark: "\u2020",
        title:
          "\u2020 EXTRAPOLATED, not measured: this position is longer-dated than anything the " +
          "surface covers" +
          (benchmark.deepestNodeDte ? ` (deepest node ~${Math.round(benchmark.deepestNodeDte)} DTE)` : "") +
          ". Past that point the benchmark curve is held FLAT, so this efficiency is scored " +
          "against a guess \u2014 and ORATS coverage is thinnest at exactly this end of the " +
          "curve. Treat the reading as indicative, not a measurement.",
      };
    }
    if (source === "market" && mode === "clamped_short") {
      return {
        mark: "\u2020",
        title:
          "\u2020 EXTRAPOLATED, not measured: this position is shorter-dated than the shallowest " +
          "node on the benchmark curve, so the curve is held FLAT below that point and this " +
          "efficiency is scored against a guess rather than a quote.",
      };
    }
    if (source === "market" && mode === "headline") {
      return {
        mark: "\u2021",
        title:
          "\u2021 NOT tenor-matched: no per-tenor curve was available, so this row was scored " +
          "against the single 24\u201330m headline benchmark. Tenor-matching exists precisely so " +
          "a long-dated rung is not judged against a shorter yardstick \u2014 without it, this " +
          "reading conflates moneyness with duration.",
      };
    }
    return null;
  };

  /* -----------------------------------------------------------------------------------
   * B1 (CIO-approved 2026-09-09): the "no partial trim" lot constraint, ported from the
   * Portfolio page (PortfolioView.tsx:1418) so both surfaces say the same thing.
   *
   * A line below the policy minimum can pass the 2x trigger, keep seasoning, and never
   * stage a ticket -- int(1 * 0.5) = 0 and the engine only tickets above zero. It is on the
   * QUANTITY cell, not the Efficiency cell: the constraint is a fact about lot size, and
   * hanging it off Efficiency would re-attach an action to the number this session
   * deliberately turned into a diagnostic.
   *
   * Like Portfolio, it fires on LOT SIZE ALONE, not on return. Gating it on "through the
   * home-run trigger" would render it on ZERO rows today -- the nearest one-lot is 1.93x --
   * which is the exact bug portfolio_view.py:2344-2352 was written to fix.
   * --------------------------------------------------------------------------------- */
  const lotConstraintBadge = (qty: unknown) => {
    const c = lotHarvestConstraint(qty, homeRun?.policy);
    if (!c || !c.blocked) return null;
    return (
      <div
        className="mt-0.5 text-[9.5px] leading-tight font-semibold"
        style={{ color: ui.textSecondary }}
        title={
          `A partial trim is not available on this line: policy needs at least ${c.minContracts} ` +
          `contracts to split a position and it holds ${c.quantity}. It can pass the ${
            typeof homeRun?.policy?.trigger_multiple === "number" ? homeRun.policy.trigger_multiple : 2
          }x harvest trigger and still stage nothing, because the trim rounds to zero. ` +
          `It is a hold-or-close decision, not a defect in the position.`
        }
      >
        No partial trim
      </div>
    );
  };

  const renderBarCell = (value: number | null, maxAbs: number, text: React.ReactNode, title?: string) => {
    const v = (value == null || !Number.isFinite(value)) ? null : value;
    const pct = v == null ? 0 : safePct(v, maxAbs);
    return (
      <div className="relative" title={title}>
        <div
          className="w-full rounded h-2 overflow-hidden"
          style={barTrackStyle()}
        >
          <div
            className="h-2 rounded"
            style={{ ...bar(pct), ...neutralBarFillStyle() }}
          />
        </div>
        <div className="mt-1">{text}</div>
      </div>
    );
  };

  type ColAlign = "left" | "center" | "right";
  // Display label is plain English for a CIO; sortKey stays the original identifier the sort
  // switch keys on (decoupled so renaming a header never breaks its sort).
  type RiskColDef = { key: string; sortKey: string; label: string; align: ColAlign; width: string };

  const RISK_COLS: RiskColDef[] = [
    { key: "Account", sortKey: "Account", label: "Account", align: "left", width: "120px" },
    { key: "Symbol", sortKey: "Symbol", label: "Symbol", align: "left", width: "70px" },
    { key: "Expiration", sortKey: "Expiration", label: "Expiration", align: "left", width: "120px" },
    { key: "DTE", sortKey: "DTE", label: "DTE", align: "right", width: "70px" },
    { key: "AgeDays", sortKey: "Age (d)", label: "Age (days)", align: "right", width: "70px" },
    { key: "Right", sortKey: "Right", label: "Right", align: "center", width: "60px" },
    { key: "Strike", sortKey: "Strike", label: "Strike", align: "right", width: "90px" },
    { key: "Quantity", sortKey: "Quantity", label: "Quantity", align: "right", width: "90px" },
    { key: "UnderlyingPrice", sortKey: "UnderlyingPrice", label: "Underlying", align: "right", width: "130px" },
    { key: "LastPrice", sortKey: "LastPrice", label: "Last", align: "right", width: "110px" },
    { key: "Delta", sortKey: "Delta", label: "Delta", align: "right", width: "110px" },
    { key: "Gamma", sortKey: "Gamma", label: "Gamma (Δ/1%)", align: "right", width: "120px" },
    { key: "Theta", sortKey: "Theta", label: "Theta", align: "right", width: "100px" },
    { key: "Vega", sortKey: "Vega", label: "Vega", align: "right", width: "110px" },
    { key: "ImpliedVol", sortKey: "ImpliedVol", label: "IV", align: "right", width: "110px" },
    { key: "VPD", sortKey: "VPD", label: "VPD", align: "right", width: "120px" },
    { key: "Efficiency", sortKey: "Efficiency", label: "Efficiency", align: "right", width: "140px" },
    { key: "UnrealizedPnL", sortKey: "UnrealizedPnL", label: "Unrealized P&L", align: "right", width: "140px" },
    { key: "PnL%", sortKey: "PnL%", label: "P&L %", align: "right", width: "90px" },
  ];

  const headClass = (align: ColAlign) => {
    const base = "px-3 py-2 whitespace-nowrap align-middle text-xs font-semibold tracking-wide cursor-pointer select-none";
    const a = align === "right" ? "text-right tabular-nums" : align === "center" ? "text-center" : "text-left";
    return `${base} ${a}`;
  };

  const cellClass = (align: ColAlign, extra?: string) => {
    const base = "px-3 py-2 whitespace-nowrap align-middle";
    const a = align === "right" ? "text-right tabular-nums" : align === "center" ? "text-center" : "text-left";
    return `${base} ${a} ${extra || ""}`.trim();
  };

  const expiryGroups = useMemo(() => {
    const groups: Record<string, {
      exp: string;
      dte: number | null;
      rows: typeof sortedRowsWithVpd;
      qtySum: number;
      vega$: number;
      delta$Per1pct: number | null;
    }> = {};

    const spx = convexity.spx;

    sortedRowsWithVpd.forEach((r) => {
      const exp = r.base.Expiration || "—";
      if (!groups[exp]) {
        groups[exp] = {
          exp,
          dte: computeDte(r.base.Expiration),
          rows: [],
          qtySum: 0,
          vega$: 0,
          delta$Per1pct: (spx != null) ? 0 : null,
        };
      }
      groups[exp].rows.push(r);
      groups[exp].qtySum += num(r.base.Quantity, 0);

      const v = num(r.base.Vega, 0);
      const qty = num(r.base.Quantity, 0);
      const mult = num(r.base.Multiplier, 100);
      groups[exp].vega$ += v * qty * mult;

      const g = groups[exp];
      if (spx != null && g.delta$Per1pct != null) {
        const d = num(r.base.Delta, 0);
        g.delta$Per1pct += d * qty * mult * (spx * 0.01);
      }
    });

    const list = Object.values(groups).sort((a, b) => {
      if (a.dte == null && b.dte == null) return 0;
      if (a.dte == null) return 1;
      if (b.dte == null) return -1;
      return a.dte - b.dte;
    });

    return list;
  }, [sortedRowsWithVpd, convexity.spx]);

  const fmtMon = (iso?: string) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, { month: "short", year: "2-digit" });
  };

  // ---- Authoritative Total NAV = broker book + bank cash + accruals (from NAV Fund Services),
  // the same figure the Risk cockpit shows. The sizing denominator (navUsed) deliberately stays the
  // broker book, because the rebalance engine computes current_weight / target / funded-cap against it.
  const navDaily = (nav as any)?.reconciliation?.daily ?? null;
  const navTotalDisplay =
    typeof navDaily?.estimated_nav_now === "number" ? navDaily.estimated_nav_now : (navUsed ?? null);

  // ---- Intraday book exposure (situational; provisional; NOT official NAV) -----------------
  // Today's brokerage move (live IBKR day P&L, LEAPS + FI both held at IBKR) layered on the last
  // reconciled NAV for an intraday NAV estimate; only off-IBKR bank cash + accruals sit held-flat.
  // Book greeks (delta$/vega$/theta$) come from the `convexity` memo and are AS OF the last
  // position snapshot, not live — so they are presented separately from the live P&L.
  // Horizon's payload only: the fund-of-record push/poll. A payload labelled for another fund is never
  // shown as Horizon's (fund toggle, 2026-09-25).
  const horizonPnl = payloadBelongsTo(intradayPnl, "HORIZON") ? intradayPnl : null;
  const horizonPnlSource = pnlSourceOf(horizonPnl);
  const dayPnl = typeof horizonPnl?.day_pnl === "number" ? horizonPnl.day_pnl : null;
  const reconNav = typeof navTotalDisplay === "number" ? navTotalDisplay : null;

  // Does the NAV basis ALREADY include today's move? When `latest_nav` served a fresh IBKR
  // pull, `estimated_nav_now` = last official NAV + the broker book's move since that date —
  // and that move already contains today's P&L. Adding day P&L on top double-counts it
  // (confirmed live 2026-08-05: the NAV path's broker mark and the P&L feed's current net liq
  // agreed to within $2.4k, i.e. the SAME live number, not a prior close).
  // Backend is authoritative via `nav_basis_includes_today`; the `source` test is only a
  // fallback for a backend that predates that flag, so the two can deploy independently.
  const navBasisIncludesToday: boolean =
    typeof navDaily?.nav_basis_includes_today === "boolean"
      ? navDaily.nav_basis_includes_today
      : /ibkr/i.test(String((nav as any)?.source ?? ""));

  // Prior-close NAV = the base the day's move was earned ON. This is the correct denominator
  // for a daily return and is what IBKR/TWS uses for "Since prior Close %"; dividing by a NAV
  // that already contains today's gain understates the move on up days (and overstates on down
  // days). Only the IBKR-held sleeves move intraday, so off-IBKR bank cash + accruals — carried
  // inside the reconciled anchor — sit flat in both terms and cancel out of the subtraction.
  const priorCloseNav =
    reconNav == null ? null : navBasisIncludesToday ? (dayPnl != null ? reconNav - dayPnl : reconNav) : reconNav;

  // Intraday NAV estimate: when the basis is already live, it IS the estimate — do not re-add.
  const intradayNavEst =
    reconNav == null ? null : navBasisIncludesToday ? reconNav : (dayPnl != null ? reconNav + dayPnl : reconNav);

  const dayPnlPctNav =
    priorCloseNav != null && priorCloseNav > 0 && dayPnl != null ? (dayPnl / priorCloseNav) * 100 : null;
  const pnlAccounts = typeof horizonPnl?.account_count === "number" ? horizonPnl.account_count : null;
  const pnlErrors: string[] = Array.isArray(horizonPnl?.errors) ? horizonPnl.errors : [];
  const pnlFeedEmpty = pnlErrors.includes("feed_empty"); // transient/after-hours — benign, not an error
  const pnlUnavailable = horizonPnl != null && dayPnl == null;
  // Day P&L caveat (null for IBKR's own per-account P&L; the NAV-change estimate is always labelled).
  const horizonDaySourceLabel = dayPnlSourceLabel(horizonPnl);
  const horizonSourcePill = sourcePill(horizonPnl);
  // The estimate's "as of HH:MM[, may be stale]" note (review F2), shown on the Day P&L tile.
  const horizonAsOf = estimateAsOfNote(horizonPnl);
  // IBKR's LAST Horizon figure, held while the Trade Desk had the other fund selected (CIO 2026-10-29).
  // Not a problem, just a held value: a neutral pill on the tile, the figure stays.
  const horizonHeld = heldInfoOf(horizonPnl);
  const horizonHeldText =
    horizonHeld == null
      ? null
      : heldPillText(horizonHeld, intradayPnlAtMs == null ? 0 : (nowTickMs - intradayPnlAtMs) / 1000);
  // While held the backend replaces the whole read (unrealized, net liq, account count too), so the
  // whole "Today's move" row says held, not just the Day P&L tile (review P2-1). Neutral slate only.
  const horizonRowHeld = horizonHeld != null;
  const horizonFundLabel =
    typeof horizonPnl?.fund?.display_name === "string" && horizonPnl.fund.display_name ? horizonPnl.fund.display_name : null;
  // Meridian (or any fund other than the fund of record): its own payloads only.
  const otherTiles =
    intradayFund === "HORIZON"
      ? null
      : otherFundTiles(otherFundPnl, otherFundNav, INTRADAY_FUNDS.find((f) => f.code === intradayFund)?.label ?? intradayFund, otherFundErr);
  const otherFundName = INTRADAY_FUNDS.find((f) => f.code === intradayFund)?.label ?? intradayFund;
  const otherFundNotFunded = pnlSourceOf(otherFundPnl) === "not_funded";
  // Partial-book undercount: Day P&L is a real dollar sum but covers only SOME fund
  // sub-accounts, so it understates the whole-fund move and (via the Intraday NAV estimate)
  // the NAV — surface it rather than show it as complete (2026-07-01 audit P1 #13).
  // feed_empty (after-hours) is benign and excluded.
  const PNL_PARTIAL_FLAGS = ["pnl_subaccount_undercount", "pnl_master_keyed", "select_account_failed", "no_fund_subaccounts"];
  // Only IBKR's own P&L can be a partial sum; the NAV-change estimate is one whole account.
  const pnlPartial = pnlIsPartial(horizonPnl, PNL_PARTIAL_FLAGS);
  const pnlDiscovered = typeof horizonPnl?.diagnostics?.discovered_subaccount_count === "number" ? horizonPnl.diagnostics.discovered_subaccount_count : null;

  // ---- Current stance (cockpit headline): one decisive read — regime → target vs now → playbook.
  // Single source of truth for the playbook so the headline band and the PlaybookCard never drift.
  const stanceRegimeLabel = (tileMeta?.effective_regime ?? tileMeta?.regime) as string | undefined;
  const stanceRegime = getRegimeDisplay(stanceRegimeLabel);
  // Regime-transition recency — a regime that flipped at last night's close is a very different
  // decision context from one that has held for a month. Read-only display of a timestamp already
  // in the payload; recent flips read blue (a directive cue), never red/green.
  const regimeTransitionNote = (() => {
    const iso = displayMeta?.last_transition_utc;
    if (!iso) return null;
    const d = parseUtc(iso);
    if (!d) return null;
    const days = Math.floor((nowTickMs - d.getTime()) / 86400000);
    if (days <= 0) return { text: "changed today", recent: true };
    if (days === 1) return { text: "changed yesterday", recent: true };
    if (days <= 21) return { text: `held ${days}d`, recent: days <= 2 };
    return { text: `since ${fmtLocalDate(iso)}`, recent: false };
  })();
  const stancePhase = tileMeta?.phase;
  const stanceTargetPct = ((displayMeta as any)?.target_pct_effective ?? (displayMeta as any)?.target_pct) ?? null;
  const stanceCurrentPct = (displayMeta as any)?.current_weight ?? null;
  const stanceGapNotional =
    typeof stanceTargetPct === "number" && typeof stanceCurrentPct === "number" && typeof navUsed === "number" && navUsed > 0
      ? (stanceTargetPct - stanceCurrentPct) * navUsed
      : null;
  // Deploy/trim wiring for the playbook. The tolerance BAND (rebalance_tolerance_pct — the same
  // band the Positioning tile shows) is the leeway: only a move PAST the band is a directive;
  // within it is genuinely "no action". allocAction already encodes ADD/TRIM/HOLD against that band.
  const allocTolAbs = resolveAllocationToleranceBand(
    typeof stanceTargetPct === "number" ? stanceTargetPct : null,
    (displayMeta as any)?.rebalance_tolerance_pct ?? null
  );
  const stanceUnderTarget = allocAction.state === "ADD" && !fundedCapBreached && !executableTrimSignal;
  const addToTargetNotional =
    stanceUnderTarget && typeof stanceGapNotional === "number" && stanceGapNotional > 0 ? stanceGapNotional : null;
  const addToBandNotional =
    addToTargetNotional != null && typeof navUsed === "number"
      ? Math.max(0, addToTargetNotional - allocTolAbs * navUsed)
      : null;
  const stanceBandLowPct =
    stanceUnderTarget && typeof stanceTargetPct === "number" ? stanceTargetPct - allocTolAbs : null;
  // Real bounded trim $ = the funded gap to target (the old wiring read keys the backend never sets).
  const trimToTargetNotional =
    executableTrimSignal && typeof stanceGapNotional === "number" && stanceGapNotional < 0
      ? Math.abs(stanceGapNotional)
      : null;
  const playbookArgs = {
    pendingHarvest: Boolean((displayMeta as any)?.pending_harvest ?? false),
    harvestActive: Boolean((displayMeta as any)?.harvest_window ?? false),
    // F4: consume the emitted-but-never-read harvest.enabled flag.
    harvestEnabled: (displayMeta as any)?.harvest_config?.enabled !== false,
    earlyDefenseActive: Boolean((displayMeta as any)?.early_defense?.active ?? false),
    // Signals are close-based; a live intraday VIX is shown for context only and must
    // NOT gate the playbook to WAIT_FOR_CLOSE. The NEXT_SESSION / after-close timing in
    // the actionable branches already enforces the close-to-close execution discipline.
    live: false,
    overTarget: executableTrimSignal,
    capBreached: fundedCapBreached,
    targetPct: stanceTargetPct,
    currentPct: stanceCurrentPct,
    trimNotional: trimToTargetNotional,
    underTarget: stanceUnderTarget,
    addNotionalLow: addToBandNotional,
    addNotionalHigh: addToTargetNotional,
    bandLowPct: stanceBandLowPct,
    vixTrimEnabled: (displayMeta as any)?.vix_trim_enabled,
    targetSource: (displayMeta as any)?.target_source ?? null,
  };
  const stancePb = playbookFromSignals(playbookArgs);
  const stanceTimingLabel =
    stancePb.timing === "ACT_NOW" ? "Act now" : stancePb.timing === "WAIT_FOR_CLOSE" ? "Wait for close" : "Next session";
  const stancePhaseLabel =
    stancePhase == null ? "" : ` · Phase ${typeof stancePhase === "number" ? stancePhase + 1 : stancePhase}`;

  return (
    <Layout>
      <SignalsMethodologyModal open={methodologyOpen} onClose={() => setMethodologyOpen(false)} />
      <div style={{ backgroundColor: ui.pageBg, color: ui.textPrimary }} className="px-3 py-4">
        <section id="signals-sec-stance" className="mb-4 scroll-mt-20 space-y-4">
          <div style={commandBandStyle}>
            <div className="flex flex-wrap items-start justify-between gap-4" style={sectionHeaderRowStyle}>
              <div className="min-w-[280px] flex-1">
                <div className={sectionLabelClass} style={smallMutedText}>Signals dashboard</div>
                <h2 className="mt-1 text-[2.1rem] leading-none font-semibold tracking-tight" style={{ color: ui.textPrimary }}>
                  LEAPS Regime Diagnostics
                </h2>
                <div className="mt-1.5 text-[0.95rem] max-w-3xl" style={{ color: ui.textSecondary }}>
                  Institutional control surface for close-confirmed convexity sizing, deployment pacing, and contract-level monitoring.
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <HelionButton
                  size="sm"
                  onClick={() => setMethodologyOpen(true)}
                  title="How to read this board — what every read means, how it is computed, and the close-to-close rule that governs it."
                >
                  Methodology
                </HelionButton>
                <HelionButton
                  size="sm"
                  variant="secondary"
                  onClick={handleExportCsv}
                  title="Download the holdings grid below (current filters & sort) as a CSV. Local download only — places no orders."
                >
                  Export CSV
                </HelionButton>
                <HelionButton
                  size="sm"
                  variant="primary"
                  onClick={forceSyncAndReload}
                  disabled={loading}
                  title="Pulls the latest positions, NAV and greeks into the dashboard. Does not place, modify, or cancel any orders."
                >
                  {loading ? "Syncing…" : "Sync IBKR + Refresh"}
                </HelionButton>
                <HelionButton size="sm" onClick={refreshAll} disabled={loading}>
                  {loading ? "Refreshing…" : "Refresh"}
                </HelionButton>
              </div>
            </div>

            {/* ---- CURRENT STANCE + LIVE TAPE: one card, explicit hierarchy ----
                 The close-confirmed verdict (regime → target vs now → playbook) is the hero and
                 leads the card. The live market tape is demoted to a muted, situational-only ticker
                 in the same card's footer (see LiveTapeStrip). Deliberately NOT merged into a single
                 equal-weight line: Helion acts on the close, and the tape must never read as if it
                 moves the playbook. */}
            <div
              className="rounded-xl overflow-hidden"
              style={{
                border: `1px solid ${ui.cardBorder}`,
                background: `linear-gradient(180deg, ${ui.overlaySoft} 0%, transparent 100%)`,
                boxShadow: `inset 0 1px 0 ${ui.innerHighlight}`,
              }}
            >
              <div className="p-3.5">
                {/* One deterministic row from 2200px (the CIO's 2560 monitor and the 2200 runs): never reflow a tile
                    (e.g. Grind axis) onto a second line there. Below 2200px the row WRAPS (2026-10-20): with the
                    Giveback stop tile added it no longer fits at 1500/1600, and a horizontal scroller left that tile
                    half off-screen -- a hidden brake reads like no brake. Wrapped lines pack from the left with one
                    consistent gap (justify-between left a wide hole at 1600). 2200px, not 1800 / 2000: there the one-line row
                    squeezed the regime / VIX tiles into two-line labels. */}
                {/* justify-between spreads the row across a wide monitor (it used to stop about
                    two-thirds of the way across). When the row is wider than the card it
                    degrades to flex-start and scrolls, so nothing is clipped on the left. */}
                {/* 2026-10-21 (CIO: "giveback stop smaller, grind axis larger, all within the same line"): the row is two
                    groups. Below 2200px they stack -- the verdict trio, then the five signal checks (VIX / Z-score / Defense
                    gate / Grind axis / Giveback stop) on ONE line of their own from 1500px. From 2200px both groups are
                    display:contents, so all eight cells sit on the single row exactly as before. Grind and Giveback share
                    the spare width by flex ratio (grind grows 3:1, capped 520 / 260px), so Grind is the wider cell
                    (~1.6-2x) and its level / status lines stay on one line each. */}
                <div className="flex flex-col min-[2200px]:flex-row min-[2200px]:flex-nowrap items-stretch min-[2200px]:justify-between gap-y-4 min-[2200px]:gap-x-7 min-[2200px]:overflow-x-auto" data-testid="signals-stance-row">
                  <div className="flex flex-wrap items-stretch gap-x-10 gap-y-4 min-[2200px]:contents" data-testid="signals-stance-verdict">
                  <div className="min-w-[150px]" data-stance-cell="regime">
                    <div className="text-[10px] uppercase tracking-[0.18em] opacity-60">Current regime (close)</div>
                    <div className="mt-1 text-[1.8rem] leading-none font-bold" style={{ color: stanceRegime.color }}>
                      {stanceRegime.name}
                    </div>
                    <div className="text-[11px] opacity-65 mt-1">{stanceRegime.band}{stancePhaseLabel}</div>
                    {regimeTransitionNote ? (
                      <div
                        className={`text-[10px] mt-0.5 ${regimeTransitionNote.recent ? "text-info font-semibold" : "opacity-55"}`}
                        title="How long the current close regime has held. A recent flip is a different decision context than a long-held regime."
                      >
                        {regimeTransitionNote.recent ? "▲ " : ""}{regimeTransitionNote.text}
                      </div>
                    ) : null}
                    {/* Harvest / Early-Defense state — relocated here from the removed WHY "Regime" tile so it stays visible. */}
                    {tileMeta ? <div className="mt-1.5"><HarvestBadges m={tileMeta} /></div> : null}
                  </div>

                  <div className="min-w-[190px]" data-stance-cell="target">
                    <div className="text-[10px] uppercase tracking-[0.18em] opacity-60">LEAPS target → now</div>
                    <div className="mt-1 text-[1.8rem] leading-none font-bold" style={{ color: ui.textPrimary }}>
                      {fmtPct(stanceTargetPct)}
                      {/* gatefix backend: the band phase was ASSUMED (prior close z missing). Positive assertion only. */}
                      {tileMeta?.target_approximate === true && typeof stanceTargetPct === "number" ? (
                        <span
                          className="ml-1 align-top text-[11px] font-semibold text-warn cursor-help"
                          title="Yesterday's VIX z-score was unavailable, so the phase was assumed (rising volatility). The engine itself would not size today."
                          data-testid="target-approx-marker"
                        >
                          approx.
                        </span>
                      ) : null}{" "}
                      <span className="opacity-40 font-normal">→</span> {fmtPct(stanceCurrentPct)}
                    </div>
                    <div className="text-[11px] opacity-65 mt-1">
                      {stanceGapNotional == null
                        ? "Gap unavailable"
                        : stanceGapNotional > 1
                        ? `Add ${fmtUSD(stanceGapNotional)} to reach target`
                        : Math.abs(stanceGapNotional) < 1
                        ? "On target"
                        : executableTrimSignal
                        ? levelTrimByDefense
                          ? `Trim signal — confirmed defense trims back to target`
                          : `Trim signal — grind axis / early defense`
                        : trimGateNotConfirmed
                        ? "Over target — target cut, sell gate not confirmed; no trim"
                        : fundedLeapsWeight == null
                        ? "Appreciation drift — funded basis unavailable; no trim"
                        : `Appreciation drift — funded ${fmtPct(fundedLeapsWeight)} ${fundedLeapsWeight > fundedCap ? "above" : "under"} ${fmtPct(fundedCap)} cap; no trim`}
                    </div>
                  </div>

                  <div className="min-w-[190px]" data-stance-cell="playbook">
                    <div className="text-[10px] uppercase tracking-[0.18em] opacity-60">Playbook</div>
                    <div className="mt-1.5 flex flex-wrap items-center gap-2">
                      <StatusPill label={stanceTimingLabel} tone={stancePb.tone} />
                      {/* Live VIX moved to the Live Market Tape (one place for all live values). */}
                    </div>
                    <div className="text-[12px] mt-1.5 leading-5" style={{ color: ui.textSecondary }}>
                      {stancePb.headline}
                    </div>
                    {/* F8: get_leaps_snapshot/__init__.py:893 declares whether it ran WITHOUT the
                        rebalance engine. Rendered only on the positive "light" assertion, so a
                        missing field shows nothing rather than a false claim. Disclosure only —
                        it says where the number came from, not where it comes from. */}
                    {tileMeta?.engine_mode === "light" ? (
                      <div className="text-[10px] mt-1 leading-4 opacity-60">
                        Reduced path: this exposure read comes from the snapshot endpoint, not the rebalance engine.
                      </div>
                    ) : null}
                  </div>

                  {/* ---- SIGNAL CHECKS — the close-basis inputs behind the verdict, inline on the SAME
                       row (no longer a stacked sub-panel). A faint divider separates the verdict trio from
                       its inputs. Signals act on the close only; the sky-colored live reads are situational
                       context and never feed the signals. ---- */}
                  </div>

                  <div className="hidden min-[2200px]:block w-px self-stretch" style={{ backgroundColor: ui.cardBorder }} aria-hidden />

                  <div className="flex flex-wrap items-stretch gap-x-10 gap-y-4 min-[2200px]:contents" data-testid="signals-stance-checks">
                  <div className="min-w-[150px]" data-stance-cell="vix">
                    <div className="text-[10px] uppercase tracking-[0.18em] opacity-60 flex items-center gap-1.5">
                      <span>
                        VIX
                        <InfoHint text="Close VIX drives the regime, z-score, and sizing signals (Helion runs close-to-close). Live VIX is intraday context only and is never fed into the signals." />
                      </span>
                      {tileMeta?.live_vix_used ? (
                        <span
                          className="inline-flex items-center gap-1 text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-live/15 text-live ring-1 ring-live/40 tracking-wider"
                          title="A live intraday VIX is being displayed for context. Regime, z-score, and sizing are computed from the close, not this value."
                        >
                          <span className="relative flex h-1.5 w-1.5">
                            <span className="absolute inline-flex h-full w-full rounded-full bg-live opacity-75 animate-ping"></span>
                            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-live"></span>
                          </span>
                          Live
                        </span>
                      ) : null}
                    </div>
                    {/* Close and live shown at equal weight so the desk can compare them at a glance. */}
                    <div className="mt-1 flex items-end gap-4">
                      <div>
                        <div className="text-[1.5rem] leading-none font-bold" style={{ color: ui.textPrimary }}>
                          {typeof tileMeta?.vix_current === "number" && Number.isFinite(tileMeta.vix_current)
                            ? fmtNum(tileMeta.vix_current, 2)
                            : "—"}
                        </div>
                        <div className="text-[10px] opacity-65 mt-1">
                          close{tileMeta?.vix_date ? ` · ${fmtLocalDate(tileMeta.vix_date)}` : ""} · signal basis
                        </div>
                      </div>
                      {tileMeta?.live_vix_used && typeof tileMeta?.live_vix === "number" && Number.isFinite(tileMeta.live_vix) ? (
                        <div title="Live intraday VIX from IBKR — situational awareness only. Signals are computed from the close, not this value.">
                          <div className="text-[1.5rem] leading-none font-bold text-live">
                            {fmtNum(tileMeta.live_vix, 2)}
                          </div>
                          <div className="text-[10px] opacity-65 mt-1">live now · not for signals</div>
                        </div>
                      ) : null}
                    </div>
                    <div className="text-[10px] opacity-55 mt-1.5" title="When the dashboard last refreshed the VIX/meta payload from the backend.">
                      updated {fmtAge(metaFetchedAtMs != null ? (nowTickMs - metaFetchedAtMs) : null)}
                    </div>
                  </div>

                  <div className="min-w-[150px]" data-stance-cell="zscore">
                    <div className="text-[10px] uppercase tracking-[0.18em] opacity-60 flex items-center gap-1.5">
                      <span>
                        Z-Score (close)
                        <InfoHint text="How extreme the close VIX is vs its own recent history, in standard deviations. This is the exact value the signals act on; higher z = more defensive." />
                      </span>
                      {/* Threshold badge — mirrors the VIX tile's Live badge position. */}
                      {typeof tileMeta?.vix_zscore === "number" && tileMeta.vix_zscore >= 1 ? (
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-down/15 text-down ring-1 ring-down/30 uppercase tracking-wide whitespace-nowrap">
                          Do Not Buy &ge; 1
                        </span>
                      ) : typeof tileMeta?.vix_zscore === "number" && tileMeta.vix_zscore >= 0.75 ? (
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-warn/15 text-warn ring-1 ring-warn/30 uppercase tracking-wide whitespace-nowrap">
                          Buy w/ caution
                        </span>
                      ) : null}
                    </div>
                    {/* Close (signal basis) and live shown side by side at equal weight, mirroring the VIX tile. */}
                    <div className="mt-1 flex items-end gap-4">
                      <div>
                        <div className="text-[1.5rem] leading-none font-bold" style={{ color: ui.textPrimary }}>{fmtNum(tileMeta?.vix_zscore, 2)}</div>
                        <div className="text-[10px] opacity-65 mt-1">close · signal basis</div>
                      </div>
                      {tileMeta?.live_vix_used && typeof tileMeta?.live_vix_zscore === "number" && Number.isFinite(tileMeta.live_vix_zscore) ? (
                        <div title="Intraday z-score implied by the live VIX. Context only — signals act on the close z-score, not this value.">
                          <div className="text-[1.5rem] leading-none font-bold text-live">{fmtNum(tileMeta.live_vix_zscore, 2)}</div>
                          <div className="text-[10px] opacity-65 mt-1">live now · not for signals</div>
                        </div>
                      ) : null}
                    </div>
                    {/* Prior close expressed as a day-over-day delta (not a repeated absolute number). */}
                    {typeof tileMeta?.vix_zscore === "number" && typeof tileMeta?.vix_zscore_yesterday === "number" && Number.isFinite(tileMeta.vix_zscore_yesterday) ? (() => {
                      const dz = (tileMeta.vix_zscore as number) - (tileMeta.vix_zscore_yesterday as number);
                      const flat = Math.abs(dz) < 0.005;
                      const arrow = flat ? "▬" : dz > 0 ? "▲" : "▼";
                      const toneCls = flat ? "" : dz > 0 ? "text-warn" : "text-up";
                      return (
                        <div className={`text-[10px] mt-1.5 ${toneCls}`} style={flat ? smallMutedText : undefined} title="Change in the close z-score vs the prior close. Rising z = more defensive.">
                          {flat ? "unchanged vs prior close" : `${arrow} ${dz > 0 ? "+" : ""}${dz.toFixed(2)} vs prior close`}
                        </div>
                      );
                    })() : null}
                  </div>

                  {/* Defense gate sits beside the Grind axis (CIO, 2026-09-18): the two de-risk
                      triggers read side by side, after the VIX / z-score inputs. */}
                  <div className="min-w-[150px]" data-stance-cell="defense">
                    <div className="text-[10px] uppercase tracking-[0.18em] opacity-60">Defense gate</div>
                    <div className="mt-1.5">
                      {tileMeta ? <DefenseGate m={tileMeta} /> : <div className="text-lg font-medium">—</div>}
                    </div>
                  </div>

                  <div className="min-w-0 basis-[330px] grow-[3] max-w-[520px]" data-stance-cell="grind">
                    <div className="text-[10px] uppercase tracking-[0.18em] opacity-60 flex items-center gap-1.5">
                      <span>
                        Grind axis
                        <InfoHint text="A persistent price downtrend the VIX-spike z-score misses: SPX closing below its 200-day for 15 of the last 20 sessions while the VIX band is still calm. It de-risks patiently and re-arms only on a DURABLE reclaim — 3 closes back above the 200-day by a 2% margin — so a brief bear-market bounce doesn't release the floor early. Advisory and close-basis; it lowers the recommended LEAPS target to a de-risk floor, the identical signal the rebalance engine acts on. The spike axis owns fast crashes; this owns slow bears." />
                      </span>
                      {tileMeta?.grind_axis?.applied ? (
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-down/15 text-down ring-1 ring-down/30 uppercase tracking-wide whitespace-nowrap">
                          De-risk
                        </span>
                      ) : tileMeta?.grind_axis?.active ? (
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-warn/15 text-warn ring-1 ring-warn/30 uppercase tracking-wide whitespace-nowrap">
                          Detected
                        </span>
                      ) : null}
                    </div>
                    <div className="mt-1.5">
                      {tileMeta ? <GrindAxisTile m={tileMeta} /> : <div className="text-lg font-medium">—</div>}
                    </div>
                  </div>

                  {/* Book-level giveback stop — SHADOW (CIO 2026-10-20). Sits beside the two market-read
                      brakes because it is the third de-risk axis, the only one that reads the book's own loss.
                      Display only: the backend computes it, nothing acts on it. */}
                  <div className="min-w-0 basis-[210px] grow max-w-[260px]" data-stance-cell="giveback">
                    <div className="text-[10px] uppercase tracking-[0.18em] opacity-60 flex items-center gap-1.5">
                      <span>
                        Giveback stop
                        <InfoHint text="SHADOW — computed and shown, acting on nothing. The book-level giveback stop: when the fund's flow-adjusted NAV has given back 25% from its running peak, this rule WOULD cap the LEAPS target at the 10% de-risk floor until SPX has closed above its 200-day + 2% for 3 straight sessions; on release the peak resets to the current NAV. Replayed from the whole NAV-of-record history (re-read at most every 10 minutes; no stored state). Research: Zenith AV3 — fired once in earnest in 36 years (Oct 2000 → Apr 2003), once falsely (Jan–Apr 2016, no cost)." />
                      </span>
                    </div>
                    <div className="mt-1.5">
                      {tileMeta ? <GivebackStopTile m={tileMeta} /> : <div className="text-lg font-medium">—</div>}
                    </div>
                  </div>
                  </div>
                </div>
              </div>

              <LiveTapeStrip tape={liveTape} ageMs={liveTapeFetchedAtMs != null ? (nowTickMs - liveTapeFetchedAtMs) : null} streaming={liveTapeStreaming && (nowTickMs - lastLiveTapePushMsRef.current < 15000)} vixLive={tileMeta?.live_vix ?? null} vixClose={tileMeta?.vix_current ?? null} vixLiveUsed={tileMeta?.live_vix_used} />
            </div>
          </div>
        </section>

        {/* Sticky section nav. It is a DIRECT child of the page container on purpose: a
            sticky element only sticks while its containing block is on screen, so nesting
            it inside the command band above (or inside a spacer div of its own height)
            would quietly stop it sticking at all. */}
        <QuickNav items={navItems} className="mb-4" />

        {/* ---- INTRADAY BOOK EXPOSURE — authenticated book/account data, situational & provisional.
             Distinct from BOTH the close-confirmed verdict above AND the official NAV (NAV Fund
             Services). Today's move is the live IBKR day P&L on the reconciled-NAV base; book greeks
             are as of the last position snapshot. Nothing here feeds signals, sizing, or the cap. ---- */}
        <section id="signals-sec-intraday" className="mb-4 scroll-mt-20" style={sectionShellStyle}>
          <div className="flex flex-wrap items-start justify-between gap-3" style={sectionHeaderRowStyle}>
            <div>
              <div className={sectionLabelClass} style={smallMutedText}>Intraday book exposure</div>
              <div className="mt-1 text-sm" style={secondaryText}>
                {intradayFund === "HORIZON"
                  ? "How Horizon’s brokerage book is moving today, layered on the last reconciled NAV — situational and provisional, not an official NAV/return."
                  : `${otherFundName}’s own IBKR account only — never combined with Horizon. Situational and provisional, not an official NAV/return.`}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {/* Fund toggle: THIS section only; each fund's own figures, never the combined book. */}
              <div
                role="group"
                aria-label="Fund shown in Intraday Book Exposure"
                data-testid="intraday-fund-toggle"
                className="inline-flex overflow-hidden rounded-full"
                style={{ border: `1px solid ${ui.cardBorder}` }}
              >
                {INTRADAY_FUNDS.map((f) => {
                  const on = intradayFund === f.code;
                  return (
                    <button
                      key={f.code}
                      type="button"
                      aria-pressed={on}
                      data-fund={f.code}
                      onClick={() => setIntradayFund(f.code)}
                      className="px-3 py-1 text-[11px] font-semibold uppercase tracking-wide"
                      style={{
                        background: on ? "var(--status-info-bg-strong)" : "transparent",
                        color: on ? "var(--status-info-text)" : ui.textSecondary,
                      }}
                    >
                      {f.label}
                    </button>
                  );
                })}
              </div>
              <StatusPill label="Provisional · situational" tone="slate" />
              {intradayFund === "HORIZON" ? (
                <>
                  {pnlStreaming ? <LiveBadge title="Streaming live via SignalR — one shared server-side subscription" /> : null}
                  {pnlHeldSince != null && intradayPnl != null ? (
                    <StatusPill label="Held · fill capture reading IBKR" tone="slate" />
                  ) : null}
                  {horizonSourcePill ? <StatusPill label={horizonSourcePill.label} tone={horizonSourcePill.tone} /> : null}
                  {pnlFeedEmpty ? (
                    <StatusPill label="P&L feed idle · after-hours" tone="slate" />
                  ) : pnlUnavailable ? (
                    <StatusPill label="Live P&L unavailable" tone="amber" />
                  ) : pnlPartial ? (
                    <StatusPill label={`Partial book · ${pnlAccounts ?? "?"}/${pnlDiscovered ?? "?"} accounts · undercount`} tone="amber" />
                  ) : null}
                </>
              ) : otherTiles?.pill ? (
                <StatusPill label={otherTiles.pill.label} tone={otherTiles.pill.tone} />
              ) : null}
            </div>
          </div>

          {/* Half/half split (CIO 2026-09-01): the three existing sub-blocks stack in the
              left column UNCHANGED; the NAV chart (with its range picker) takes the right
              half. The left column is the taller of the two, so the section's height is
              still governed by the tiles — the chart rides inside existing space. */}
          {intradayFund === "HORIZON" ? (
          <>
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2" data-testid="intraday-horizon-view">
            <div>
          {/* Detect-and-ask: renders ONLY when the flow ledger needs a confirmation
              (month attestation, probable wire, probable fee wire). Confirming writes
              the attested row; the return tile below self-heals within ~5 minutes. */}
          <LedgerConfirmCard className="mb-3" />
          <div
            className="text-[10px] uppercase tracking-[0.14em] font-semibold mb-2"
            data-testid="todays-move-label"
            data-held={horizonRowHeld ? "true" : "false"}
            style={horizonRowHeld ? { color: tonePalette.slate.text } : smallMutedText}
          >
            {horizonRowHeld ? "Today’s move · held" : "Today’s move · live"}
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="rounded-xl px-3 py-2.5" style={metricTileStyle}>
              <div className="text-[10px] uppercase tracking-[0.14em] font-semibold" style={smallMutedText}>Day P&L (brokerage)</div>
              <div
                className="mt-1 text-[1.45rem] leading-none font-semibold"
                style={{ color: dayPnl == null ? ui.textMuted : dayPnl >= 0 ? "var(--status-positive-text)" : "var(--status-danger-text)" }}
              >
                {dayPnl == null ? "—" : `${dayPnl >= 0 ? "+" : "−"}${fmtUSD(Math.abs(dayPnl))}`}
              </div>
              <div className="text-[11px] mt-1" style={{ color: ui.textSecondary }}>
                {dayPnlPctNav == null
                  ? pnlFeedEmpty
                    ? "feed idle · after-hours"
                    : pnlUnavailable
                    ? "live P&L unavailable"
                    : "—"
                  : `${fmtSignedPct(dayPnlPctNav, 2)} since prior close · ${horizonDaySourceLabel ?? "LEAPS + FI"}`}
              </div>
              {dayPnlPctNav == null && dayPnl != null && horizonDaySourceLabel ? (
                <div className="text-[11px] mt-0.5" style={{ color: ui.textSecondary }}>{horizonDaySourceLabel}</div>
              ) : null}
              {dayPnl != null && horizonAsOf ? (
                <div
                  className="text-[11px] mt-0.5 font-semibold"
                  data-testid="day-pnl-asof"
                  data-stale={horizonAsOf.stale ? "true" : "false"}
                  style={{ color: horizonAsOf.stale ? "var(--status-warning-text)" : ui.textSecondary }}
                >
                  {horizonAsOf.text}
                </div>
              ) : dayPnl != null && horizonPnlSource === "ibkr_pnl" ? (
                // Reserved slot under IBKR's own figure: the held pill can come and go read to read,
                // so its space is always kept and the tiles never jump.
                <div className="mt-1 flex min-h-[20px] items-center" data-testid="day-pnl-held-slot">
                  {horizonHeld && horizonHeldText ? (
                    <span
                      data-testid="day-pnl-held"
                      title={heldTooltip(horizonHeld, horizonFundLabel)}
                      className="inline-flex items-center rounded-full px-2 py-0.5 text-[10.5px] font-semibold leading-none"
                      style={{
                        backgroundColor: tonePalette.slate.chipBg,
                        color: tonePalette.slate.chipText,
                        border: `1px solid ${tonePalette.slate.border}`,
                      }}
                    >
                      {horizonHeldText}
                    </span>
                  ) : null}
                </div>
              ) : null}
            </div>

            <div className="rounded-xl px-3 py-2.5" style={metricTileStyle}>
              <div className="text-[10px] uppercase tracking-[0.14em] font-semibold" style={smallMutedText}>Intraday NAV estimate</div>
              <div className="mt-1 text-[1.45rem] leading-none font-semibold" style={{ color: ui.textPrimary }}>
                {intradayNavEst == null ? "—" : fmtUSD(intradayNavEst)}
              </div>
              <div className="text-[11px] mt-1" data-testid="nav-estimate-sub" style={{ color: ui.textSecondary }}>
                {/* Label the base honestly — only "reconciled" when the overlay is actually reliable;
                    otherwise say broker-book / pending, matching the Total NAV tile below. The base
                    quoted here is the PRIOR-CLOSE NAV, so "<base> + today's P&L" reads as the exact
                    arithmetic of the figure above it in BOTH basis cases (live or stored) — the
                    branch is handled upstream in priorCloseNav, not in the wording. */}
                {reconNav == null
                  ? "base NAV unavailable"
                  : dayPnl == null
                  ? `${navDaily == null ? "broker book" : "reconciled"} ${fmtUSD(reconNav)} · today’s P&L not loaded`
                  : navDaily == null
                  ? `broker book ${fmtUSD(priorCloseNav ?? reconNav)} + today’s P&L (bank & accruals not loaded)`
                  : navDaily.overlay_reliable === false
                  ? `broker book + overlay ${fmtUSD(priorCloseNav ?? reconNav)} + today’s P&L (bank & accruals pending close)`
                  : `prior close ${fmtUSD(priorCloseNav ?? reconNav)} + today’s P&L`}
                {/* prior close + the HELD day P&L: held too. */}
                {reconNav != null && dayPnl != null && horizonRowHeld ? " · held" : null}
              </div>
            </div>

            <div className="rounded-xl px-3 py-2.5" style={metricTileStyle}>
              <div className="text-[10px] uppercase tracking-[0.14em] font-semibold" style={smallMutedText}>Open unrealized P&L</div>
              <div className="mt-1 text-[1.45rem] leading-none font-semibold" style={{ color: ui.textPrimary }}>
                {typeof horizonPnl?.unrealized_pnl === "number" ? fmtUSD(horizonPnl.unrealized_pnl) : "—"}
              </div>
              <div className="text-[11px] mt-1" data-testid="unrealized-sub" style={{ color: ui.textSecondary }}>
                {horizonPnl == null ? "—" : unrealizedSubLabel(horizonPnl, "Horizon")}
                {typeof horizonPnl?.unrealized_pnl === "number" && horizonRowHeld ? " · held" : null}
              </div>
            </div>
          </div>

          <div className="text-[10px] uppercase tracking-[0.14em] font-semibold mt-4 mb-2" style={smallMutedText}>
            Book exposure · live
          </div>
          <BookExposureGreeks
            data={{
              deltaDollarPer1pct:
                liveBookGreeks?.cockpit?.net_delta != null && liveBookGreeks?.cockpit?.spot_level != null
                  ? liveBookGreeks.cockpit.net_delta * liveBookGreeks.cockpit.spot_level * 0.01
                  : null,
              vegaPerPoint: liveBookGreeks?.cockpit?.net_vega_per_pt ?? null,
              thetaPerDay: liveBookGreeks?.cockpit?.net_theta_per_day ?? null,
            }}
            asOf={liveBookGreeks?.as_of}
            /* Was a bare `live` (i.e. always true), so this footnote asserted "Live book
               greeks · as of 06:52" over close-basis numbers every pre-open morning. The
               component's honesty switch existed and no caller had ever used it. Client
               clock AND the backend's holiday-aware flag must both agree it is open. */
            live={isMarketOpenAt(nowTickMs) && liveTape?.market_open !== false}
          />

          {/* PERIOD, NOT TODAY. Deliberately given its own header and placed below the
              live block: this figure measures the whole span since the administrator's
              strike and — when nav_basis_includes_today is true — ALREADY contains the
              day P&L shown above. Reading the two as additive is the double-count fixed
              2026-08-06, so they are never presented as a single row. */}
          <div className="text-[10px] uppercase tracking-[0.14em] font-semibold mt-4 mb-2" style={smallMutedText}>
            Since the official strike · estimated (period, not today’s move)
          </div>
          <PeriodReturnTile reconciliation={(nav as any)?.reconciliation ?? null} />
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-[0.14em] font-semibold mb-2" style={smallMutedText}>
                NAV — session &amp; ranges
              </div>
              {/* Same component pair as the Portfolio page — one fetch/honesty implementation,
                  two mounts, so the two pages can never disagree. Refreshes once per minute
                  (the mark cadence) while the page is open; range views on demand. */}
              <NavChartPanel refreshSignal={Math.floor(nowTickMs / 60_000)} />
            </div>
          </div>

          <div className="mt-3 text-xs" style={smallMutedText}>
            Today’s move is the live IBKR brokerage day P&L (LEAPS + FI sleeves, both custodied at IBKR); off-IBKR bank cash and accruals
            are held flat inside the reconciled base. Book greeks are LIVE — the same single source (risk/long_end_vol) as the Risk
            dashboard’s Long-End Vol & Vega card, so the two never disagree. Situational only — nothing here feeds signals, sizing, or the
            40% cap, and it is not an official NAV/return (NAV Fund Services is the official source).
            {horizonPnlSource === "nav_change_estimate"
              ? " Right now IBKR is not splitting P&L by account, so Horizon’s Day P&L is " + ESTIMATE_LABEL + "."
              : null}
          </div>
          </>
          ) : (
          <>
          {/* Another fund (Meridian): ONLY that fund's own reads. No Horizon figure, greek, NAV
              base, ledger card or chart is shown under its name. */}
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2" data-testid="intraday-other-fund-view" data-fund={intradayFund}>
            <div>
              <div className="text-[10px] uppercase tracking-[0.14em] font-semibold mb-2" style={smallMutedText}>
                Today’s move · {otherFundName}
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div className="rounded-xl px-3 py-2.5" style={metricTileStyle}>
                  <div className="text-[10px] uppercase tracking-[0.14em] font-semibold" style={smallMutedText}>Day P&L (brokerage)</div>
                  <div
                    className="mt-1 text-[1.45rem] leading-none font-semibold"
                    data-testid="other-fund-day-pnl"
                    style={{
                      color:
                        otherTiles?.dayPnl == null
                          ? ui.textMuted
                          : otherTiles.dayPnl >= 0
                          ? "var(--status-positive-text)"
                          : "var(--status-danger-text)",
                    }}
                  >
                    {otherTiles?.dayPnl == null
                      ? otherTiles?.dayPnlText ?? "—"
                      : `${otherTiles.dayPnl >= 0 ? "+" : "−"}${fmtUSD(Math.abs(otherTiles.dayPnl))}`}
                  </div>
                  <div className="text-[11px] mt-1" style={{ color: ui.textSecondary }}>{otherTiles?.dayPnlSub ?? "—"}</div>
                  {otherTiles?.asOf ? (
                    <div
                      className="text-[11px] mt-0.5 font-semibold"
                      data-testid="day-pnl-asof"
                      data-stale={otherTiles.asOf.stale ? "true" : "false"}
                      style={{ color: otherTiles.asOf.stale ? "var(--status-warning-text)" : ui.textSecondary }}
                    >
                      {otherTiles.asOf.text}
                    </div>
                  ) : null}
                </div>

                <div className="rounded-xl px-3 py-2.5" style={metricTileStyle}>
                  <div className="text-[10px] uppercase tracking-[0.14em] font-semibold" style={smallMutedText}>
                    {otherTiles?.valueTitle ?? "Live IBKR value"}
                  </div>
                  <div className="mt-1 text-[1.45rem] leading-none font-semibold" data-testid="other-fund-value" style={{ color: ui.textPrimary }}>
                    {otherTiles?.value == null ? "—" : fmtUSD(otherTiles.value)}
                  </div>
                  <div className="text-[11px] mt-1" style={{ color: ui.textSecondary }}>{otherTiles?.valueSub ?? "—"}</div>
                </div>

                <div className="rounded-xl px-3 py-2.5" style={metricTileStyle}>
                  <div className="text-[10px] uppercase tracking-[0.14em] font-semibold" style={smallMutedText}>Open unrealized P&L</div>
                  <div className="mt-1 text-[1.45rem] leading-none font-semibold" style={{ color: ui.textPrimary }}>
                    {otherTiles?.unrealized == null ? "—" : fmtUSD(otherTiles.unrealized)}
                  </div>
                  <div className="text-[11px] mt-1" style={{ color: ui.textSecondary }}>{otherTiles?.unrealizedSub ?? "—"}</div>
                </div>
              </div>

              <div className="text-[10px] uppercase tracking-[0.14em] font-semibold mt-4 mb-2" style={smallMutedText}>
                Book exposure
              </div>
              <div className="rounded-xl px-3 py-2.5 text-sm" style={{ ...metricTileStyle, color: ui.textSecondary }}>
                {otherFundNotFunded
                  ? `No positions — ${otherFundName} is not funded. Book greeks are shown for Horizon only.`
                  : `Book greeks are Horizon’s only until per-fund positions (M7); none are shown for ${otherFundName}.`}
              </div>

              <div className="text-[10px] uppercase tracking-[0.14em] font-semibold mt-4 mb-2" style={smallMutedText}>
                Since the official strike
              </div>
              <div className="rounded-xl px-3 py-2.5 text-sm" style={{ ...metricTileStyle, color: ui.textSecondary }}>
                Administrator not connected for {otherFundName} — no official NAV or period return yet.
              </div>
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-[0.14em] font-semibold mb-2" style={smallMutedText}>
                NAV — session &amp; ranges
              </div>
              <div className="rounded-xl px-3 py-6 text-sm text-center" style={{ ...metricTileStyle, color: ui.textSecondary }}>
                {otherFundNotFunded
                  ? "Not funded: no marks recorded."
                  : `No NAV marks are recorded for ${otherFundName} yet (per-fund NAV arrives with M7).`}
              </div>
            </div>
          </div>

          <div className="mt-3 text-xs" style={smallMutedText}>
            {otherFundName}’s figures come only from its own IBKR account (read without switching the broker session’s selected
            account) and are never combined with Horizon’s. {otherFundNotFunded
              ? "While it is not funded, its live IBKR value is shown for reference only; nothing is recorded for it."
              : "When IBKR does not split P&L by account, its Day P&L is " + ESTIMATE_LABEL + "."}{" "}
            Situational only — not an official NAV/return.
          </div>
          </>
          )}
        </section>

        {/* ---- SPX PRIORITY DEPLOYMENT + TODAY'S PLAYBOOK — moved to sit directly below Intraday
             Book Exposure (was inside the command band above the live tape). Close-confirmed
             deployment throttle; only its position changed, none of its logic. ---- */}
        <section id="signals-sec-deployment" className="mb-4 scroll-mt-20 grid grid-cols-1 gap-3.5 xl:grid-cols-[minmax(0,1.42fr)_380px]">
          {/* Deployment track is measured on the basis the engine enforces: the FUNDED (cost-basis)
              weight against caps.funded_max from the payload — the same two values the "Funded LEAPS
              basis" tile below uses, so the two can never quote different caps. */}
          <SpxPriorityCard
            signal={(displayMeta as any)?.rerisk_signal ?? null}
            fundedWeight={fundedLeapsWeight}
            fundedCap={fundedCap}
            fundedCost={fundedLeapsCostShown}
            lockout={harvestLockout}
          />
          <PlaybookCard {...playbookArgs} />
        </section>

        {/* POSITIONING + CAPITAL RECYCLING — one section (CIO, 2026-09-02). Harvest used to
            be its own full-width band directly above; the two now share a row, because the
            positioning row had a third of a column of dead space and harvest is the same
            decision seen from the other side (what the book holds vs what it can recycle).
            The harvest DECISION — active lockouts, candidates, Record TWS Trim — lives in
            that same third column, under the summary tiles (CIO, later on 2026-09-02): the
            page is a once-a-day view and the full-width "Harvest decisions" band it used to
            occupy was mostly prose on a quiet day.
            RESTACKED 2026-09-18 (CIO): three-up left Positioning and Convexity ending far above a
            much taller Harvest card. From 2xl the row is now two halves — Positioning stacked
            over Convexity on the left, Harvest on the right — so the columns come out about the
            same height. Below 2xl nothing changed: Positioning | Convexity side by side, Harvest
            full width underneath. The tiles inside go 4-across / 3-across only from 2200px,
            where half the page is wide enough for them. */}
        <section id="signals-sec-positioning" className="mb-4 scroll-mt-20">
            {/* items-start, not the default stretch: the panels hold different numbers of
                tiles, and stretching them to a common height just moved the dead space inside the
                shorter cards instead of removing it. */}
            <div className="grid grid-cols-12 items-start gap-4">
              <div className="col-span-12 2xl:col-span-6 grid grid-cols-12 items-start gap-4">
              <div className="col-span-12 lg:col-span-6 2xl:col-span-12">
                <SectionShell
                  title="Positioning vs target"
                  meta={<>Live composition vs the close-based target; sell only when policy requires it</>}
                >
                {/* Four tiles, one home per number (CIO, 2026-09-18). Target Allocation, Current
                    LEAPS % and the Action verdict restated the stance band's "LEAPS target → now";
                    Total NAV restated Intraday Book Exposure; Deployment Status was Cash Reserve
                    as a percentage. What is left is only what this card says and nothing else does. */}
                <div className="grid grid-cols-2 min-[2200px]:grid-cols-4 gap-4">
                  {/* DRIFT VS TARGET. Target and current weight live in the stance band; this tile
                      says how far apart they are, against which band, and whether the distance
                      means anything. The dollar figure appears ONLY on a real trim (regime target
                      drop / grind axis) and is the Playbook's own figure. During appreciation drift
                      (current − target) × NAV is not an amount anyone should sell, and as the
                      headline beside "not a sell signal" it read as one. */}
                  <div className={cardBaseClass} style={themeCardStyle}>
                    <p className={MICRO_LABEL} style={smallMutedText}>Drift vs target<InfoHint text="How far the market-value LEAPS weight sits from the regime target, measured against the rebalance tolerance band. The target and current weight themselves are in the stance band at the top of the page. Sizing percentages on this page use the IBKR broker book as their denominator, as the rebalance engine does, not the larger reconciled NAV shown in Intraday Book Exposure." /></p>
                    {(() => {
                      const st = allocAction.state;
                      const isMarketValueDrift = st === "TRIM" && !executableTrimSignal;
                      const isRealTrim = st === "TRIM" && executableTrimSignal;
                      const tone = isMarketValueDrift
                        ? "bg-warn/15 text-warn ring-1 ring-warn/30"
                        : st === "TRIM"
                        ? "bg-down/15 text-down ring-1 ring-down/30"
                        : st === "ADD"
                        ? "bg-live/15 text-live ring-1 ring-live/30"
                        : "bg-up/15 text-up ring-1 ring-up/30";
                      const headline = isMarketValueDrift ? "Over Target" : st === "TRIM" ? "Trim" : st === "ADD" ? "Add" : "Hold";
                      const actionReason = isMarketValueDrift && trimGateNotConfirmed
                        ? "Target cut, sell gate not confirmed - no trim"
                        : isMarketValueDrift
                        ? "Appreciation drift - not a sell signal"
                        : isRealTrim
                        ? levelTrimByDefense
                          ? "Confirmed defense: trim back to target"
                          : "Grind axis / early defense"
                        : allocAction.reason;
                      const pts = allocAction.overPctPts;
                      const hasPts = typeof pts === "number" && Number.isFinite(pts);
                      return (
                        <div>
                          {/* nowrap chip + wrapping row: in the 4-across layout the tile is ~250px
                              and the chip used to break onto two lines ("OVER / TARGET"). */}
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                            <span className={`text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wide whitespace-nowrap ${tone}`}>{headline}</span>
                            <span className="text-xs" style={smallMutedText}>{actionReason}</span>
                          </div>
                          <div className="mt-1 flex items-baseline gap-1.5">
                            <span className="text-lg font-medium" style={{ color: ui.textPrimary }}>
                              {hasPts ? `${pts > 0 ? "+" : ""}${pts.toFixed(1)} pts` : "—"}
                            </span>
                            {hasPts ? (
                              <span className="text-xs" style={smallMutedText}>
                                {pts > 0 ? "above target" : pts < 0 ? "below target" : "on target"}
                              </span>
                            ) : null}
                          </div>
                          <div className="text-xs" style={secondaryText}>
                            Tolerance band: {formatAllocationTolerance(effectiveLeapsTargetPct, displayMeta?.rebalance_tolerance_pct ?? null)}
                          </div>
                          {isRealTrim ? (
                            <div className="text-xs mt-0.5" style={secondaryText}>
                              Trim to target: <span className="font-semibold" style={{ color: ui.textPrimary }}>
                                {trimToTargetNotional != null ? fmtUSD(trimToTargetNotional) : "—"}
                              </span>
                            </div>
                          ) : null}
                          {isMarketValueDrift ? (
                            <div className="mt-1 text-[10px] leading-snug" style={smallMutedText}>
                              No cap on options performance: the 40% funded/cost basis limits what we buy, not what we hold. Only a confirmed defense (any band) or the grind axis trims.
                            </div>
                          ) : null}
                        </div>
                      );
                    })()}
                  </div>

                  <div className={cardBaseClass} style={themeCardStyle}>
                    <p className={MICRO_LABEL} style={smallMutedText}>Regime Pressure<InfoHint text="0–100 composite of how much the current regime is pushing toward defense (z-score plus term-structure, downtrend, and defense confirmations). Higher = more pressure to de-risk." /></p>
                    {(() => {
                      const score = pressure.score;
                      const sev = pressure.severity;
                      const badgeTone = sev === "Extreme" ? "bg-down/15 text-down ring-1 ring-down/30" : sev === "Elevated" ? "bg-elevated/15 text-elevated ring-1 ring-elevated/30" : sev === "Watch" ? "bg-warn/15 text-warn ring-1 ring-warn/30" : "bg-up/15 text-up ring-1 ring-up/30";
                      const pctW = score == null ? 0 : clamp(score, 0, 100);
                      const tip = pressure.reasons.length ? pressure.reasons.join(" • ") : "No additional confirmations";
                      return (
                        <div title={tip}>
                          <div className="flex items-center justify-between">
                            <div className="text-lg font-medium" style={{ color: ui.textPrimary }}>{score == null ? "—" : `${Math.round(score)}`}</div>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wide ${badgeTone}`}>{sev}</span>
                          </div>
                          <div
                            className="mt-2 w-full rounded h-2 overflow-hidden"
                            style={barTrackStyle(true)}
                          >
                            <div
                              className="h-2 rounded"
                              style={{ width: `${pctW}%`, ...severityBarFillStyle(sev) }}
                            />
                          </div>
                          <div className="mt-1 text-[10px] truncate" style={smallMutedText}>
                            {tip}
                          </div>
                        </div>
                      );
                    })()}
                  </div>

                  <div className={cardBaseClass} style={themeCardStyle}>
                    <p className={MICRO_LABEL} style={smallMutedText}>Funded LEAPS basis</p>
                    <p className="text-lg font-medium" style={{ color: fundedCapBreached ? "var(--status-danger-text)" : ui.textPrimary }}>
                      {fundedLeapsWeight != null ? fmtPct(fundedLeapsWeight) : "—"}
                    </p>
                    <div className="text-xs mt-1" style={smallMutedText}>
                      Cap {fmtPct(fundedCap)} · cost basis, not winner market value
                    </div>
                  </div>

                  {/* CASH & DEPLOYABILITY — Cash Reserve and Deployment Status merged. Deployment
                      Status was (NAV − reserved cash) / NAV: this cash figure restated as a
                      percentage, under a name that collided with the SPX card's funded
                      "Deployment track". The share of NAV is now taken from the SAME dollar figure
                      shown above it, so the two cannot disagree. The headline under it is the
                      shared lockout derivation, unchanged. */}
                  <div className={cardBaseClass} style={themeCardStyle}>
                    <p className={MICRO_LABEL} style={smallMutedText}>Cash &amp; deployability</p>
                    <p className="text-lg font-medium" style={{ color: ui.textPrimary }}>{fmtUSD(cashShown)}</p>
                    <div className="text-xs mt-1" style={secondaryText}>
                      {cashPctNav != null ? `${fmtPct(cashPctNav)} of NAV · ` : ""}{deploymentLabel}
                    </div>
                    <div className="text-[10px] mt-0.5" style={smallMutedText}>
                      {cashOverrideState.enabled ? "Discretionary reserve on" : "Observed broker cash"}
                      {nav?.source ? ` • ${String(nav.source).toUpperCase()}` : ""}
                    </div>
                  </div>

                  {/* Convexity Snapshot block removed — its dollar greeks duplicate Intraday Book
                      Exposure; its unique %-of-NAV reads (Δ NAV for +1% SPX, theta bps/day, Δ/Vega)
                      moved to the "Book Sensitivity" tile in Convexity & Risk. */}
                </div>
                </SectionShell>
              </div>

              <div className="col-span-12 lg:col-span-6 2xl:col-span-12">
                <SectionShell
                  title="Convexity & risk"
                  meta={<>Vega, carry & efficiency diagnostics</>}
                >
                {/* Three tiles. Data age went (CIO, 2026-09-18): its "signal meta" age is the same
                    fetch clock as the stance band's VIX "updated", and its risk-snapshot time is the
                    "Last Risk Snapshot" above the holdings table. The Group-by-expiry switch that
                    was once a tile here lives in that table's filter row. Below 2200px Book
                    Sensitivity takes the full width with its four figures two-up, and the VPD pair
                    shares the row beneath — the efficiency reads beside its own yardstick. */}
                <div className="grid grid-cols-2 min-[2200px]:grid-cols-3 gap-4">
                  <div className={`${cardBaseClass} col-span-2 min-[2200px]:col-span-1`} style={themeCardStyle}>
                    <p className={MICRO_LABEL} style={smallMutedText}>Book Sensitivity<InfoHint text="Whole-book LEAPS greeks as a share of NAV, on the CLOSE-CONFIRMED position snapshot (sizing framing — stable, not jittery intraday), and unaffected by the holdings table's filters. The dollar versions — Δ$ per 1% SPX, net vega $, net theta $ — are in Intraday Book Exposure at the top of the page, but those are LIVE greeks, so the two can differ intraday by design: live for the intraday move, close-confirmed here for sizing. Vega is a measurement, not a verdict: there is no authored vega budget to compare it against (the regime band once shown beside it was an unsourced UI constant, removed 2026-07-31)." /></p>
                    <div className="grid grid-cols-2 gap-x-8 gap-y-2 min-[2200px]:grid-cols-1">
                      <div className="flex items-center justify-between text-sm">
                        <span style={secondaryText}>Δ NAV for +1% SPX</span>
                        <span className="font-medium" style={{ color: ui.textPrimary }}>
                          {convexity.deltaPctNavPer1pct != null ? `${convexity.deltaPctNavPer1pct.toFixed(2)}%` : "—"}
                        </span>
                      </div>
                      {/* Was the standalone Vega / NAV tile. It rendered `vegaBps || 0`, so a
                          missing figure showed "0.0 bps"; it now shows "—". */}
                      <div className="flex items-center justify-between text-sm" title="Net vega per +1 vol point, as bps of NAV">
                        <span style={secondaryText}>Vega per +1 vol</span>
                        <span className="font-medium" style={{ color: ui.textPrimary }}>
                          {convexity.vegaBps != null ? fmtBps(convexity.vegaBps) : "—"}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm" title="Theta as bps/day on NAV">
                        <span style={secondaryText}>Theta bps/day</span>
                        <span className="font-medium" style={{ color: ui.textPrimary }}>
                          {convexity.thetaBpsPerDay != null ? fmtBps(convexity.thetaBpsPerDay) : "—"}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-[11px]" style={smallMutedText} title="Quick sanity ratio: Delta $ for +1% SPX divided by Vega $ per 1 vol pt">
                        <span>Δ/Vega (1% vs 1 vol)</span>
                        <span className="font-medium" style={{ color: ui.textSecondary }}>
                          {convexity.deltaToVega != null ? convexity.deltaToVega.toFixed(2) : "—"}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className={cardBaseClass} style={themeCardStyle}>
                    <p className={MICRO_LABEL} style={smallMutedText}>Median VPD Efficiency<InfoHint text="A seasoning gauge, not a recycling screen. VPD = vega per premium dollar (how much vol exposure each dollar buys). Efficiency = each position's VPD ÷ a fresh ATM LEAPS at its OWN tenor (per-tenor curve off today's surface), so a 5yr rung is judged against fresh 5yr convexity, not a 2yr yardstick — the reading reflects MONEYNESS, not duration. Read it as roughly the share of premium still held as time value. Bands: ≥85% fresh-equivalent, 70–85% lightly seasoned, 50–70% seasoned, <50% deep ITM. A low number means the line has gone deep in-the-money — normally a winner, and a position that has de-levered itself — not a defect. Rotating into fresher convexity at constant dollars is a LEVERAGE decision with adverse evidence behind it (Study P10; the failed immediate-redeploy test), not routine maintenance." /></p>
                    <div className="text-lg font-medium" style={{ color: ui.textPrimary }}>
                      {medianEfficiency != null ? `${(medianEfficiency * 100).toFixed(0)}%` : "—"}
                    </div>
                    <div className="text-xs mt-1" style={smallMutedText}>
                      {benchmark.source === "market"
                        ? (benchmark.perTenor ? "vs. fresh market benchmark (per-tenor)" : "vs. fresh market benchmark")
                        : benchmark.source == null
                          ? "benchmark unavailable"
                          : "vs. in-book benchmark (market unavailable)"}
                      {benchmark.extrapolatedCount ? (
                        <span className="ml-1 text-warn" title={
                          `${benchmark.extrapolatedCount} position(s) sit beyond the deepest tenor the surface actually covers` +
                          (benchmark.deepestNodeDte ? ` (~${Math.round(benchmark.deepestNodeDte)} DTE)` : "") +
                          `. Past that point the benchmark curve is held FLAT, so those efficiencies are measured against an ` +
                          `EXTRAPOLATION, not a quote. ORATS coverage thins to a handful of strikes at the long end.`
                        }>
                          · {benchmark.extrapolatedCount} extrapolated
                        </span>
                      ) : null}
                    </div>
                  </div>

                  <div className={cardBaseClass} style={themeCardStyle}>
                    <p className={MICRO_LABEL} style={smallMutedText}>Fresh VPD Benchmark<InfoHint text="The vega-per-premium-dollar of a fresh ATM index LEAPS priced off today's ORATS surface. It's a per-tenor curve: each held position is measured against fresh convexity at its OWN maturity (out through the thin 4–5.5yr long end). The value shown is the 24–30m headline point. Falls back to an in-book benchmark (the freshest DTE quartile actually held) if the surface can't support it." /></p>
                    <div className="text-lg font-medium" style={{ color: ui.textPrimary }}>
                      {freshVpd != null ? freshVpd.toFixed(5) : "—"}
                    </div>
                    <div className="text-xs mt-1" style={smallMutedText}>
                      {benchmark.source === "market" ? (
                        <>
                          {benchmark.perTenor
                            ? `per-tenor curve · ${benchmark.nodeCount} node${benchmark.nodeCount === 1 ? "" : "s"}`
                            : `fresh ATM${benchmark.tenorDays ? ` ~${Math.round(benchmark.tenorDays / 30.4)}m` : ""}`}
                          {benchmark.atmIv != null ? ` · 24–30m IV ${(benchmark.atmIv * 100).toFixed(1)}%` : ""}
                          {benchmark.asOf ? ` · ${benchmark.asOf}` : ""}
                          {benchmark.stale ? <span className="text-warn"> · stale surface</span> : null}
                        </>
                      ) : benchmark.source == null ? (
                        "—"
                      ) : (
                        `in-book fallback${benchmark.reason ? ` (${benchmark.reason})` : ""}`
                      )}
                    </div>
                  </div>
                </div>
                </SectionShell>
              </div>

              {/* Distinguish endpoint UNAVAILABLE (neutral) from a genuine no-lockout ALL-CLEAR
                  (green): a failed harvest fetch collapses to homeRun=null and must NOT read as
                  a healthy $0. */}
              </div>

              <div id="signals-harvest" className="col-span-12 2xl:col-span-6 scroll-mt-20">
                <SectionShell
                  title="Home-run capital recycling"
                  meta={
                    <StatusPill
                      label={
                        !harvestLockout.known
                          ? "Harvest data unavailable"
                          : harvestLockout.status === "none"
                          ? "No active lockout"
                          : "Lockout active"
                      }
                      tone={!harvestLockout.known ? "slate" : harvestLockout.status === "none" ? "green" : "amber"}
                    />
                  }
                >
                {/* Tiles carry this row's card chrome, not the gradient metric tile they used as a
                    standalone band — three panels side by side have to read as one row. Figures and
                    their guards are unchanged.
                    Two distinct failures, both of which used to render as a confident $0:
                    homeRun === null is the fetch failing, and a MISSING policy block is
                    portfolio_view's {policy:{}, summary:{lockedNotional:0}} fallback after an
                    exception inside the harvest builder. A boolean `enabled` is the marker that the
                    policy actually loaded. */}
                {/* QUIET DAY = ONE LINE (CIO, 2026-09-18). With nothing under lock the four tiles
                    read $0 / 0 / 0 / — and cost ~180px, and the 2x count repeats the candidate
                    list directly below. Only a CONFIRMED all-clear collapses: a live lock, a lock
                    not applied to the budget, or an unknown read (fetch failed, policy missing,
                    stale) still gets the full tiles with their honest "—". */}
                {harvestLockout.known && harvestLockout.status === "none" ? (
                  <div className="rounded-xl px-3 py-2 text-[12px]" style={{ ...themeCardStyle, color: ui.textSecondary }}>
                    Nothing under a redeployment lockout —{" "}
                    <span className="font-semibold" style={{ color: ui.textPrimary }}>{fmtUSD(harvestLockout.rawLockedNotional)}</span>{" "}
                    withheld from the buy budget ·{" "}
                    <span className="font-semibold" style={{ color: ui.textPrimary }}>{harvestLockout.activeLockCount ?? 0}</span>{" "}
                    active 60-day clocks
                  </div>
                ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {/* These two now read from the SHARED derivation rather than their own
                      inline guard, so this card and the deployment track above cannot say
                      different things about the same locks. The guard is strictly stronger
                      than the one it replaces (it also requires apply_to_buy_budget, a
                      numeric lockedNotional, and a read that is not stale). */}
                  <div className={cardBaseClass} style={themeCardStyle}>
                    <p className={MICRO_LABEL} style={smallMutedText}>Locked notional</p>
                    <p className="text-lg font-medium" style={{ color: ui.textPrimary }}>
                      {harvestLockout.known ? fmtUSD(harvestLockout.rawLockedNotional) : "—"}
                    </p>
                    <div className="text-[10px] mt-1" style={smallMutedText}>
                      {harvestLockout.status === "locked_not_applied"
                        ? "Under lock - but NOT withheld from the buy budget"
                        : "Capital under lock - not deployable"}
                    </div>
                  </div>

                  <div className={cardBaseClass} style={themeCardStyle}>
                    <p className={MICRO_LABEL} style={smallMutedText}>Active clocks</p>
                    <p className="text-lg font-medium" style={{ color: ui.textPrimary }}>
                      {harvestLockout.known ? harvestLockout.activeLockCount ?? 0 : "—"}
                    </p>
                    <div className="text-[10px] mt-1" style={smallMutedText}>60-calendar-day redeployment lockouts</div>
                  </div>

                  <div className={cardBaseClass} style={themeCardStyle}>
                    <p className={MICRO_LABEL} style={smallMutedText}>2× candidates</p>
                    <p className="text-lg font-medium" style={{ color: ui.textPrimary }}>
                      {homeRun == null ? "—" : homeRun.summary?.candidateCount ?? homeRun.candidates?.length ?? 0}
                    </p>
                    <div className="text-[10px] mt-1" style={smallMutedText}>Struck on the book mark - CIO review</div>
                  </div>

                  <div className={cardBaseClass} style={themeCardStyle}>
                    <p className={MICRO_LABEL} style={smallMutedText}>Next unlock</p>
                    <p className="text-lg font-medium" style={{ color: ui.textPrimary }}>
                      {!harvestLockout.known || !harvestLockout.nextUnlockAtUTC
                        ? "—"
                        : fmtLocalDateTime(parseUtc(harvestLockout.nextUnlockAtUTC))}
                    </p>
                    <div className="text-[10px] mt-1" style={smallMutedText}>Local time</div>
                  </div>
                </div>
                )}
                {/* The decision list — active lockouts, candidates, Record TWS Trim — sits
                    directly under the tiles, in the same card. Its rows stack (identity,
                    figures, harvest + action) so they fit this column; the old full-width
                    "Harvest decisions" band and the footnote that pointed at it are gone
                    (CIO, 2026-09-02). The panel carries the one manual-gate + book-mark
                    caveat now. Same payload as the tiles, so the two cannot disagree.
                    (Moved to this page from Portfolio on 2026-08-27, CIO decision Q7:
                    harvesting is a LEAPS decision and needs the regime context here.) */}
                <HarvestDecisionPanel
                  className="mt-4"
                  homeRun={homeRun as any}
                  userEmail={userEmail}
                  onRecorded={loadHomeRunHarvest}
                  onReleased={loadHomeRunHarvest}
                />
                </SectionShell>
              </div>
            </div>
          </section>

        {/* Plain, actionable error copy for the CIO — the raw technical detail (URLs, HTTP codes)
            stays in the browser console and on hover, never on a screen an allocator might see. */}
        {err && (
          <p className="mb-4 text-sm" style={{ color: "var(--status-danger-text)" }} title={err}>
            Couldn’t load the latest signals data. Try “Sync IBKR + Refresh”, and if it persists notify the desk.
          </p>
        )}
        {/* Its OWN text, not the generic loader message: which specific thing failed and
            what that means for the numbers below is the whole content of this warning. */}
        {syncNote && (
          <p className="mb-1 text-sm" style={secondaryText}>
            {syncNote}
          </p>
        )}
        {syncWarn && (
          <p className="mb-4 text-sm" style={{ color: "var(--status-warning-text)" }}>
            {syncWarn}
          </p>
        )}
        {loading && <p className="italic mb-2" style={secondaryText}>Loading…</p>}

        <section id="signals-sec-holdings" className="scroll-mt-20 flex flex-wrap items-end gap-3 mb-3">
          <div>
            <label className="block text-xs mb-1" style={secondaryText}>Account</label>
            <select
              value={accountFilter}
              onChange={(e) => setAccountFilter(e.target.value)}
              className="rounded px-2 py-1 text-sm"
              style={themeInputStyle}
            >
              {accounts.map(a => (
                <option key={a} value={a}>
                  {a === "__ALL__" ? "All Accounts (combined)" : a}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs mb-1" style={secondaryText}>Symbol</label>
            <input
              className="rounded px-2 py-1 text-sm"
              style={themeInputStyle}
              placeholder="Filter by symbol…"
              value={symbolFilter}
              onChange={(e) => setSymbolFilter(e.target.value)}
            />
          </div>
          {/* Moved here from the Convexity & risk card (was its "Mode" tile): it only changes
              how this table is laid out, so it sits with the table's other controls. */}
          <div>
            <label className="block text-xs mb-1" style={secondaryText}>Group by expiry</label>
            <button
              className={`rounded px-2.5 py-1 text-sm uppercase tracking-wide ${groupByExpiry ? "bg-helion-navy text-white" : ""}`}
              style={!groupByExpiry ? { backgroundColor: ui.groupBg, color: ui.textSecondary } : undefined}
              onClick={() => setGroupByExpiry(v => !v)}
              type="button"
              aria-pressed={groupByExpiry}
              title="Group the holdings rows under one header per expiry. Layout only; changes no figure."
            >
              {groupByExpiry ? "On" : "Off"}
            </button>
          </div>

          <div className="ml-auto flex flex-col items-end">
            <div className="text-xs" style={smallMutedText}>Last Risk Snapshot</div>
            <div className="text-sm font-semibold" style={{ color: ui.textPrimary }}>{latestSnapshotAt}</div>
          </div>
        </section>

        {/* Bound the Table's own scroll wrapper (its inner div) to a max height so vertical scroll
            happens inside it and the sticky column header actually sticks within that box, instead
            of the whole page scrolling and the header scrolling away. Inner div keeps overflow-auto,
            so horizontal scroll for the wide grid still works. */}
        <div className="[&>div]:max-h-[75vh]">
          <Table
            className="w-full min-w-[1600px] rounded text-sm table-fixed border-collapse"
            style={{
              backgroundColor: ui.tableBg,
              color: ui.textPrimary,
              border: `1px solid ${ui.cardBorder}`,
              boxShadow: ui.shadow,
            }}
          >
            <colgroup>
              {RISK_COLS.map((col) => (
                <col key={`col_${col.key}`} style={{ width: col.width }} />
              ))}
            </colgroup>

            <TableHeader
              className="sticky top-0 z-20 shadow-sm"
              style={{ backgroundColor: ui.tableHeaderBg, color: ui.tableHeaderText }}
            >
              <TableRow>
                {RISK_COLS.map((col) => (
                  <TableHead
                    key={col.key}
                    onClick={() => handleSort(col.sortKey)}
                    className={headClass(col.align)}
                    style={{ color: ui.tableHeaderText }}
                  >
                    {col.label}{sortIndicator(col.sortKey)}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>

            <TableBody>
              {sortedRowsWithVpd.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={RISK_COLS.length} className={cellClass("center", "italic")} style={secondaryText}>
                    No LEAPS risk rows found. Try “Sync IBKR + Refresh”; if the book is genuinely empty, this is expected.
                  </TableCell>
                </TableRow>
              ) : groupByExpiry ? (
                <>
                  {expiryGroups.map((g) => (
                    <React.Fragment key={`grp_${g.exp}`}>
                      <TableRow style={{ backgroundColor: ui.groupBg }}>
                        <TableCell colSpan={RISK_COLS.length} className="px-3 py-2">
                          <div className="flex flex-wrap items-center justify-between gap-3">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold" style={{ color: ui.textPrimary }}>
                                {fmtMon(g.exp)}{g.dte != null ? ` • ${g.dte} DTE` : ""}
                              </span>
                              <span className="text-xs" style={smallMutedText}>{g.exp}</span>
                            </div>
                            <div className="flex flex-wrap items-center gap-4 text-xs" style={{ color: ui.textSecondary }}>
                              <div>
                                <span style={smallMutedText}>Qty</span>{" "}
                                <span className="font-medium" style={{ color: ui.textPrimary }}>{g.qtySum.toLocaleString()}</span>
                              </div>
                              <div title="Sum(Vega × Qty × Multiplier)">
                                <span style={smallMutedText}>Vega/pt</span>{" "}
                                <span className="font-medium" style={{ color: ui.textPrimary }}>{fmtUSD(g.vega$)}</span>
                              </div>
                              <div title="Approx option $ change for a +1% SPX move">
                                <span style={smallMutedText}>Δ $ (+1% SPX)</span>{" "}
                                <span className="font-medium" style={{ color: ui.textPrimary }}>
                                  {g.delta$Per1pct != null ? fmtUSD(g.delta$Per1pct) : "—"}
                                </span>
                              </div>
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>

                      {g.rows.map((r) => {
                        const dte = computeDte(r.base.Expiration);
                        const ageDays = (typeof r.base.AgeDays === "number" && Number.isFinite(r.base.AgeDays)) ? Math.round(r.base.AgeDays) : null;
                        const vpdDisp = (r as any).vpd != null ? (r as any).vpd.toFixed(5) : "—";
                        const eff = (r as any).efficiency != null ? (r as any).efficiency as number : null;

                        const deltaVal = (typeof r.base.Delta === "number" && Number.isFinite(r.base.Delta)) ? r.base.Delta : null;
                        const vegaVal = (typeof r.base.Vega === "number" && Number.isFinite(r.base.Vega)) ? r.base.Vega : null;
                        const vpdVal = ((r as any).vpd != null && Number.isFinite((r as any).vpd)) ? (r as any).vpd as number : null;
                        const effVal = (eff != null && Number.isFinite(eff)) ? eff : null;
                        const thetaVal = thetaNorm(r.base.Theta);

                        return (
                          <TableRow
                            key={`${r.base.AccountId || "ALL"}_${r.base.Conid}`}
                            style={{ backgroundColor: "transparent" }}
                            className="transition-colors"
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = ui.hoverBgLight;
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = "transparent";
                            }}
                          >
                            <TableCell className={cellClass("left")} style={{ color: ui.textPrimary }}>{r.base.AccountId || "—"}</TableCell>
                            <TableCell className={cellClass("left", "font-medium")} style={{ color: ui.textPrimary }}>{r.base.Symbol ?? "—"}</TableCell>
                            <TableCell className={cellClass("left")} style={{ color: ui.textPrimary }}>{r.base.Expiration || "—"}</TableCell>
                            <TableCell className={cellClass("right")} style={{ color: ui.textPrimary }}>{dte ?? "—"}</TableCell>
                            <TableCell className={cellClass("right")} style={{ color: ui.textPrimary }} title={r.base.EntryDate ? `Held since ${fmtLocalDate(r.base.EntryDate)}` : undefined}>{ageDays ?? "—"}</TableCell>
                            <TableCell className={cellClass("center")} style={{ color: ui.textPrimary }}>{r.base.Right || "—"}</TableCell>
                            <TableCell className={cellClass("right")} style={{ color: ui.textPrimary }}>{r.base.Strike ?? "—"}</TableCell>
                            <TableCell className={cellClass("right")} style={{ color: ui.textPrimary }}>
                              {r.base.Quantity ?? "—"}
                              {lotConstraintBadge(r.base.Quantity)}
                            </TableCell>
                            <TableCell className={cellClass("right")} style={{ color: ui.textPrimary }}>{fmtNum(r.base.UnderlyingPrice, 2)}</TableCell>
                            <TableCell className={cellClass("right")} style={{ color: ui.textPrimary }}>{fmtNum(r.base.LastPrice, 2)}</TableCell>

                            <TableCell className={cellClass("right")} style={{ color: ui.textPrimary }}>
                              {renderBarCell(deltaVal, barScales.maxDelta, <span>{fmtNum(deltaVal, 3)}</span>, "Delta (scaled bar = relative magnitude)")}
                            </TableCell>

                            <TableCell className={cellClass("right")} style={{ color: ui.textPrimary }}>{gammaCell(r.base)}</TableCell>
                            <TableCell className={cellClass("right")} style={{ color: ui.textPrimary }}>{fmtNum(thetaVal, 3)}</TableCell>

                            <TableCell className={cellClass("right")} style={{ color: ui.textPrimary }}>
                              {renderBarCell(vegaVal, barScales.maxVega, <span>{fmtNum(vegaVal, 2)}</span>, "Vega (scaled bar = relative magnitude)")}
                            </TableCell>

                            <TableCell className={cellClass("right")} style={{ color: ui.textPrimary }}>{fmtIV(r.base.ImpliedVol)}</TableCell>

                            <TableCell className={cellClass("right")} style={{ color: ui.textPrimary }}>
                              {renderBarCell(vpdVal, barScales.maxVpd, <span>{vpdDisp}</span>, "VPD (Vega ÷ Price)")}
                            </TableCell>

                            <TableCell className={cellClass("right")} style={{ color: ui.textPrimary }}>
                              {renderBarCell(
                                effVal,
                                barScales.maxEff,
                                <span>
                                  {efficiencyBadge(effVal)}
                                  {(() => {
                                    const p = benchProvenance(r);
                                    return p ? (
                                      <sup className="ml-0.5 text-warn font-semibold" title={p.title}>{p.mark}</sup>
                                    ) : null;
                                  })()}
                                </span>,
                                "Efficiency = VPD ÷ a tenor-matched fresh ATM benchmark — roughly the share of premium still held as time value. Low = deep in-the-money, i.e. the line has won. A diagnostic, not a trade prompt."
                              )}
                            </TableCell>

                            <TableCell className={cellClass("right")} style={{ color: ui.textPrimary }}>{fmtUSD(r.base.UnrealizedPnL as number)}</TableCell>
                            <TableCell className={cellClass("right")} style={{ color: ui.textPrimary }}>{fmtPnLPct(r.base.UnrealizedPnLPercent as number)}</TableCell>
                          </TableRow>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </>
              ) : (
                <>
                  {sortedRowsWithVpd.map((r) => {
                    const dte = computeDte(r.base.Expiration);
                    const ageDays = (typeof r.base.AgeDays === "number" && Number.isFinite(r.base.AgeDays)) ? Math.round(r.base.AgeDays) : null;
                    const vpdDisp = (r as any).vpd != null ? (r as any).vpd.toFixed(5) : "—";
                    const eff = (r as any).efficiency != null ? (r as any).efficiency as number : null;

                    const deltaVal = (typeof r.base.Delta === "number" && Number.isFinite(r.base.Delta)) ? r.base.Delta : null;
                    const vegaVal = (typeof r.base.Vega === "number" && Number.isFinite(r.base.Vega)) ? r.base.Vega : null;
                    const vpdVal = ((r as any).vpd != null && Number.isFinite((r as any).vpd)) ? (r as any).vpd as number : null;
                    const effVal = (eff != null && Number.isFinite(eff)) ? eff : null;
                    const thetaVal = thetaNorm(r.base.Theta);

                    return (
                      <TableRow
                        key={`${r.base.AccountId || "ALL"}_${r.base.Conid}`}
                        style={{ backgroundColor: "transparent" }}
                        className="transition-colors"
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = ui.hoverBgLight;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "transparent";
                        }}
                      >
                        <TableCell className={cellClass("left")} style={{ color: ui.textPrimary }}>{r.base.AccountId || "—"}</TableCell>
                        <TableCell className={cellClass("left", "font-medium")} style={{ color: ui.textPrimary }}>{r.base.Symbol ?? "—"}</TableCell>
                        <TableCell className={cellClass("left")} style={{ color: ui.textPrimary }}>{r.base.Expiration || "—"}</TableCell>
                        <TableCell className={cellClass("right")} style={{ color: ui.textPrimary }}>{dte ?? "—"}</TableCell>
                        <TableCell className={cellClass("right")} style={{ color: ui.textPrimary }} title={r.base.EntryDate ? `Held since ${fmtLocalDate(r.base.EntryDate)}` : undefined}>{ageDays ?? "—"}</TableCell>
                        <TableCell className={cellClass("center")} style={{ color: ui.textPrimary }}>{r.base.Right || "—"}</TableCell>
                        <TableCell className={cellClass("right")} style={{ color: ui.textPrimary }}>{r.base.Strike ?? "—"}</TableCell>
                        <TableCell className={cellClass("right")} style={{ color: ui.textPrimary }}>
                          {r.base.Quantity ?? "—"}
                          {lotConstraintBadge(r.base.Quantity)}
                        </TableCell>
                        <TableCell className={cellClass("right")} style={{ color: ui.textPrimary }}>{fmtNum(r.base.UnderlyingPrice, 2)}</TableCell>
                        <TableCell className={cellClass("right")} style={{ color: ui.textPrimary }}>{fmtNum(r.base.LastPrice, 2)}</TableCell>

                        <TableCell className={cellClass("right")} style={{ color: ui.textPrimary }}>
                          {renderBarCell(deltaVal, barScales.maxDelta, <span>{fmtNum(deltaVal, 3)}</span>, "Delta (scaled bar = relative magnitude)")}
                        </TableCell>

                        <TableCell className={cellClass("right")} style={{ color: ui.textPrimary }}>{gammaCell(r.base)}</TableCell>
                        <TableCell className={cellClass("right")} style={{ color: ui.textPrimary }}>{fmtNum(thetaVal, 3)}</TableCell>

                        <TableCell className={cellClass("right")} style={{ color: ui.textPrimary }}>
                          {renderBarCell(vegaVal, barScales.maxVega, <span>{fmtNum(vegaVal, 2)}</span>, "Vega (scaled bar = relative magnitude)")}
                        </TableCell>

                        <TableCell className={cellClass("right")} style={{ color: ui.textPrimary }}>{fmtIV(r.base.ImpliedVol)}</TableCell>

                        <TableCell className={cellClass("right")} style={{ color: ui.textPrimary }}>
                          {renderBarCell(vpdVal, barScales.maxVpd, <span>{vpdDisp}</span>, "VPD (Vega ÷ Price)")}
                        </TableCell>

                        <TableCell className={cellClass("right")} style={{ color: ui.textPrimary }}>
                          {renderBarCell(
                            effVal,
                            barScales.maxEff,
                            <span>
                              {efficiencyBadge(effVal)}
                              {(() => {
                                const p = benchProvenance(r);
                                return p ? (
                                  <sup className="ml-0.5 text-warn font-semibold" title={p.title}>{p.mark}</sup>
                                ) : null;
                              })()}
                            </span>,
                            "Efficiency = VPD ÷ a tenor-matched fresh ATM benchmark — roughly the share of premium still held as time value. Low = deep in-the-money, i.e. the line has won. A diagnostic, not a trade prompt."
                          )}
                        </TableCell>

                        <TableCell className={cellClass("right")} style={{ color: ui.textPrimary }}>{fmtUSD(r.base.UnrealizedPnL as number)}</TableCell>
                        <TableCell className={cellClass("right")} style={{ color: ui.textPrimary }}>{fmtPnLPct(r.base.UnrealizedPnLPercent as number)}</TableCell>
                      </TableRow>
                    );
                  })}
                </>
              )}
            </TableBody>
          </Table>
        </div>

        {/* Wrapped in its own <section> rather than putting the id on the ladder itself:
            the IIFE returns null when the book has no priced rows, and a jump pill whose
            target can vanish is a silent no-op. */}
        <section id="signals-sec-expiry" className="scroll-mt-20">
        {(() => {
          type ExpBucket = { exp: string; dte: number | null; notional: number };
          const buckets: Record<string, ExpBucket> = {};
          filteredRows.forEach((r) => {
            const qty = num(r.Quantity, 0);
            const mult = num(r.Multiplier, 100);
            const price = (num(r.Mid, NaN) && num(r.Mid, NaN) > 0) ? num(r.Mid) : num(r.LastPrice);
            if (!qty || !mult || !price) return;
            const exp = r.Expiration || "—";
            const dte = computeDte(r.Expiration);
            const notional = price * mult * qty;
            if (!buckets[exp]) buckets[exp] = { exp, dte, notional: 0 };
            buckets[exp].notional += notional;
          });

          // Market value per held expiry (whole book), merged with the Screener's ladder rungs (cost, 18m+ book).
          // A listed rung the fund does not hold yet gets a row with an empty bar, so its gap is visible.
          // Unpriced rungs only when the holdings are loaded and unfiltered: the ladder column is the WHOLE fund-of-record
          // book, so under an Account / Symbol filter (or before rows load) a missing bar does not mean "no price".
          const ladderFiltered = accountFilter !== "__ALL__" || !!symbolFilter.trim();
          const series = mergeCapitalRows(Object.values(buckets), ladder, { addUnpricedRungs: rows.length > 0 && !ladderFiltered });
          if (series.length === 0) return null;

          const total = series.reduce((s, x) => s + x.notional, 0);
          const ladderOn = !!(ladder && ladder.active);

          const scale = ladderScale(ladder);
          const band = ladder?.bandPct ?? null;
          // Fixed column widths: the bar is capped (max 34rem) so on a 2560px monitor the status and numbers sit next
          // to it instead of floating at the far edge (CIO 2026-10-29: "design wise I think this misses the mark").
          // The bar column takes the free width; status and numbers follow it directly. Below 2xl the table container is
          // capped (76rem) so the bar stays ~35rem; from 2xl the table fills the space left of the bucket panel (no dead space).
          const gridCols = { gridTemplateColumns: "9.5rem minmax(9rem, 1fr) 9.5rem 9rem 8.5rem" } as React.CSSProperties;

          const footer = (
            <div className="text-xs mt-3 space-y-1" style={smallMutedText}>
              {ladderOn ? (
                <div>Market value = (Mid or Last) × Multiplier × Quantity, as a share of the whole held book (incl. &lt;18m).</div>
              ) : (
                <div>Notional = (Mid or Last) × Multiplier × Quantity, grouped by expiration.</div>
              )}
              <div data-testid="ladder-footnote">
                {ladderOn && ladderFiltered ? "Filtered view: the ladder bars are the whole fund-of-record book; expiries outside the filter are not listed. " : ""}
                {ladderFootnote(ladder, ladderErr)}
                {ladder && ladderErr ? ` Last refresh failed (${ladderErr}); showing the previous read.` : ""}
              </div>
            </div>
          );

          const title = (
            <div className="flex items-baseline justify-between mb-3 gap-4 flex-wrap">
              <div>
                <h3 className="text-lg font-semibold" style={{ color: ui.textPrimary }}>Capital by Expiration</h3>
                <div className="text-xs mt-1" style={smallMutedText}>
                  {ladderOn
                    ? "Each bar is the expiry's share of the 18m+ LEAPS book at cost; the tick is the Screener's ladder target and the shaded zone is on target."
                    : "Expiration ladder distribution across the live convexity book."}
                </div>
              </div>
              <div className="text-sm" style={smallMutedText}>
                Market value: <span className="font-medium" style={{ color: ui.textPrimary }}>{fmtUSD(total)}</span>
              </div>
            </div>
          );

          if (!ladderOn) {
            // Ladder off / unavailable: the card as it was before the ladder (market value over the whole book).
            return (
              <div className="mt-8">
                {title}
                <div className="space-y-2" data-testid="capital-by-expiration">
                  {series.map((b) => {
                    const pct = total > 0 ? (b.notional / total) * 100 : 0;
                    const widthPct = b.priced ? Math.max(2, Math.round(pct)) : 0;
                    return (
                      <div key={b.exp} className="grid grid-cols-12 gap-3 items-center" data-testid="capital-row" data-exp={b.exp}>
                        <div className="col-span-3 md:col-span-2 text-xs md:text-sm">
                          <div className="font-medium" style={{ color: ui.textPrimary }}>{fmtMon(b.exp)}{b.dte != null ? `  •  ${b.dte}d` : ""}</div>
                          <div style={smallMutedText}>{b.exp || "—"}</div>
                        </div>
                        <div className="col-span-6 md:col-span-8">
                          <div className="w-full rounded h-4 overflow-hidden" style={barTrackStyle(true)}>
                            {b.priced ? <div className="h-4 rounded" style={{ width: `${widthPct}%`, ...neutralBarFillStyle(true) }} title={`${fmtUSD(b.notional)} (${pct.toFixed(1)}%)`} /> : null}
                          </div>
                        </div>
                        <div className="col-span-3 md:col-span-2 text-right text-xs md:text-sm">
                          <div className="font-medium" style={{ color: ui.textPrimary }}>{fmtUSD(b.notional)}</div>
                          <div style={smallMutedText}>{pct.toFixed(1)}%</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {footer}
              </div>
            );
          }

          // Bucket summary (CIO 2026-10-29: "now we have dead space"): the ladder by BUCKET, where the targets are set,
          // beside the table from 2xl (the CIO's 2560 monitor) and stacked under it below that.
          const ladderBkts = ladderBuckets(ladder);
          const bScale = bucketScale(ladderBkts, band);
          const roomList = roomRungs(ladder);
          const bucketPanel = ladderBkts.length ? (
            <div className="max-w-[40rem] 2xl:max-w-none 2xl:w-[28rem] 2xl:shrink-0 2xl:border-l 2xl:pl-8" style={{ borderColor: ui.cardBorder }} data-testid="ladder-bucket-panel">
              <div className="text-sm font-semibold" style={{ color: ui.textPrimary }}>By bucket</div>
              <div className="text-xs mt-0.5 mb-3" style={smallMutedText}>
                Where the targets are set. Cost, 18m+ book; each bucket = the sum of its rungs, same ±{typeof band === "number" ? (band * 100).toFixed(0) : "3"}-pt rule.
              </div>
              <div className="space-y-3">
                {ladderBkts.map((bk) => {
                  const g = ladderBarGeometry({ expiry: bk.bucket, sharePct: bk.sharePct, targetPct: bk.targetPct }, bScale, band);
                  return (
                    <div key={bk.bucket} className="grid items-center gap-x-3" style={{ gridTemplateColumns: "4rem minmax(6rem, 1fr) 5.5rem 6.5rem" }} data-testid="ladder-bucket-row">
                      <div className="text-sm font-semibold" style={{ color: ui.textPrimary }}>{bk.label}</div>
                      <div className="relative w-full h-4 rounded overflow-hidden" style={barTrackStyle(true)}>
                        {g.bandLoPct != null && g.bandHiPct != null ? (
                          <div className="absolute inset-y-0" style={{ left: `${g.bandLoPct}%`, width: `${Math.max(0, g.bandHiPct - g.bandLoPct)}%`, backgroundColor: "rgba(148, 163, 184, 0.16)" }} />
                        ) : null}
                        <div className="absolute inset-y-0 left-0 rounded" style={{ width: `${g.fillPct}%`, backgroundColor: ladderFill(bk.status) }} />
                        {g.tickPct != null ? (
                          <div className="absolute" data-testid="ladder-bucket-tick" style={{ left: `calc(${g.tickPct}% - 1px)`, top: -1, bottom: -1, width: 2, backgroundColor: ui.textPrimary, opacity: 0.85 }} />
                        ) : null}
                      </div>
                      <div><RungStatusChip status={bk.status} /></div>
                      <div className="text-right tabular-nums">
                        <div className="text-sm font-semibold" style={{ color: ui.textPrimary }}>
                          {(bk.sharePct * 100).toFixed(1)}%<span className="font-normal" style={smallMutedText}> / {(bk.targetPct * 100).toFixed(0)}%</span>
                        </div>
                        <div className="text-xs" style={smallMutedText}>{ladderGapSigned({ expiry: bk.bucket, sharePct: bk.sharePct, targetPct: bk.targetPct })} pts</div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-4 text-xs" style={smallMutedText} data-testid="ladder-room-list">
                <div className="uppercase tracking-wide text-[11px] mb-1">Rungs with room</div>
                {roomList.length ? (
                  <ul className="space-y-0.5">
                    {roomList.map((r) => (
                      <li key={r.expiry}>
                        <span className="font-medium" style={{ color: ui.textPrimary }}>{fmtMon(r.expiry)}</span>
                        {` · ${r.bucketLabel || ""} · ${ladderGapText(r)}`}
                        {r.rollingOff ? ` · rolling off${r.daysUntilUnder18m != null ? ` (under 18m in ${r.daysUntilUnder18m}d)` : ""}` : ""}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div>No rung has room: every rung is on target or full.</div>
                )}
                <div className="mt-2">The Screener's next-buy pick also weighs today's quotes and spreads; see the Screener.</div>
              </div>
            </div>
          ) : null;

          return (
            <div className="mt-8 flex flex-col 2xl:flex-row 2xl:items-start gap-8" data-testid="capital-card">
            {/* Table capped to its own width so the title, total and footnote line up with it; the bucket panel sits beside it. */}
            <div className="max-w-[76rem] min-w-0 2xl:max-w-none 2xl:flex-1">
              {title}

              <div className="grid gap-x-4 items-end mb-1.5 text-[11px] uppercase tracking-wide" style={{ ...gridCols, ...smallMutedText }}>
                <div>Expiry</div>
                <div className="flex justify-between" data-testid="ladder-col-head">
                  <span>Share of 18m+ book at cost · tick = target</span>
                  <span>{(scale * 100).toFixed(0)}%</span>
                </div>
                <div>Status</div>
                <div className="text-right">Share / target</div>
                <div className="text-right">Market value</div>
              </div>

              <div className="space-y-2.5" data-testid="capital-by-expiration">
                {series.map((b) => {
                  const pct = total > 0 ? (b.notional / total) * 100 : 0;
                  const lv = b.ladder;
                  const rung = lv.kind === "rung" ? lv.rung : null;
                  const g = rung ? ladderBarGeometry(rung, scale, band) : null;
                  const muted = !rung;
                  return (
                    <div key={b.exp} className="grid gap-x-4 items-center" style={gridCols} data-testid="capital-row" data-exp={b.exp}>
                      <div className="text-sm leading-tight" style={muted ? { opacity: 0.65 } : undefined}>
                        <div className="font-semibold" style={{ color: ui.textPrimary }}>{fmtMon(b.exp)}<span className="font-normal" style={smallMutedText}>{b.dte != null ? `  ·  ${b.dte}d` : ""}</span></div>
                        <div className="text-xs" style={smallMutedText}>{rung?.bucketLabel || (lv.kind === "outside" ? "<18m" : "—")}</div>
                      </div>

                      <div title={rung?.statusWhy || (lv.kind === "outside" ? lv.reason : undefined)}>
                        <div className="relative w-full h-5 rounded overflow-hidden" style={barTrackStyle(true)} data-testid="ladder-bar">
                          {g && g.bandLoPct != null && g.bandHiPct != null ? (
                            <div className="absolute inset-y-0" style={{ left: `${g.bandLoPct}%`, width: `${Math.max(0, g.bandHiPct - g.bandLoPct)}%`, backgroundColor: "rgba(148, 163, 184, 0.16)" }} />
                          ) : null}
                          {g ? (
                            <div className="absolute inset-y-0 left-0 rounded" style={{ width: `${g.fillPct}%`, backgroundColor: ladderFill(rung?.status, rung?.rollingOff) }} />
                          ) : null}
                          {g && g.tickPct != null ? (
                            <div className="absolute" data-testid="ladder-tick" style={{ left: `calc(${g.tickPct}% - 1px)`, top: -1, bottom: -1, width: 2, backgroundColor: ui.textPrimary, opacity: 0.85 }} />
                          ) : null}
                          {lv.kind === "outside" ? (
                            <div className="absolute inset-0 flex items-center px-2 text-[11px]" style={smallMutedText}>outside the ladder (under 18 months)</div>
                          ) : null}
                        </div>
                      </div>

                      <div data-testid="ladder-cell">
                        {rung ? (
                          <div>
                            <RungStatusChip status={rung.status} rollingOff={rung.rollingOff} />
                            {rung.rollingOff && rung.daysUntilUnder18m != null ? (
                              <div className="text-[11px] mt-0.5" style={smallMutedText}>under 18m in {rung.daysUntilUnder18m}d</div>
                            ) : null}
                          </div>
                        ) : (
                          <span className="text-xs" style={smallMutedText}>—</span>
                        )}
                      </div>

                      <div className="text-right tabular-nums">
                        {rung ? (
                          <>
                            <div className="text-sm font-semibold" style={{ color: ui.textPrimary }}>
                              {typeof rung.sharePct === "number" ? (rung.sharePct * 100).toFixed(1) : "—"}%
                              <span className="font-normal" style={smallMutedText}> / {typeof rung.targetPct === "number" ? (rung.targetPct * 100).toFixed(1) : "—"}%</span>
                            </div>
                            <div className="text-xs" style={smallMutedText} title={ladderGapText(rung)}>{ladderGapSigned(rung)} pts</div>
                          </>
                        ) : (
                          <span className="text-xs" style={smallMutedText}>—</span>
                        )}
                      </div>

                      <div className="text-right tabular-nums">
                        {b.priced ? (
                          <>
                            <div className="text-sm font-medium" style={{ color: ui.textPrimary }}>{fmtUSD(b.notional)}</div>
                            <div className="text-xs" style={smallMutedText}>{pct.toFixed(1)}% of book</div>
                          </>
                        ) : b.held ? (
                          <div className="text-xs" style={smallMutedText} title="Held at cost (ladder), but no usable market price on the holdings rows">held · no market price</div>
                        ) : (
                          <div className="text-xs" style={smallMutedText}>not held</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {footer}
            </div>
            {bucketPanel}
            </div>
          );
        })()}
        </section>

        <div className="text-xs mt-4 space-y-2" style={smallMutedText}>
          <div>
            Fresh VPD benchmark{" "}
            <span style={smallMutedText}>
              ({benchmark.source === "market"
                ? `${benchmark.perTenor ? `per-tenor curve, ${benchmark.nodeCount} nodes` : "fresh ATM ~24–30m"}, market surface${benchmark.asOf ? `, ${benchmark.asOf}` : ""}${benchmark.stale ? " — stale" : ""}`
                : benchmark.source == null
                  ? "unavailable"
                  : `in-book fallback${benchmark.reason ? ` — ${benchmark.reason}` : ""}`})
            </span>{benchmark.perTenor ? ", 24–30m headline" : ""}:{" "}
            <span className="font-semibold" style={{ color: ui.textPrimary }}>
              {freshVpd != null ? freshVpd.toFixed(5) : "—"}
            </span>
          </div>
          <div className="leading-relaxed">
            <p>
              <span className="font-semibold" style={{ color: ui.textPrimary }}>VPD (Vega per Dollar)</span> shows how much
              volatility exposure a contract provides relative to its price. Higher VPD =
              more “live” convexity per dollar. As a position drifts deep in-the-money or
              short-dated, its convexity is spent and VPD falls.
            </p>
            <p>
              The <span className="font-semibold" style={{ color: ui.textPrimary }}>Fresh VPD Benchmark</span> is a
              <span className="font-semibold" style={{ color: ui.textPrimary }}> per-tenor curve</span>: the VPD of a fresh ATM
              index LEAPS priced off today’s ORATS surface at each supported maturity, so
              every position is measured against fresh convexity at its OWN tenor (a 5-year
              rung vs fresh 5-year convexity, not a 2-year yardstick) — including the thin
              4–5.5yr long end. The number above is the 24–30m headline point. If the
              surface can’t support a trustworthy read, it falls back to an in-book
              benchmark (the freshest DTE quartile held, per underlying) and says so.
              Benchmarks are measured per underlying, so SPX and NDX never mix.
            </p>
            <p>
              <span className="font-semibold" style={{ color: ui.textPrimary }}>Efficiency %</span> = Contract VPD ÷ Fresh benchmark
              — roughly the share of premium still held as time value.
              <span className="ml-1">Bands:</span>
              <span className="ml-1 px-1.5 py-0.5 rounded bg-up/15 text-up ring-1 ring-up/30"> ≥85% </span>
              fresh-equivalent,
              <span className="ml-1 px-1.5 py-0.5 rounded bg-up/10 text-up ring-1 ring-up/20"> 70–85% </span>
              lightly seasoned,
              <span
                className="ml-1 px-1.5 py-0.5 rounded"
                style={{ backgroundColor: ui.overlaySoft, color: ui.textSecondary, boxShadow: `inset 0 0 0 1px ${ui.cardBorder}` }}
              > 50–70% </span>
              seasoned,
              <span
                className="ml-1 px-1.5 py-0.5 rounded"
                style={{ backgroundColor: ui.overlayStrong, color: ui.textPrimary, boxShadow: `inset 0 0 0 1px ${ui.cardBorder}` }}
              > &lt;50% </span>
              deep ITM.
            </p>
            <p>
              A low reading is a <span className="font-semibold" style={{ color: ui.textPrimary }}>description, not an
              instruction</span>: it means the line has gone deep in-the-money, which is normally a winner and a position
              that has de-levered itself. Because the benchmark is tenor-matched, the number reflects moneyness rather than
              how long-dated the rung is. There is no roll or recycle rule behind this column, and the evidence argues
              against one — swapping a seasoned line into fresh ATM convexity at constant dollars buys more vega but
              also materially more delta, and Study P10 measured that as leverage (vol 21% → 53–69%), not alpha.
            </p>
            <p>
              <span className="font-semibold" style={{ color: ui.textPrimary }}>Benchmark provenance.</span> An efficiency is
              only as good as the contract it is measured against, so rows whose benchmark was not a tenor-matched
              measurement carry a marker:
              <span className="ml-1 text-warn font-semibold">†</span> the curve was held FLAT beyond (or below) the
              tenors the surface covers, so the reading is scored against an extrapolation — and ORATS coverage is
              thinnest at exactly the long end;
              <span className="ml-1 text-warn font-semibold">‡</span> no per-tenor curve was available, so the row was
              scored against the single 24–30m headline point and its reading conflates moneyness with duration;
              <span className="ml-1 text-warn font-semibold">§</span> the market benchmark was unavailable and the row
              was scored against our own freshest holdings, which is self-referential. An unmarked row was interpolated
              between two real surface nodes. Hover any marker for the specifics.
            </p>
            <p>
              <span className="font-semibold" style={{ color: ui.textPrimary }}>“No partial trim”</span> under a
              Quantity means the line holds fewer contracts than policy needs to split a position, so it can pass the
              harvest trigger and still stage nothing — the trim rounds to zero. It is a hold-or-close decision, not a
              defect in the position, and it is reported on lot size alone rather than waiting for the line to cross the
              trigger. The same badge appears on the Portfolio page; both read the threshold from config.
            </p>
          </div>
        </div>

        {/* ---- OPTIONS ML INTELLIGENCE — moved to the foot of the page (was directly under
             the deployment row). Model telemetry is reference material, not a daily read:
             entry ML has carried weight 0.0 in every mode since 2026-08-07, and the exit
             model is decision support behind the rule. It stays collapsed by default and
             nothing above it depends on it. Only its position changed, not its content. ---- */}
        <section id="signals-sec-ml" className="mt-4 scroll-mt-20">
          <details className="rounded-[18px] border" style={panelChromeStyle}>
            <summary className="cursor-pointer select-none px-4 py-3" style={{ color: ui.textPrimary }}>
              <span className="text-sm font-semibold">Options ML Intelligence</span>
              <span className="ml-2 text-[11px]" style={smallMutedText}>
                Model cards, training telemetry and feature importances — reference material, not a daily read
              </span>
            </summary>
            <div className="border-t p-3" style={{ borderColor: ui.cardBorder }}>
              <OptionsMlBriefing overview={displayMeta?.optionsMl ?? null} compact />
            </div>
          </details>
        </section>
      </div>
    </Layout>
  );
};

export default SignalsDashboard;